/**
 * Scriptable iOS Type Definitions
 * 
 * @file Auto-generated abstract class definitions for Scriptable iOS
 * @source @types/scriptable-ios@1.7.7
 * @module scriptable-abstract
 * @generated 2025-02-13T08:51:18.588Z
 * @license MIT
 */

/// <reference types="@types/scriptable-ios" />


// Base class definitions
/**
 * Base abstract class with shared core functionality.
 * Provides common methods for property access and method invocation.
 */

// Type definition for Scriptable constructor and static members
export type ScriptableConstructor<T extends object = object, Instance extends Scriptable<T> = Scriptable<T>> = {
  new (...args: any[]): Instance;
  readonly type: 'class' | 'variable';
  readonly identifier: string;
};

/**
 * Initializer function type for scriptable instances.
 * Defines initialization behavior for a scriptable class.
 * @template T The state type of the scriptable instance
 * @param instance The scriptable instance to initialize
 */
export type ScriptableInitializer<T extends object> = (instance: Scriptable<T>) => void;

export abstract class Scriptable<T extends object> {
  /**
   * The type of the scriptable implementation.
   */
  static readonly type: 'class' | 'variable';
  /**
   * The identifier name in the target system.
   */
  static readonly identifier: string;

  // Use WeakMap for private state storage to prevent direct access
  private static readonly states = new WeakMap<object, object>();

  /**
   * Store class-level initializers
   * Using WeakMap to allow garbage collection of unused class references
   */
  private static readonly classInitializers = new WeakMap<Function, ScriptableInitializer<any>>();

  // Cache frozen state
  private frozenState?: Readonly<T>;

  /**
   * Creates a new instance of a Scriptable class
   * @param initialState Initial state for the instance
   */
  constructor(initialState: Partial<T> = {} as T) {
    // Initialize with type-safe default state
    this.setState(initialState);

    // Apply class-level initializer if exists
    const classInitializer = Scriptable.classInitializers.get(this.constructor);
    if (classInitializer) {
      classInitializer(this);
    }
  }

  /**
   * Registers a class-level initializer that will be applied to all instances during construction.
   * This is useful for setting up default behaviors and states for all instances of a class.
   *
   * @template T The state type of the target class
   * @param targetClass The class to register the initializer for
   * @param initializer The initialization function
   * @throws {Error} If an initializer is already registered for the target class
   * @example
   * ```typescript
   * Scriptable.initialize(MyClass, instance => {
   *   instance.setState({ defaultValue: 'some value' });
   * });
   * ```
   */
  static initialize<T extends object>(
    targetClass: ScriptableConstructor<T>,
    initializer: ScriptableInitializer<T>,
  ): void {
    if (this.classInitializers.has(targetClass)) {
      throw new Error(
        `Class initializer already registered for ${targetClass.name}. ` +
          `Use replaceInitializer() if you need to override the existing initializer.`,
      );
    }
    this.classInitializers.set(targetClass, initializer);
  }

  /**
   * Replaces an existing class-level initializer or sets a new one.
   * Use this method with caution as it can affect all future instances of the class.
   *
   * @template T The state type of the target class
   * @param targetClass The class to set the initializer for
   * @param initializer The new initialization function
   */
  static replaceInitializer<T extends object>(
    targetClass: ScriptableConstructor<T>,
    initializer: ScriptableInitializer<T>,
  ): void {
    this.classInitializers.set(targetClass, initializer);
  }

  /**
   * Removes the class-level initializer for the specified class.
   *
   * @param targetClass The class to remove the initializer from
   * @returns true if an initializer was removed, false otherwise
   */
  static clearInitializer(targetClass: ScriptableConstructor<any>): boolean {
    return this.classInitializers.delete(targetClass);
  }

  // Public API
  get state(): Readonly<T> {
    // Cache frozen state for better performance
    if (!this.frozenState) {
      this.frozenState = Object.freeze({ ...this.internalState });
    }
    return this.frozenState;
  }

  setState<K extends keyof T>(state: Pick<T, K> | Partial<T> | ((prevState: Readonly<T>) => Pick<T, K>)): this {
    // Batch updates for better performance
    const updates = typeof state === 'function' ? state(this.state) : state;

    if (Object.keys(updates).length === 0) {
      return this; // Skip empty updates
    }

    this.updateState(updates as Partial<T>);
    return this;
  }

  // Protected internal methods
  protected get internalState(): T {
    return (Scriptable.states.get(this) ?? {}) as T;
  }

  protected set internalState(value: T) {
    this.frozenState = undefined; // Invalidate cache
    Scriptable.states.set(this, value);
  }

  protected updateState(updates: Partial<T>): void {
    // More efficient state update
    const newState = { ...this.internalState };
    let hasChanges = false;

    for (const key in updates) {
      const value = updates[key];
      if (value !== undefined && value !== this.internalState[key]) {
        newState[key] = value;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      this.internalState = newState;
    }
  }

  // Method invocation
  protected invokeMethod<R>(method: string, _args: unknown[]): R {
    throw new Error(`Method '${String(method)}' not implemented in ${this.constructor.name}`);
  }

  protected static invokeStaticMethod<R>(method: string, _args: unknown[]): R {
    throw new Error(`Static method '${method}' not implemented in ${this.name}`);
  }

  protected asOriginal<O>(): O {
    return this as any as O;
  }
}

/**
 * Base class for objects.
 * Provides implementation for class-based scriptable objects.
 */
export abstract class ScriptableClass<T extends object> extends Scriptable<T> {
  static readonly type = 'class';
}

/**
 * Base class for global variables.
 * Provides implementation for global variable access and manipulation.
 */
export abstract class ScriptableVariable<T extends object> extends Scriptable<T> {
  static readonly type = 'variable';

  protected static _instance?: ScriptableVariable<any>;

  constructor(initialState: Partial<T> = {} as T) {
    super(initialState);
    if ((this.constructor as typeof ScriptableVariable)._instance) {
      throw new Error(`${this.constructor.name} is a singleton`);
    }
  }

  static get instance(): ScriptableVariable<any> {
    if (!this._instance) {
      this._instance = new (this as any)();
    }
    return this._instance!;
  }
}


// Generated code
// Class type definitions
// These types are automatically generated from @types/scriptable-ios

export interface AlertState {
  title: string;
  message: string;
}

export class AbsAlert<S extends AlertState = AlertState> extends ScriptableClass<S> implements Alert {
  static readonly identifier = 'Alert';

  get asAlert(): Alert {
    return this.asOriginal<Alert>();
  }


  get title() {
    if (!('title' in this.state)) {
      return "";
    }
    return this.state.title as string;
  }

  set title(value: string) {
    this.setState({ title: value });
  }
  get message() {
    if (!('message' in this.state)) {
      return "";
    }
    return this.state.message as string;
  }

  set message(value: string) {
    this.setState({ message: value });
  }

  /**
     * _Adds an action to the alert._
     *
     * Adds an action button to the alert. To check if an action was selected, you should use the first parameter provided when the promise returned by presentAlert() and presentSheet()
     * is resolved.
     * @param title - Title of the action.
     * @see https://docs.scriptable.app/alert/#-addaction
     */
  addAction(title: string): void {
    return this.invokeMethod<void>('addAction', Array.from(arguments));
  }
  /**
     * _Adds a destructive action to the alert._
     *
     * Destructive action titles have a red text color, signaling that the action may modify or delete data.
     * @param title - Title of the action.
     * @see https://docs.scriptable.app/alert/#-adddestructiveaction
     */
  addDestructiveAction(title: string): void {
    return this.invokeMethod<void>('addDestructiveAction', Array.from(arguments));
  }
  /**
     * _Adds a cancel action to the alert._
     *
     * Adds a cancel action to the alert. When a cancel action is selected, the index provided by presentAlert() or presentSheet() will always be -1. Please note that when running on the
     * iPad and presenting using presentSheet(), the action will not be shown in the list of actions. The operation is cancelled by tapping outside the sheet.
     *
     * An alert can only contain a single cancel action. Attempting to add more cancel actions will remove any previously added cancel actions.
     * @param title - Title of the action.
     * @see https://docs.scriptable.app/alert/#-addcancelaction
     */
  addCancelAction(title: string): void {
    return this.invokeMethod<void>('addCancelAction', Array.from(arguments));
  }
  /**
     * _Adds a text field prompting for user input._
     *
     * Adds a text field to the alert controller prompting for user input. Retrieve the value for the text field using textFieldValue() and supply the index of the text field. Indices for
     * text fields are assigned in the same order as they are added to the alert starting at 0.
     *
     * Text fields are not supported when using the sheet presentation.
     * @param placeholder - Optional placeholder that will be displayed when the text field is empty.
     * @param text - Optional default value for the text field.
     * @see https://docs.scriptable.app/alert/#-addtextfield
     */
  addTextField(placeholder?: string, text?: string): TextField {
    return this.invokeMethod<TextField>('addTextField', Array.from(arguments));
  }
  /**
     * _Adds a secure text field prompting for user input._
     *
     * Adds a secure text field to the alert controller prompting for user input. Values entered into a secure text field will be hidden behind dots. Retrieve the value for the text field
     * using textFieldValue() and supply the index of the text field. Indices for text fields are assigned in the same order as they are added to the alert starting at 0.
     * @param placeholder - Optional placeholder that will be displayed when the text field is empty.
     * @param text - Optional default value for the text field.
     * @see https://docs.scriptable.app/alert/#-addsecuretextfield
     */
  addSecureTextField(placeholder?: string, text?: string): TextField {
    return this.invokeMethod<TextField>('addSecureTextField', Array.from(arguments));
  }
  /**
     * _Retrieves value of a text field._
     *
     * Retrieves the value of a text field added using addTextField() or addSecureTextField(). Indices for text fields are assigned in the same order as they are added to the alert
     * starting at 0.
     * @param index - Index of text field to retrieve for value.
     * @see https://docs.scriptable.app/alert/#-textfieldvalue
     */
  textFieldValue(index: number): string {
    return this.invokeMethod<string>('textFieldValue', Array.from(arguments));
  }
  /**
     * _Presents the alert modally._
     *
     * This is a shorthand for presentAlert().
     * @see https://docs.scriptable.app/alert/#-present
     */
  present(): Promise<number> {
    return this.invokeMethod<Promise<number>>('present', Array.from(arguments));
  }
  /**
     * _Presents the alert modally._
     * @see https://docs.scriptable.app/alert/#-presentalert
     */
  presentAlert(): Promise<number> {
    return this.invokeMethod<Promise<number>>('presentAlert', Array.from(arguments));
  }
  /**
     * _Presents the alert as a sheet._
     * @see https://docs.scriptable.app/alert/#-presentsheet
     */
  presentSheet(): Promise<number> {
    return this.invokeMethod<Promise<number>>('presentSheet', Array.from(arguments));
  }
}

export interface CalendarState {
  identifier: string;
  title: string;
  isSubscribed: boolean;
  allowsContentModifications: boolean;
  color: Color;
}

export class AbsCalendar<S extends CalendarState = CalendarState> extends ScriptableClass<S> implements Calendar {
  static readonly identifier = 'Calendar';

  get asCalendar(): Calendar {
    return this.asOriginal<Calendar>();
  }

  /**
     * _Fetches calendars for reminders._
     *
     * A calendar can only hold either reminders or events. Call this function to fetch all calendars that can hold reminders.
     * @see https://docs.scriptable.app/calendar/#forreminders
     */
  static forReminders(): Promise<Calendar[]> {
    return this.invokeStaticMethod<Promise<Calendar[]>>('forReminders', Array.from(arguments));
  }
  /**
     * _Fetches calendars for events._
     *
     * A calendar can only hold either reminders or events. Call this function to fetch all calendars that can hold events.
     * @see https://docs.scriptable.app/calendar/#forevents
     */
  static forEvents(): Promise<Calendar[]> {
    return this.invokeStaticMethod<Promise<Calendar[]>>('forEvents', Array.from(arguments));
  }
  /**
     * _Fetches a calendar that holds reminders._
     * @param title - Title of calendar.
     * @see https://docs.scriptable.app/calendar/#forremindersbytitle
     */
  static forRemindersByTitle(title: string): Promise<Calendar> {
    return this.invokeStaticMethod<Promise<Calendar>>('forRemindersByTitle', Array.from(arguments));
  }
  /**
     * _Fetches a calendar that holds events._
     * @param title - Title of calendar.
     * @see https://docs.scriptable.app/calendar/#foreventsbytitle
     */
  static forEventsByTitle(title: string): Promise<Calendar> {
    return this.invokeStaticMethod<Promise<Calendar>>('forEventsByTitle', Array.from(arguments));
  }
  /**
     * _Create a new calendar that holds reminders._
     *
     * This will create a new list for reminders in the Reminders app. The list is automatically saved so there is no need to call `save()` after creating the list.
     * @see https://docs.scriptable.app/calendar/#createforreminders
     */
  static createForReminders(title: string): Promise<Calendar> {
    return this.invokeStaticMethod<Promise<Calendar>>('createForReminders', Array.from(arguments));
  }
  /**
     * _Find or create a new calendar that holds reminders._
     *
     * This will attempt to find a calendar for reminders with the specified name. If no calendar is found, a new calendar is created and the calendar will appear as a reminder list in
     * the Reminders app. If multiple calendars are found for the specified name, the first one will be returned. The list is automatically saved so there is no need to call `save()` in
     * the case the list was created.
     * @see https://docs.scriptable.app/calendar/#findorcreateforreminders
     */
  static findOrCreateForReminders(title: string): Promise<Calendar> {
    return this.invokeStaticMethod<Promise<Calendar>>('findOrCreateForReminders', Array.from(arguments));
  }
  /**
     * _Default calendar for reminders._
     *
     * A calendar can only hold either reminders or events. Call this function to get the default calendar that can hold reminders.
     * @see https://docs.scriptable.app/calendar/#defaultforreminders
     */
  static defaultForReminders(): Promise<Calendar> {
    return this.invokeStaticMethod<Promise<Calendar>>('defaultForReminders', Array.from(arguments));
  }
  /**
     * _Default calendar for events._
     *
     * A calendar can only hold either reminders or events. Call this function to get the default calendar that can hold events.
     * @see https://docs.scriptable.app/calendar/#defaultforevents
     */
  static defaultForEvents(): Promise<Calendar> {
    return this.invokeStaticMethod<Promise<Calendar>>('defaultForEvents', Array.from(arguments));
  }
  /**
     * _Presents a view for picking calendars._
     * @param allowMultiple - Whether to allow picking multiple calenders. Defaults to false.
     * @see https://docs.scriptable.app/calendar/#presentpicker
     */
  static presentPicker(allowMultiple?: boolean): Promise<Calendar[]> {
    return this.invokeStaticMethod<Promise<Calendar[]>>('presentPicker', Array.from(arguments));
  }

  get identifier() {
    if (!('identifier' in this.state)) {
      return "";
    }
    return this.state.identifier as string;
  }

  set identifier(value: string) {
    this.setState({ identifier: value });
  }
  get title() {
    if (!('title' in this.state)) {
      return "";
    }
    return this.state.title as string;
  }

  set title(value: string) {
    this.setState({ title: value });
  }
  get isSubscribed() {
    if (!('isSubscribed' in this.state)) {
      return false;
    }
    return this.state.isSubscribed as boolean;
  }

  set isSubscribed(value: boolean) {
    this.setState({ isSubscribed: value });
  }
  get allowsContentModifications() {
    if (!('allowsContentModifications' in this.state)) {
      return false;
    }
    return this.state.allowsContentModifications as boolean;
  }

  set allowsContentModifications(value: boolean) {
    this.setState({ allowsContentModifications: value });
  }
  get color() {
    if (!('color' in this.state)) {
      throw new Error('Required property "color" of type "Color" is not set');
    }
    return this.state.color as Color;
  }

  set color(value: Color) {
    this.setState({ color: value });
  }

  /**
     * _Checks if the calendar supports availability._
     *
     * The following values are supported:
     *
     * *   busy
     * *   free
     * *   tentative
     * *   unavailable
     *
     * Not all calendars support all of these availabilities and some calendars may not support availability at all. Use this function to check if the calendar supports a specific
     * availability.
     * @param availability - Availability to check against.
     * @see https://docs.scriptable.app/calendar/#-supportsavailability
     */
  supportsAvailability(availability: "busy" | "free" | "tentative" | "unavailable"): boolean {
    return this.invokeMethod<boolean>('supportsAvailability', Array.from(arguments));
  }
  /**
     * _Saves calendar._
     *
     * Saves changes to the calendar.
     * @see https://docs.scriptable.app/calendar/#-save
     */
  save(): void {
    return this.invokeMethod<void>('save', Array.from(arguments));
  }
  /**
     * _Removes calendar._
     *
     * The calendar is removed immediately. This cannot be undone.
     * @see https://docs.scriptable.app/calendar/#-remove
     */
  remove(): void {
    return this.invokeMethod<void>('remove', Array.from(arguments));
  }
}

export interface CalendarEventState {
  identifier: string;
  title: string;
  location: string;
  notes: string;
  startDate: Date;
  endDate: Date;
  isAllDay: boolean;
  attendees: CalendarEvent.Attendees[];
  availability: "busy" | "free" | "tentative" | "unavailable";
  timeZone: string;
  calendar: Calendar;
}

export class AbsCalendarEvent<S extends CalendarEventState = CalendarEventState> extends ScriptableClass<S> implements CalendarEvent {
  static readonly identifier = 'CalendarEvent';

  get asCalendarEvent(): CalendarEvent {
    return this.asOriginal<CalendarEvent>();
  }

  /**
     * _Presents a view for creating a calendar event._
     *
     * The presented view supports editing various attributes of the event, including title, location, dates, recurrence and alerts.
     * @see https://docs.scriptable.app/calendarevent/#presentcreate
     */
  static presentCreate(): Promise<CalendarEvent> {
    return this.invokeStaticMethod<Promise<CalendarEvent>>('presentCreate', Array.from(arguments));
  }
  /**
     * _Events occurring today._
     * @param calendars - Calendars to fetch events for. Defaults to all calendars.
     * @see https://docs.scriptable.app/calendarevent/#today
     */
  static today(calendars?: readonly Calendar[]): Promise<CalendarEvent[]> {
    return this.invokeStaticMethod<Promise<CalendarEvent[]>>('today', Array.from(arguments));
  }
  /**
     * _Events occurring tomorrow._
     * @param calendars - Calendars to fetch events for. Defaults to all calendars.
     * @see https://docs.scriptable.app/calendarevent/#tomorrow
     */
  static tomorrow(calendars?: readonly Calendar[]): Promise<CalendarEvent[]> {
    return this.invokeStaticMethod<Promise<CalendarEvent[]>>('tomorrow', Array.from(arguments));
  }
  /**
     * _Events that occurred yesterday._
     * @param calendars - Calendars to fetch events for. Defaults to all calendars.
     * @see https://docs.scriptable.app/calendarevent/#yesterday
     */
  static yesterday(calendars?: readonly Calendar[]): Promise<CalendarEvent[]> {
    return this.invokeStaticMethod<Promise<CalendarEvent[]>>('yesterday', Array.from(arguments));
  }
  /**
     * _Events that occur this week._
     * @param calendars - Calendars to fetch events for. Defaults to all calendars.
     * @see https://docs.scriptable.app/calendarevent/#thisweek
     */
  static thisWeek(calendars?: readonly Calendar[]): Promise<CalendarEvent[]> {
    return this.invokeStaticMethod<Promise<CalendarEvent[]>>('thisWeek', Array.from(arguments));
  }
  /**
     * _Events that occur next week._
     * @param calendars - Calendars to fetch events for. Defaults to all calendars.
     * @see https://docs.scriptable.app/calendarevent/#nextweek
     */
  static nextWeek(calendars?: readonly Calendar[]): Promise<CalendarEvent[]> {
    return this.invokeStaticMethod<Promise<CalendarEvent[]>>('nextWeek', Array.from(arguments));
  }
  /**
     * _Events that occurred last week._
     * @param calendars - Calendars to fetch events for. Defaults to all calendars.
     * @see https://docs.scriptable.app/calendarevent/#lastweek
     */
  static lastWeek(calendars?: readonly Calendar[]): Promise<CalendarEvent[]> {
    return this.invokeStaticMethod<Promise<CalendarEvent[]>>('lastWeek', Array.from(arguments));
  }
  /**
     * _Events that occurs between two dates._
     * @param startDate - Start date to fetch events for.
     * @param endDate - End date to fetch events for.
     * @param calendars - Calendars to fetch events for. Defaults to all calendars.
     * @see https://docs.scriptable.app/calendarevent/#between
     */
  static between(startDate: Date, endDate: Date, calendars?: readonly Calendar[]): Promise<CalendarEvent[]> {
    return this.invokeStaticMethod<Promise<CalendarEvent[]>>('between', Array.from(arguments));
  }

  get identifier() {
    if (!('identifier' in this.state)) {
      return "";
    }
    return this.state.identifier as string;
  }

  set identifier(value: string) {
    this.setState({ identifier: value });
  }
  get title() {
    if (!('title' in this.state)) {
      return "";
    }
    return this.state.title as string;
  }

  set title(value: string) {
    this.setState({ title: value });
  }
  get location() {
    if (!('location' in this.state)) {
      return "";
    }
    return this.state.location as string;
  }

  set location(value: string) {
    this.setState({ location: value });
  }
  get notes() {
    if (!('notes' in this.state)) {
      return "";
    }
    return this.state.notes as string;
  }

  set notes(value: string) {
    this.setState({ notes: value });
  }
  get startDate() {
    if (!('startDate' in this.state)) {
      throw new Error('Required property "startDate" of type "Date" is not set');
    }
    return this.state.startDate as Date;
  }

  set startDate(value: Date) {
    this.setState({ startDate: value });
  }
  get endDate() {
    if (!('endDate' in this.state)) {
      throw new Error('Required property "endDate" of type "Date" is not set');
    }
    return this.state.endDate as Date;
  }

  set endDate(value: Date) {
    this.setState({ endDate: value });
  }
  get isAllDay() {
    if (!('isAllDay' in this.state)) {
      return false;
    }
    return this.state.isAllDay as boolean;
  }

  set isAllDay(value: boolean) {
    this.setState({ isAllDay: value });
  }
  get attendees() {
    if (!('attendees' in this.state)) {
      return [];
    }
    return this.state.attendees as CalendarEvent.Attendees[];
  }

  set attendees(value: CalendarEvent.Attendees[]) {
    this.setState({ attendees: value });
  }
  get availability() {
    if (!('availability' in this.state)) {
      throw new Error('Required property "availability" of type ""busy" | "free" | "tentative" | "unavailable"" is not set');
    }
    return this.state.availability as "busy" | "free" | "tentative" | "unavailable";
  }

  set availability(value: "busy" | "free" | "tentative" | "unavailable") {
    this.setState({ availability: value });
  }
  get timeZone() {
    if (!('timeZone' in this.state)) {
      return "";
    }
    return this.state.timeZone as string;
  }

  set timeZone(value: string) {
    this.setState({ timeZone: value });
  }
  get calendar() {
    if (!('calendar' in this.state)) {
      throw new Error('Required property "calendar" of type "Calendar" is not set');
    }
    return this.state.calendar as Calendar;
  }

  set calendar(value: Calendar) {
    this.setState({ calendar: value });
  }

  /**
     * _Adds a recurrence rule._
     *
     * Recurrence rules specify when the event or reminder should be repeated. See the documentation of RecurrenceRule for more information on creating rules.
     * @param recurrenceRule - Recurrence rule to add to the reminder.
     * @see https://docs.scriptable.app/calendarevent/#-addrecurrencerule
     */
  addRecurrenceRule(recurrenceRule: RecurrenceRule): void {
    return this.invokeMethod<void>('addRecurrenceRule', Array.from(arguments));
  }
  /**
     * _Removes all recurrence rules._
     * @see https://docs.scriptable.app/calendarevent/#-removeallrecurrencerules
     */
  removeAllRecurrenceRules(): void {
    return this.invokeMethod<void>('removeAllRecurrenceRules', Array.from(arguments));
  }
  /**
     * _Saves event._
     *
     * Saves changes to an event, inserting it into the calendar if it is newly created.
     * @see https://docs.scriptable.app/calendarevent/#-save
     */
  save(): void {
    return this.invokeMethod<void>('save', Array.from(arguments));
  }
  /**
     * _Removes event from calendar._
     * @see https://docs.scriptable.app/calendarevent/#-remove
     */
  remove(): void {
    return this.invokeMethod<void>('remove', Array.from(arguments));
  }
  /**
     * _Presents a view for editing the calendar event._
     *
     * The presented view supports editing various attributes of the event, including title, location, dates, recurrence and alerts.
     * @see https://docs.scriptable.app/calendarevent/#-presentedit
     */
  presentEdit(): Promise<CalendarEvent> {
    return this.invokeMethod<Promise<CalendarEvent>>('presentEdit', Array.from(arguments));
  }
}

export interface CallbackURLState {

}

export class AbsCallbackURL<T extends string, S extends CallbackURLState = CallbackURLState> extends ScriptableClass<S> implements CallbackURL<T> {
  static readonly identifier = 'CallbackURL';

  get asCallbackURL(): CallbackURL<T> {
    return this.asOriginal<CallbackURL<T>>();
  }



  /**
     * _Construct CallbackURL._
     *
     * Appends a key/value pair to the base URL as a query parameter. The name and value are automatically encoded. Do not add the x-callback-url paramters, i.e. x-source, x-success,
     * x-error and x-cancel as Scriptable will add those.
     * @param name - Name of the query parameter to add.
     * @param value - Value of the query parameter to add.
     * @see https://docs.scriptable.app/callbackurl/#-addparameter
     */
  addParameter(name: string, value: string): void {
    return this.invokeMethod<void>('addParameter', Array.from(arguments));
  }
  /**
     * _Opens the callback URL._
     *
     * Opens the target app and waits for the target app to perform the action. The returned promise contains the query parameters supplied by the target app when it invokes the callback.
     * If the action failed in the target app or the action was cancelled, the promise will be rejected. The promise is also rejected if the action times out because the target app did
     * not invoke the callback.
     * @see https://docs.scriptable.app/callbackurl/#-open
     */
  open(): Promise<
        T extends `shortcuts://x-callback-url/${string}` ? { result: string | number | boolean | null }
            : Record<string, string | number | boolean | null>
    > {
    return this.invokeMethod<Promise<
        T extends `shortcuts://x-callback-url/${string}` ? { result: string | number | boolean | null }
            : Record<string, string | number | boolean | null>
    >>('open', Array.from(arguments));
  }
  /**
     * _Creates the callback URL._
     *
     * Creates a callback URL with the specified base URL and query parameters.
     * @see https://docs.scriptable.app/callbackurl/#-geturl
     */
  getURL(): string {
    return this.invokeMethod<string>('getURL', Array.from(arguments));
  }
}

export interface ColorState {
  hex: string;
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

export class AbsColor<S extends ColorState = ColorState> extends ScriptableClass<S> implements Color {
  static readonly identifier = 'Color';

  get asColor(): Color {
    return this.asOriginal<Color>();
  }

  /**
     * _Constructs a black color._
     * @see https://docs.scriptable.app/color/#black
     */
  static black(): Color {
    return this.invokeStaticMethod<Color>('black', Array.from(arguments));
  }
  /**
     * _Constructs a dark gray color._
     * @see https://docs.scriptable.app/color/#darkgray
     */
  static darkGray(): Color {
    return this.invokeStaticMethod<Color>('darkGray', Array.from(arguments));
  }
  /**
     * _Constructs a light gray color._
     * @see https://docs.scriptable.app/color/#lightgray
     */
  static lightGray(): Color {
    return this.invokeStaticMethod<Color>('lightGray', Array.from(arguments));
  }
  /**
     * _Constructs a white color._
     * @see https://docs.scriptable.app/color/#white
     */
  static white(): Color {
    return this.invokeStaticMethod<Color>('white', Array.from(arguments));
  }
  /**
     * _Constructs a gray color._
     * @see https://docs.scriptable.app/color/#gray
     */
  static gray(): Color {
    return this.invokeStaticMethod<Color>('gray', Array.from(arguments));
  }
  /**
     * _Constructs a red color._
     * @see https://docs.scriptable.app/color/#red
     */
  static red(): Color {
    return this.invokeStaticMethod<Color>('red', Array.from(arguments));
  }
  /**
     * _Constructs a green color._
     * @see https://docs.scriptable.app/color/#green
     */
  static green(): Color {
    return this.invokeStaticMethod<Color>('green', Array.from(arguments));
  }
  /**
     * _Constructs a blue color._
     * @see https://docs.scriptable.app/color/#blue
     */
  static blue(): Color {
    return this.invokeStaticMethod<Color>('blue', Array.from(arguments));
  }
  /**
     * _Constructs a cyan color._
     * @see https://docs.scriptable.app/color/#cyan
     */
  static cyan(): Color {
    return this.invokeStaticMethod<Color>('cyan', Array.from(arguments));
  }
  /**
     * _Constructs a yellow color._
     * @see https://docs.scriptable.app/color/#yellow
     */
  static yellow(): Color {
    return this.invokeStaticMethod<Color>('yellow', Array.from(arguments));
  }
  /**
     * _Constructs a magenta color._
     * @see https://docs.scriptable.app/color/#magenta
     */
  static magenta(): Color {
    return this.invokeStaticMethod<Color>('magenta', Array.from(arguments));
  }
  /**
     * _Constructs a orange color._
     * @see https://docs.scriptable.app/color/#orange
     */
  static orange(): Color {
    return this.invokeStaticMethod<Color>('orange', Array.from(arguments));
  }
  /**
     * _Constructs a purple color._
     * @see https://docs.scriptable.app/color/#purple
     */
  static purple(): Color {
    return this.invokeStaticMethod<Color>('purple', Array.from(arguments));
  }
  /**
     * _Constructs a brown color._
     * @see https://docs.scriptable.app/color/#brown
     */
  static brown(): Color {
    return this.invokeStaticMethod<Color>('brown', Array.from(arguments));
  }
  /**
     * _Constructs a transparent color._
     * @see https://docs.scriptable.app/color/#clear
     */
  static clear(): Color {
    return this.invokeStaticMethod<Color>('clear', Array.from(arguments));
  }
  /**
     * _Creates a dynamic color._
     *
     * The dynamic color will use either its light or dark variant depending on the appearance of the system.
     *
     * Dynamic colors are not supported when used with `DrawContext`.
     * @param lightColor - Color used in light appearance.
     * @param darkColor - Color used in dark appearance.
     * @see https://docs.scriptable.app/color/#dynamic
     */
  static dynamic(lightColor: Color, darkColor: Color): Color {
    return this.invokeStaticMethod<Color>('dynamic', Array.from(arguments));
  }

  get hex() {
    if (!('hex' in this.state)) {
      return "";
    }
    return this.state.hex as string;
  }

  set hex(value: string) {
    this.setState({ hex: value });
  }
  get red() {
    if (!('red' in this.state)) {
      return 0;
    }
    return this.state.red as number;
  }

  set red(value: number) {
    this.setState({ red: value });
  }
  get green() {
    if (!('green' in this.state)) {
      return 0;
    }
    return this.state.green as number;
  }

  set green(value: number) {
    this.setState({ green: value });
  }
  get blue() {
    if (!('blue' in this.state)) {
      return 0;
    }
    return this.state.blue as number;
  }

  set blue(value: number) {
    this.setState({ blue: value });
  }
  get alpha() {
    if (!('alpha' in this.state)) {
      return 0;
    }
    return this.state.alpha as number;
  }

  set alpha(value: number) {
    this.setState({ alpha: value });
  }

}

export interface ContactState {
  identifier: string;
  namePrefix: string;
  givenName: string;
  middleName: string;
  familyName: string;
  nickname: string;
  birthday: Date;
  image: Image;
  emailAddresses: Contact.EmailAddresses[];
  phoneNumbers: Contact.PhoneNumbers[];
  postalAddresses: Contact.PostalAddresses[];
  socialProfiles: Contact.SocialProfiles[];
  note: string;
  urlAddresses: { [key: string]: string; }[];
  dates: { [key: string]: any; }[];
  organizationName: string;
  departmentName: string;
  jobTitle: string;
  isNamePrefixAvailable: boolean;
  isGiveNameAvailable: boolean;
  isMiddleNameAvailable: boolean;
  isFamilyNameAvailable: boolean;
  isNicknameAvailable: boolean;
  isBirthdayAvailable: boolean;
  isEmailAddressesAvailable: boolean;
  isPhoneNumbersAvailable: boolean;
  isPostalAddressesAvailable: boolean;
  isSocialProfilesAvailable: boolean;
  isImageAvailable: boolean;
  isNoteAvailable: boolean;
  isURLAddressesAvailable: boolean;
  isOrganizationNameAvailable: boolean;
  isDepartmentNameAvailable: boolean;
  isJobTitleAvailable: boolean;
  isDatesAvailable: boolean;
}

export class AbsContact<S extends ContactState = ContactState> extends ScriptableClass<S> implements Contact {
  static readonly identifier = 'Contact';

  get asContact(): Contact {
    return this.asOriginal<Contact>();
  }

  /**
     * _Fetches contacts._
     *
     * Fetches the contacts in the specified containers. A contact can be in only one container.
     * @param containers - Containers to fetch contacts from.
     * @see https://docs.scriptable.app/contact/#all
     */
  static all(containers: readonly ContactsContainer[]): Promise<Contact[]> {
    return this.invokeStaticMethod<Promise<Contact[]>>('all', Array.from(arguments));
  }
  /**
     * _Fetches contacts in groups._
     *
     * Fetches the contacts in the specified contacts groups. A contact may belong to many groups.
     * @param groups - Groups to fetch contacts from.
     * @see https://docs.scriptable.app/contact/#ingroups
     */
  static inGroups(groups: readonly ContactsGroup[]): Promise<Contact[]> {
    return this.invokeStaticMethod<Promise<Contact[]>>('inGroups', Array.from(arguments));
  }
  /**
     * _Queues a contact to be added._
     *
     * After you have created a contact, you must queue the contact to be added to the address book and invoke `Contact.persistChanges()` to persist the changes to the address book.
     *
     * For performance reasons, it is best to batch changes to the address book. Therefore you should queue all updates, insertions and removals of contacts and contacts groups to as
     * large batches as possible and then call `Contact.persistChanges()` when you want to persist the changes to the address book.
     * @param contact - Contact to queue to be added.
     * @param containerIdentifier - Optional. Identifier of container to add the contact to. If null is specified, the contact will be added to the default container.
     * @see https://docs.scriptable.app/contact/#add
     */
  static add(contact: Contact, containerIdentifier?: string): void {
    return this.invokeStaticMethod<void>('add', Array.from(arguments));
  }
  /**
     * _Queues an update to a contact._
     *
     * After you have updated one or more properties on a contact, you must queue the contact to be updated and invoke `Contact.persistChanges()` to persist the changes to the address
     * book.
     *
     * For performance reasons, it is best to batch changes to the address book. Therefore you should queue all updates, insertions and removals of contacts and contacts groups to as
     * large batches as possible and then call `Contact.persistChanges()` when you want to persist the changes to the address book.
     * @param contact - Contact to queue to be updated.
     * @see https://docs.scriptable.app/contact/#update
     */
  static update(contact: Contact): void {
    return this.invokeStaticMethod<void>('update', Array.from(arguments));
  }
  /**
     * _Queues a contact to be deleted._
     *
     * To delete a contact, you must queue the contact for deletion and invoke `Contact.persistChanges()` to persist the changes to the address book.
     *
     * For performance reasons, it is best to batch changes to the address book. Therefore you should queue all updates, insertions and removals of contacts and contacts groups to as
     * large batches as possible and then call `Contact.persistChanges()` when you want to persist the changes to the address book.
     * @param contact - Contact to queue to be deleted.
     * @see https://docs.scriptable.app/contact/#delete
     */
  static delete(contact: Contact): void {
    return this.invokeStaticMethod<void>('delete', Array.from(arguments));
  }
  /**
     * _Persist queued changes to the address book._
     *
     * Call this function to persist changes queued with `Contact.add()`, `Contact.update()` and `Contact.delete()`.
     *
     * For performance reasons, it is best to batch changes to the address book. Therefore you should queue all updates, insertions and removals of contacts and contacts groups to as
     * large batches as possible and then call `Contact.persistChanges()` when you want to persist the changes to the address book.
     * @see https://docs.scriptable.app/contact/#persistchanges
     */
  static persistChanges(): Promise<void> {
    return this.invokeStaticMethod<Promise<void>>('persistChanges', Array.from(arguments));
  }

  get identifier() {
    if (!('identifier' in this.state)) {
      return "";
    }
    return this.state.identifier as string;
  }

  set identifier(value: string) {
    this.setState({ identifier: value });
  }
  get namePrefix() {
    if (!('namePrefix' in this.state)) {
      return "";
    }
    return this.state.namePrefix as string;
  }

  set namePrefix(value: string) {
    this.setState({ namePrefix: value });
  }
  get givenName() {
    if (!('givenName' in this.state)) {
      return "";
    }
    return this.state.givenName as string;
  }

  set givenName(value: string) {
    this.setState({ givenName: value });
  }
  get middleName() {
    if (!('middleName' in this.state)) {
      return "";
    }
    return this.state.middleName as string;
  }

  set middleName(value: string) {
    this.setState({ middleName: value });
  }
  get familyName() {
    if (!('familyName' in this.state)) {
      return "";
    }
    return this.state.familyName as string;
  }

  set familyName(value: string) {
    this.setState({ familyName: value });
  }
  get nickname() {
    if (!('nickname' in this.state)) {
      return "";
    }
    return this.state.nickname as string;
  }

  set nickname(value: string) {
    this.setState({ nickname: value });
  }
  get birthday() {
    if (!('birthday' in this.state)) {
      throw new Error('Required property "birthday" of type "Date" is not set');
    }
    return this.state.birthday as Date;
  }

  set birthday(value: Date) {
    this.setState({ birthday: value });
  }
  get image() {
    if (!('image' in this.state)) {
      throw new Error('Required property "image" of type "Image" is not set');
    }
    return this.state.image as Image;
  }

  set image(value: Image) {
    this.setState({ image: value });
  }
  get emailAddresses() {
    if (!('emailAddresses' in this.state)) {
      return [];
    }
    return this.state.emailAddresses as Contact.EmailAddresses[];
  }

  set emailAddresses(value: Contact.EmailAddresses[]) {
    this.setState({ emailAddresses: value });
  }
  get phoneNumbers() {
    if (!('phoneNumbers' in this.state)) {
      return [];
    }
    return this.state.phoneNumbers as Contact.PhoneNumbers[];
  }

  set phoneNumbers(value: Contact.PhoneNumbers[]) {
    this.setState({ phoneNumbers: value });
  }
  get postalAddresses() {
    if (!('postalAddresses' in this.state)) {
      return [];
    }
    return this.state.postalAddresses as Contact.PostalAddresses[];
  }

  set postalAddresses(value: Contact.PostalAddresses[]) {
    this.setState({ postalAddresses: value });
  }
  get socialProfiles() {
    if (!('socialProfiles' in this.state)) {
      return [];
    }
    return this.state.socialProfiles as Contact.SocialProfiles[];
  }

  set socialProfiles(value: Contact.SocialProfiles[]) {
    this.setState({ socialProfiles: value });
  }
  get note() {
    if (!('note' in this.state)) {
      return "";
    }
    return this.state.note as string;
  }

  set note(value: string) {
    this.setState({ note: value });
  }
  get urlAddresses() {
    if (!('urlAddresses' in this.state)) {
      return [];
    }
    return this.state.urlAddresses as { [key: string]: string; }[];
  }

  set urlAddresses(value: { [key: string]: string; }[]) {
    this.setState({ urlAddresses: value });
  }
  get dates() {
    if (!('dates' in this.state)) {
      return [];
    }
    return this.state.dates as { [key: string]: any; }[];
  }

  set dates(value: { [key: string]: any; }[]) {
    this.setState({ dates: value });
  }
  get organizationName() {
    if (!('organizationName' in this.state)) {
      return "";
    }
    return this.state.organizationName as string;
  }

  set organizationName(value: string) {
    this.setState({ organizationName: value });
  }
  get departmentName() {
    if (!('departmentName' in this.state)) {
      return "";
    }
    return this.state.departmentName as string;
  }

  set departmentName(value: string) {
    this.setState({ departmentName: value });
  }
  get jobTitle() {
    if (!('jobTitle' in this.state)) {
      return "";
    }
    return this.state.jobTitle as string;
  }

  set jobTitle(value: string) {
    this.setState({ jobTitle: value });
  }
  get isNamePrefixAvailable() {
    if (!('isNamePrefixAvailable' in this.state)) {
      return false;
    }
    return this.state.isNamePrefixAvailable as boolean;
  }

  set isNamePrefixAvailable(value: boolean) {
    this.setState({ isNamePrefixAvailable: value });
  }
  get isGiveNameAvailable() {
    if (!('isGiveNameAvailable' in this.state)) {
      return false;
    }
    return this.state.isGiveNameAvailable as boolean;
  }

  set isGiveNameAvailable(value: boolean) {
    this.setState({ isGiveNameAvailable: value });
  }
  get isMiddleNameAvailable() {
    if (!('isMiddleNameAvailable' in this.state)) {
      return false;
    }
    return this.state.isMiddleNameAvailable as boolean;
  }

  set isMiddleNameAvailable(value: boolean) {
    this.setState({ isMiddleNameAvailable: value });
  }
  get isFamilyNameAvailable() {
    if (!('isFamilyNameAvailable' in this.state)) {
      return false;
    }
    return this.state.isFamilyNameAvailable as boolean;
  }

  set isFamilyNameAvailable(value: boolean) {
    this.setState({ isFamilyNameAvailable: value });
  }
  get isNicknameAvailable() {
    if (!('isNicknameAvailable' in this.state)) {
      return false;
    }
    return this.state.isNicknameAvailable as boolean;
  }

  set isNicknameAvailable(value: boolean) {
    this.setState({ isNicknameAvailable: value });
  }
  get isBirthdayAvailable() {
    if (!('isBirthdayAvailable' in this.state)) {
      return false;
    }
    return this.state.isBirthdayAvailable as boolean;
  }

  set isBirthdayAvailable(value: boolean) {
    this.setState({ isBirthdayAvailable: value });
  }
  get isEmailAddressesAvailable() {
    if (!('isEmailAddressesAvailable' in this.state)) {
      return false;
    }
    return this.state.isEmailAddressesAvailable as boolean;
  }

  set isEmailAddressesAvailable(value: boolean) {
    this.setState({ isEmailAddressesAvailable: value });
  }
  get isPhoneNumbersAvailable() {
    if (!('isPhoneNumbersAvailable' in this.state)) {
      return false;
    }
    return this.state.isPhoneNumbersAvailable as boolean;
  }

  set isPhoneNumbersAvailable(value: boolean) {
    this.setState({ isPhoneNumbersAvailable: value });
  }
  get isPostalAddressesAvailable() {
    if (!('isPostalAddressesAvailable' in this.state)) {
      return false;
    }
    return this.state.isPostalAddressesAvailable as boolean;
  }

  set isPostalAddressesAvailable(value: boolean) {
    this.setState({ isPostalAddressesAvailable: value });
  }
  get isSocialProfilesAvailable() {
    if (!('isSocialProfilesAvailable' in this.state)) {
      return false;
    }
    return this.state.isSocialProfilesAvailable as boolean;
  }

  set isSocialProfilesAvailable(value: boolean) {
    this.setState({ isSocialProfilesAvailable: value });
  }
  get isImageAvailable() {
    if (!('isImageAvailable' in this.state)) {
      return false;
    }
    return this.state.isImageAvailable as boolean;
  }

  set isImageAvailable(value: boolean) {
    this.setState({ isImageAvailable: value });
  }
  get isNoteAvailable() {
    if (!('isNoteAvailable' in this.state)) {
      return false;
    }
    return this.state.isNoteAvailable as boolean;
  }

  set isNoteAvailable(value: boolean) {
    this.setState({ isNoteAvailable: value });
  }
  get isURLAddressesAvailable() {
    if (!('isURLAddressesAvailable' in this.state)) {
      return false;
    }
    return this.state.isURLAddressesAvailable as boolean;
  }

  set isURLAddressesAvailable(value: boolean) {
    this.setState({ isURLAddressesAvailable: value });
  }
  get isOrganizationNameAvailable() {
    if (!('isOrganizationNameAvailable' in this.state)) {
      return false;
    }
    return this.state.isOrganizationNameAvailable as boolean;
  }

  set isOrganizationNameAvailable(value: boolean) {
    this.setState({ isOrganizationNameAvailable: value });
  }
  get isDepartmentNameAvailable() {
    if (!('isDepartmentNameAvailable' in this.state)) {
      return false;
    }
    return this.state.isDepartmentNameAvailable as boolean;
  }

  set isDepartmentNameAvailable(value: boolean) {
    this.setState({ isDepartmentNameAvailable: value });
  }
  get isJobTitleAvailable() {
    if (!('isJobTitleAvailable' in this.state)) {
      return false;
    }
    return this.state.isJobTitleAvailable as boolean;
  }

  set isJobTitleAvailable(value: boolean) {
    this.setState({ isJobTitleAvailable: value });
  }
  get isDatesAvailable() {
    if (!('isDatesAvailable' in this.state)) {
      return false;
    }
    return this.state.isDatesAvailable as boolean;
  }

  set isDatesAvailable(value: boolean) {
    this.setState({ isDatesAvailable: value });
  }

}

export interface ContactsContainerState {
  identifier: string;
  name: string;
}

export class AbsContactsContainer<S extends ContactsContainerState = ContactsContainerState> extends ScriptableClass<S> implements ContactsContainer {
  static readonly identifier = 'ContactsContainer';

  get asContactsContainer(): ContactsContainer {
    return this.asOriginal<ContactsContainer>();
  }

  /**
     * _Fetches default contacts container._
     * @see https://docs.scriptable.app/contactscontainer/#default
     */
  static default(): Promise<ContactsContainer> {
    return this.invokeStaticMethod<Promise<ContactsContainer>>('default', Array.from(arguments));
  }
  /**
     * _Fetches all contacts containers._
     * @see https://docs.scriptable.app/contactscontainer/#all
     */
  static all(): Promise<ContactsContainer[]> {
    return this.invokeStaticMethod<Promise<ContactsContainer[]>>('all', Array.from(arguments));
  }
  /**
     * _Fetches a contacts container._
     * @param identifier - Identifier of the contacts container to fetch.
     * @see https://docs.scriptable.app/contactscontainer/#withidentifier
     */
  static withIdentifier(identifier: string): Promise<ContactsContainer> {
    return this.invokeStaticMethod<Promise<ContactsContainer>>('withIdentifier', Array.from(arguments));
  }

  get identifier() {
    if (!('identifier' in this.state)) {
      return "";
    }
    return this.state.identifier as string;
  }

  set identifier(value: string) {
    this.setState({ identifier: value });
  }
  get name() {
    if (!('name' in this.state)) {
      return "";
    }
    return this.state.name as string;
  }

  set name(value: string) {
    this.setState({ name: value });
  }

}

export interface ContactsGroupState {
  identifier: string;
  name: string;
}

export class AbsContactsGroup<S extends ContactsGroupState = ContactsGroupState> extends ScriptableClass<S> implements ContactsGroup {
  static readonly identifier = 'ContactsGroup';

  get asContactsGroup(): ContactsGroup {
    return this.asOriginal<ContactsGroup>();
  }

  /**
     * _Fetches contacts groups._
     *
     * Fetches the contacts groups in the specified containers. A group can be in only one container.
     * @param containers - Container to fetch contacts groups from.
     * @see https://docs.scriptable.app/contactsgroup/#all
     */
  static all(containers: readonly ContactsContainer[]): Promise<ContactsGroup[]> {
    return this.invokeStaticMethod<Promise<ContactsGroup[]>>('all', Array.from(arguments));
  }
  /**
     * _Queues a contacts group to be added._
     *
     * After you have created a group, you must queue the group to be added to the address book and invoke `Contact.persistChanges()` to persist the changes to the address book.
     *
     * For performance reasons, it is best to batch changes to the address book. Therefore you should queue all updates, insertions and removals of contacts and contacts groups to as
     * large batches as possible and then call `Contact.persistChanges()` when you want to persist the changes to the address book.
     * @param group - Contacts group to queue to be added.
     * @param containerIdentifier - Optional. Identifier of container to add the contacts group to. If null is specified, the group will be added to the default container.
     * @see https://docs.scriptable.app/contactsgroup/#add
     */
  static add(group: ContactsGroup, containerIdentifier?: string): void {
    return this.invokeStaticMethod<void>('add', Array.from(arguments));
  }
  /**
     * _Queues an update to a contacts group._
     *
     * After you have updated one or more properties on a contacts group, you must queue the group to be updated and invoke `Contact.persistChanges()` to persist the changes to the
     * address book.
     *
     * For performance reasons, it is best to batch changes to the address book. Therefore you should queue all updates, insertions and removals of contacts and contacts groups to as
     * large batches as possible and then call `Contact.persistChanges()` when you want to persist the changes to the address book.
     * @param group - Contacts group to queue to be updated.
     * @see https://docs.scriptable.app/contactsgroup/#update
     */
  static update(group: ContactsGroup): void {
    return this.invokeStaticMethod<void>('update', Array.from(arguments));
  }
  /**
     * _Queues a contacts group to be deleted._
     *
     * To delete a contacts group, you must queue the group for deletion and invoke `Contact.persistChanges()` to persist the changes to the address book.
     *
     * For performance reasons, it is best to batch changes to the address book. Therefore you should queue all updates, insertions and removals of contacts and contacts groups to as
     * large batches as possible and then call `Contact.persistChanges()` when you want to persist the changes to the address book.
     * @param group - Contacts group to queue to be deleted.
     * @see https://docs.scriptable.app/contactsgroup/#delete
     */
  static delete(group: ContactsGroup): void {
    return this.invokeStaticMethod<void>('delete', Array.from(arguments));
  }

  get identifier() {
    if (!('identifier' in this.state)) {
      return "";
    }
    return this.state.identifier as string;
  }

  set identifier(value: string) {
    this.setState({ identifier: value });
  }
  get name() {
    if (!('name' in this.state)) {
      return "";
    }
    return this.state.name as string;
  }

  set name(value: string) {
    this.setState({ name: value });
  }

  /**
     * _Adds a contact to the group._
     *
     * In order to persist the change, you should call `Contact.persistChanges()`. It is important that the contact is added to the address book. To add the contact to the address book,
     * you should queue it for insertion using `Contact.add()` before persisting the changes.
     * @param contact - Contact to add to the group.
     * @see https://docs.scriptable.app/contactsgroup/#-addmember
     */
  addMember(contact: Contact): void {
    return this.invokeMethod<void>('addMember', Array.from(arguments));
  }
  /**
     * _Removes a contact from the group._
     *
     * In order to persist the change, you should call `Contact.persistChanges()`. It is important that the contact is added to the address book. To add the contact to the address book,
     * you should queue it for insertion using `Contact.add()` before persisting the changes.
     * @param contact - Contact to add to the group.
     * @see https://docs.scriptable.app/contactsgroup/#-removemember
     */
  removeMember(contact: Contact): void {
    return this.invokeMethod<void>('removeMember', Array.from(arguments));
  }
}

export interface DataState {

}

export class AbsData<S extends DataState = DataState> extends ScriptableClass<S> implements Data {
  static readonly identifier = 'Data';

  get asData(): Data {
    return this.asOriginal<Data>();
  }

  /**
     * _Creates data from string._
     *
     * The provided string is assumed to be UTF8 encoded. If the string is not UTF8 encoded, the function will return null.
     * @param string - String to create data from.
     * @see https://docs.scriptable.app/data/#fromstring
     */
  static fromString(string: string): Data {
    return this.invokeStaticMethod<Data>('fromString', Array.from(arguments));
  }
  /**
     * _Reads data from file path._
     *
     * Reads the raw data of the file at the specified file path.
     * @param filePath - Path of file to read data from.
     * @see https://docs.scriptable.app/data/#fromfile
     */
  static fromFile(filePath: string): Data {
    return this.invokeStaticMethod<Data>('fromFile', Array.from(arguments));
  }
  /**
     * _Creates data from base64 encoded string._
     *
     * The supplied string must be base64 encoded otherwise the function will return null.
     * @param base64String - Base64 encoded string to create data from.
     * @see https://docs.scriptable.app/data/#frombase64string
     */
  static fromBase64String(base64String: string): Data {
    return this.invokeStaticMethod<Data>('fromBase64String', Array.from(arguments));
  }
  /**
     * _Creates data from JPEG image._
     * @param image - JPEG image to convert to data.
     * @see https://docs.scriptable.app/data/#fromjpeg
     */
  static fromJPEG(image: Image): Data {
    return this.invokeStaticMethod<Data>('fromJPEG', Array.from(arguments));
  }
  /**
     * _Creates data from PNG image._
     * @param image - PNG image to convert to data.
     * @see https://docs.scriptable.app/data/#frompng
     */
  static fromPNG(image: Image): Data {
    return this.invokeStaticMethod<Data>('fromPNG', Array.from(arguments));
  }
  /**
     * _Creates data from an array of bytes._
     * @param bytes - Array of bytes to convert to data.
     * @see https://docs.scriptable.app/data/#frombytes
     */
  static fromBytes(bytes: readonly number[]): Data {
    return this.invokeStaticMethod<Data>('fromBytes', Array.from(arguments));
  }


  /**
     * _Creates a string from the data._
     *
     * The data is assumed to represent a UTF8 encoded string. If the string is not UTF8 encoded string, the function will return null.
     * @see https://docs.scriptable.app/data/#-torawstring
     */
  toRawString(): string {
    return this.invokeMethod<string>('toRawString', Array.from(arguments));
  }
  /**
     * _Creates a base64 encoded string._
     *
     * Creates a base64 encoded string from the data.
     * @see https://docs.scriptable.app/data/#-tobase64string
     */
  toBase64String(): string {
    return this.invokeMethod<string>('toBase64String', Array.from(arguments));
  }
  /**
     * _Gets bytes from data._
     * @see https://docs.scriptable.app/data/#-getbytes
     */
  getBytes(): number[] {
    return this.invokeMethod<number[]>('getBytes', Array.from(arguments));
  }
}

export interface DateFormatterState {
  dateFormat: string;
  locale: string;
}

export class AbsDateFormatter<S extends DateFormatterState = DateFormatterState> extends ScriptableClass<S> implements DateFormatter {
  static readonly identifier = 'DateFormatter';

  get asDateFormatter(): DateFormatter {
    return this.asOriginal<DateFormatter>();
  }


  get dateFormat() {
    if (!('dateFormat' in this.state)) {
      return "";
    }
    return this.state.dateFormat as string;
  }

  set dateFormat(value: string) {
    this.setState({ dateFormat: value });
  }
  get locale() {
    if (!('locale' in this.state)) {
      return "";
    }
    return this.state.locale as string;
  }

  set locale(value: string) {
    this.setState({ locale: value });
  }

  /**
     * _Creates a string from a date._
     * @param date - The date to convert to a string.
     * @see https://docs.scriptable.app/dateformatter/#-string
     */
  string(date: Date): string {
    return this.invokeMethod<string>('string', Array.from(arguments));
  }
  /**
     * _Creates a date from a string._
     *
     * Uses the date formatters configuration to parse the string into a date. If the string cannot be parsed with the date foramtters configuration, the function will return null.
     * @param str - The string to parse into a date.
     * @see https://docs.scriptable.app/dateformatter/#-date
     */
  date(str: string): Date {
    return this.invokeMethod<Date>('date', Array.from(arguments));
  }
  /**
     * _Use no style for the date._
     *
     * This will remove the date from the formatted string.
     * @see https://docs.scriptable.app/dateformatter/#-usenodatestyle
     */
  useNoDateStyle(): void {
    return this.invokeMethod<void>('useNoDateStyle', Array.from(arguments));
  }
  /**
     * _Use a short style for the date._
     *
     * Dates with a short style are typically numeric only e.g. "08/23/19".
     * @see https://docs.scriptable.app/dateformatter/#-useshortdatestyle
     */
  useShortDateStyle(): void {
    return this.invokeMethod<void>('useShortDateStyle', Array.from(arguments));
  }
  /**
     * _Use a medium style for the date._
     *
     * Dates with a medium style usually includes abbreviations, e.g. "Aug 23, 2019".
     * @see https://docs.scriptable.app/dateformatter/#-usemediumdatestyle
     */
  useMediumDateStyle(): void {
    return this.invokeMethod<void>('useMediumDateStyle', Array.from(arguments));
  }
  /**
     * _Use a long style for the date._
     *
     * Dates with a long style usually includes a full text, e.g. "August 23, 2019".
     * @see https://docs.scriptable.app/dateformatter/#-uselongdatestyle
     */
  useLongDateStyle(): void {
    return this.invokeMethod<void>('useLongDateStyle', Array.from(arguments));
  }
  /**
     * _Use a full style for the date._
     *
     * Dates with a full style includes all details, e.g. "Friday, August 23, 2019 AD".
     * @see https://docs.scriptable.app/dateformatter/#-usefulldatestyle
     */
  useFullDateStyle(): void {
    return this.invokeMethod<void>('useFullDateStyle', Array.from(arguments));
  }
  /**
     * _Use no style for the time._
     *
     * This will remove the time from the formatted string.
     * @see https://docs.scriptable.app/dateformatter/#-usenotimestyle
     */
  useNoTimeStyle(): void {
    return this.invokeMethod<void>('useNoTimeStyle', Array.from(arguments));
  }
  /**
     * _Use a short style for the time._
     *
     * Times with a short style are typically numeric only but also includes the period for 12-hour clocks, e.g. "7:17 PM".
     * @see https://docs.scriptable.app/dateformatter/#-useshorttimestyle
     */
  useShortTimeStyle(): void {
    return this.invokeMethod<void>('useShortTimeStyle', Array.from(arguments));
  }
  /**
     * _Use a short style for the time._
     *
     * Times with a medium style usually includes abbreviations, e.g. "7:16:42 PM".
     * @see https://docs.scriptable.app/dateformatter/#-usemediumtimestyle
     */
  useMediumTimeStyle(): void {
    return this.invokeMethod<void>('useMediumTimeStyle', Array.from(arguments));
  }
  /**
     * _Use a long style for the time._
     *
     * Times with a long style usually includes a full text, e.g. "7:16:42 PM PST".
     * @see https://docs.scriptable.app/dateformatter/#-uselongtimestyle
     */
  useLongTimeStyle(): void {
    return this.invokeMethod<void>('useLongTimeStyle', Array.from(arguments));
  }
  /**
     * _Use a full style for the time._
     *
     * Times with a full style includes all details, e.g. "7:16:42 PM Pacific Standard Time".
     * @see https://docs.scriptable.app/dateformatter/#-usefulltimestyle
     */
  useFullTimeStyle(): void {
    return this.invokeMethod<void>('useFullTimeStyle', Array.from(arguments));
  }
}

export interface DatePickerState {
  minimumDate: Date;
  maximumDate: Date;
  countdownDuration: number;
  minuteInterval: number;
  initialDate: Date;
}

export class AbsDatePicker<S extends DatePickerState = DatePickerState> extends ScriptableClass<S> implements DatePicker {
  static readonly identifier = 'DatePicker';

  get asDatePicker(): DatePicker {
    return this.asOriginal<DatePicker>();
  }


  get minimumDate() {
    if (!('minimumDate' in this.state)) {
      throw new Error('Required property "minimumDate" of type "Date" is not set');
    }
    return this.state.minimumDate as Date;
  }

  set minimumDate(value: Date) {
    this.setState({ minimumDate: value });
  }
  get maximumDate() {
    if (!('maximumDate' in this.state)) {
      throw new Error('Required property "maximumDate" of type "Date" is not set');
    }
    return this.state.maximumDate as Date;
  }

  set maximumDate(value: Date) {
    this.setState({ maximumDate: value });
  }
  get countdownDuration() {
    if (!('countdownDuration' in this.state)) {
      return 0;
    }
    return this.state.countdownDuration as number;
  }

  set countdownDuration(value: number) {
    this.setState({ countdownDuration: value });
  }
  get minuteInterval() {
    if (!('minuteInterval' in this.state)) {
      return 0;
    }
    return this.state.minuteInterval as number;
  }

  set minuteInterval(value: number) {
    this.setState({ minuteInterval: value });
  }
  get initialDate() {
    if (!('initialDate' in this.state)) {
      throw new Error('Required property "initialDate" of type "Date" is not set');
    }
    return this.state.initialDate as Date;
  }

  set initialDate(value: Date) {
    this.setState({ initialDate: value });
  }

  /**
     * _Presents the date picker displaying hours and minutes._
     *
     * Use the method to pick a time. The date picker will display hours and minutes and, depending on the locale of the device, an AM/PM designation.
     *
     * The returned date will be the current date with the hour and minute set to the selected values. Use the `initialDate` property to set the initially selected date.
     * @see https://docs.scriptable.app/datepicker/#-picktime
     */
  pickTime(): Promise<Date> {
    return this.invokeMethod<Promise<Date>>('pickTime', Array.from(arguments));
  }
  /**
     * _Presents the date picker displaying day, month and year._
     *
     * Use the method to pick a date. The date picker will display the day, month and year. Use the `initialDate` property to set the initially selected date.
     * @see https://docs.scriptable.app/datepicker/#-pickdate
     */
  pickDate(): Promise<Date> {
    return this.invokeMethod<Promise<Date>>('pickDate', Array.from(arguments));
  }
  /**
     * _Presents the date picker displaying date and time._
     *
     * Use the method to pick a date and a time. The date picker will display the day, month, year, hour, minutes and, depending on the locale of the device, an AM/PM designation. Use the
     * `initialDate` property to set the initially selected date.
     * @see https://docs.scriptable.app/datepicker/#-pickdateandtime
     */
  pickDateAndTime(): Promise<Date> {
    return this.invokeMethod<Promise<Date>>('pickDateAndTime', Array.from(arguments));
  }
  /**
     * _Presents the date picker for selecting the duration of a countdown._
     *
     * Use the method to pick the duration of a countdown, e.g. a timer. The date picker will display hours and minutes. Use the `countdownDuration` property to set the initially selected
     * duration.
     * @see https://docs.scriptable.app/datepicker/#-pickcountdownduration
     */
  pickCountdownDuration(): Promise<number> {
    return this.invokeMethod<Promise<number>>('pickCountdownDuration', Array.from(arguments));
  }
}

export interface DrawContextState {
  size: Size;
  respectScreenScale: boolean;
  opaque: boolean;
}

export class AbsDrawContext<S extends DrawContextState = DrawContextState> extends ScriptableClass<S> implements DrawContext {
  static readonly identifier = 'DrawContext';

  get asDrawContext(): DrawContext {
    return this.asOriginal<DrawContext>();
  }


  get size() {
    if (!('size' in this.state)) {
      throw new Error('Required property "size" of type "Size" is not set');
    }
    return this.state.size as Size;
  }

  set size(value: Size) {
    this.setState({ size: value });
  }
  get respectScreenScale() {
    if (!('respectScreenScale' in this.state)) {
      return false;
    }
    return this.state.respectScreenScale as boolean;
  }

  set respectScreenScale(value: boolean) {
    this.setState({ respectScreenScale: value });
  }
  get opaque() {
    if (!('opaque' in this.state)) {
      return false;
    }
    return this.state.opaque as boolean;
  }

  set opaque(value: boolean) {
    this.setState({ opaque: value });
  }

  /**
     * _Retrieves the image._
     *
     * Call this to retrieve the image you have drawn to the context.
     * @see https://docs.scriptable.app/drawcontext/#-getimage
     */
  getImage(): Image {
    return this.invokeMethod<Image>('getImage', Array.from(arguments));
  }
  /**
     * _Draws an image in the specified rect._
     *
     * Draws the image in the rectangle. The image will be scaled to fit within the rectangle.
     * @param image - Image to draw.
     * @param rect - Rectangle to draw the image in.
     * @see https://docs.scriptable.app/drawcontext/#-drawimageinrect
     */
  drawImageInRect(image: Image, rect: Rect): void {
    return this.invokeMethod<void>('drawImageInRect', Array.from(arguments));
  }
  /**
     * _Draws an image at the specified point._
     *
     * Draws the image at the point. The top-left corner of the image will be drawn at the specified point.
     * @param image - Image to draw.
     * @param point - Point at which to draw top-left corner of the image.
     * @see https://docs.scriptable.app/drawcontext/#-drawimageatpoint
     */
  drawImageAtPoint(image: Image, point: Point): void {
    return this.invokeMethod<void>('drawImageAtPoint', Array.from(arguments));
  }
  /**
     * _Sets the fill color._
     *
     * Sets the fill color to be used when performing a fill operation. Any fill operation performed afterwards will fill with the specified color until another call to setFillColor is
     * made.
     * @param color - Color to set for filling.
     * @see https://docs.scriptable.app/drawcontext/#-setfillcolor
     */
  setFillColor(color: Color): void {
    return this.invokeMethod<void>('setFillColor', Array.from(arguments));
  }
  /**
     * _Sets the stroke color._
     *
     * Sets the stroke color to be used when performing a stroke operation. Any stroke operation performed afterwards will stroke with the specified color until another call to
     * setStrokeColor is made.
     * @param color - Color to set for stroking.
     * @see https://docs.scriptable.app/drawcontext/#-setstrokecolor
     */
  setStrokeColor(color: Color): void {
    return this.invokeMethod<void>('setStrokeColor', Array.from(arguments));
  }
  /**
     * _Sets the line width for stroking._
     *
     * Sets the line width to be used when performing a stroke operation.
     * @param width - Line width to use for stroking.
     * @see https://docs.scriptable.app/drawcontext/#-setlinewidth
     */
  setLineWidth(width: number): void {
    return this.invokeMethod<void>('setLineWidth', Array.from(arguments));
  }
  /**
     * _Fills a rectangle._
     *
     * Fills the rectangle with the color set when calling setFillColor.
     * @param rect - Rectangle to fill.
     * @see https://docs.scriptable.app/drawcontext/#-fill
     */
  fill(rect: Rect): void {
    return this.invokeMethod<void>('fill', Array.from(arguments));
  }
  /**
     * _Fills a rectangle._
     *
     * Fills the rectangle with the color set when calling setFillColor.
     * @param rect - Rectangle to fill.
     * @see https://docs.scriptable.app/drawcontext/#-fillrect
     */
  fillRect(rect: Rect): void {
    return this.invokeMethod<void>('fillRect', Array.from(arguments));
  }
  /**
     * _Fills an ellipse._
     *
     * Fills the ellipse that fits within the supplied rectangle with the color set when calling setFillColor.
     * @param rect - Rectangle incapsulating the ellipse to fill.
     * @see https://docs.scriptable.app/drawcontext/#-fillellipse
     */
  fillEllipse(rect: Rect): void {
    return this.invokeMethod<void>('fillEllipse', Array.from(arguments));
  }
  /**
     * _Strokes a rectangle._
     *
     * Draws a line around the rectangle using the color set when calling setStrokeColor. The line will have the width set when calling setLineWidth.
     * @param rect - Rectangle to stroke.
     * @see https://docs.scriptable.app/drawcontext/#-stroke
     */
  stroke(rect: Rect): void {
    return this.invokeMethod<void>('stroke', Array.from(arguments));
  }
  /**
     * _Strokes a rectangle._
     *
     * Draws a line around the rectangle using the color set when calling setStrokeColor. The line will have the width set when calling setLineWidth.
     * @param rect - Rectangle to stroke.
     * @see https://docs.scriptable.app/drawcontext/#-strokerect
     */
  strokeRect(rect: Rect): void {
    return this.invokeMethod<void>('strokeRect', Array.from(arguments));
  }
  /**
     * _Strokes an ellipse._
     *
     * Draws a line around the ellipse that fits within the supplied rectangle. The line will have the color set when calling setStrokeColor and the width set when calling setLineWidth.
     * @param rect - Rectangle incapsulating the ellipse to stroke.
     * @see https://docs.scriptable.app/drawcontext/#-strokeellipse
     */
  strokeEllipse(rect: Rect): void {
    return this.invokeMethod<void>('strokeEllipse', Array.from(arguments));
  }
  /**
     * _Adds a path to the context._
     *
     * After adding a path to the context, the path can be stroked or filled by calling strokePath and fillPath. Note that only the path that was added latest will be affected by calls to
     * strokePath and fillPath.
     * @param path - Path to add to the context.
     * @see https://docs.scriptable.app/drawcontext/#-addpath
     */
  addPath(path: Path): void {
    return this.invokeMethod<void>('addPath', Array.from(arguments));
  }
  /**
     * _Strokes the path that was added the latest._
     *
     * The path that was added the latest to the context is stroked with the color set using setStrokeColor and the line width set using setLineWidth.
     * @see https://docs.scriptable.app/drawcontext/#-strokepath
     */
  strokePath(): void {
    return this.invokeMethod<void>('strokePath', Array.from(arguments));
  }
  /**
     * _Fills the path that was added the latest._
     *
     * The path that was latest added to the context is filled with the color set using setFillColor.
     * @see https://docs.scriptable.app/drawcontext/#-fillpath
     */
  fillPath(): void {
    return this.invokeMethod<void>('fillPath', Array.from(arguments));
  }
  /**
     * _Draws text at a position._
     *
     * Call this to draw a text string to the context. The top-left of the text will be drawn at the specified position.
     * @param text - Text to draw.
     * @param pos - Position to draw the top-left of the text.
     * @see https://docs.scriptable.app/drawcontext/#-drawtext
     */
  drawText(text: string, pos: Point): void {
    return this.invokeMethod<void>('drawText', Array.from(arguments));
  }
  /**
     * _Draws text in a rectangle._
     *
     * Call this to draw a text string in a rectangle. Specify how the text should be aligned within the rectangle by calling setTextAlignedLeft, setTextAlignedCenter or
     * setTextAlignedRight before drawing the text.
     * @param text - Text to draw.
     * @param rect - Rectangle to draw text in.
     * @see https://docs.scriptable.app/drawcontext/#-drawtextinrect
     */
  drawTextInRect(text: string, rect: Rect): void {
    return this.invokeMethod<void>('drawTextInRect', Array.from(arguments));
  }
  /**
     * _Sets the font to use when drawing text._
     *
     * Sets the font to be used when drawing texts to the context.
     * @param font - Font to use when drawing text.
     * @see https://docs.scriptable.app/drawcontext/#-setfont
     */
  setFont(font: Font): void {
    return this.invokeMethod<void>('setFont', Array.from(arguments));
  }
  /**
     * _Sets the text color used when drawing text._
     *
     * Sets the text color to be used when drawing text strings to the context.
     * @param color - Color to use when drawing text.
     * @see https://docs.scriptable.app/drawcontext/#-settextcolor
     */
  setTextColor(color: Color): void {
    return this.invokeMethod<void>('setTextColor', Array.from(arguments));
  }
  /**
     * _Specifies that texts should be left aligned._
     *
     * Sets text alignment to left. Texts drawn after calling this will be left aligned inside the provided rectangle.
     * @see https://docs.scriptable.app/drawcontext/#-settextalignedleft
     */
  setTextAlignedLeft(): void {
    return this.invokeMethod<void>('setTextAlignedLeft', Array.from(arguments));
  }
  /**
     * _Specifies that texts should be center aligned._
     *
     * Sets text alignment to center. Texts drawn after calling this will be center aligned inside the provided rectangle.
     * @see https://docs.scriptable.app/drawcontext/#-settextalignedcenter
     */
  setTextAlignedCenter(): void {
    return this.invokeMethod<void>('setTextAlignedCenter', Array.from(arguments));
  }
  /**
     * _Specifies that texts should be right aligned._
     *
     * Sets text alignment to right. Texts drawn after calling this will be right aligned inside the provided rectangle.
     * @see https://docs.scriptable.app/drawcontext/#-settextalignedright
     */
  setTextAlignedRight(): void {
    return this.invokeMethod<void>('setTextAlignedRight', Array.from(arguments));
  }
}

export interface FileManagerState {

}

export class AbsFileManager<S extends FileManagerState = FileManagerState> extends ScriptableClass<S> implements FileManager {
  static readonly identifier = 'FileManager';

  get asFileManager(): FileManager {
    return this.asOriginal<FileManager>();
  }

  /**
     * _Creates a local FileManager._
     *
     * Creates a file manager for operating with files stored locally.
     * @see https://docs.scriptable.app/filemanager/#local
     */
  static local(): FileManager {
    return this.invokeStaticMethod<FileManager>('local', Array.from(arguments));
  }
  /**
     * _Creates an iCloud FileManager._
     *
     * Creates a file manager for operating with files stored in iCloud. iCloud must be enabled on the device in order to use this.
     * @see https://docs.scriptable.app/filemanager/#icloud
     */
  static iCloud(): FileManager {
    return this.invokeStaticMethod<FileManager>('iCloud', Array.from(arguments));
  }


  /**
     * _Read contents of a file as data._
     *
     * Reads the contents of the file specified by the file path as raw data. To read the file as a string see `readString(filePath)` and to read it as an image see `readImage(filePath)`.
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of the file to read.
     * @see https://docs.scriptable.app/filemanager/#-read
     */
  read(filePath: string): Data {
    return this.invokeMethod<Data>('read', Array.from(arguments));
  }
  /**
     * _Read contents of a file as string._
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of the file to read.
     * @see https://docs.scriptable.app/filemanager/#-readstring
     */
  readString(filePath: string): string {
    return this.invokeMethod<string>('readString', Array.from(arguments));
  }
  /**
     * _Read contents of a file as an image._
     *
     * Reads the contents of the file specified by the file path and converts it to an image.
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of the file to read.
     * @see https://docs.scriptable.app/filemanager/#-readimage
     */
  readImage(filePath: string): Image {
    return this.invokeMethod<Image>('readImage', Array.from(arguments));
  }
  /**
     * _Write data to a file._
     * @param filePath - Path of file to write to.
     * @param content - Data to write to disk.
     * @see https://docs.scriptable.app/filemanager/#-write
     */
  write(filePath: string, content: Data): void {
    return this.invokeMethod<void>('write', Array.from(arguments));
  }
  /**
     * _Write a string to a file._
     *
     * Writes the content to the specified file path on disk. If the file does not already exist, it will be created. If the file already exists the contents of the file will be
     * overwritten with the new content.
     * @param filePath - Path of file to write to.
     * @param content - Content to write to disk.
     * @see https://docs.scriptable.app/filemanager/#-writestring
     */
  writeString(filePath: string, content: string): void {
    return this.invokeMethod<void>('writeString', Array.from(arguments));
  }
  /**
     * _Write an image to a file._
     *
     * Writes the image to the specified file path on disk. If the file does not already exist, it will be created. If the file already exists the contents of the file will be overwritten
     * with the new content.
     * @param filePath - Path of file to write to.
     * @param image - Image to write to disk.
     * @see https://docs.scriptable.app/filemanager/#-writeimage
     */
  writeImage(filePath: string, image: Image): void {
    return this.invokeMethod<void>('writeImage', Array.from(arguments));
  }
  /**
     * _Removes a file._
     *
     * Removes the file at the specified path. Use with caution. Removed files cannot be restored.
     * @param filePath - Path of file to remove.
     * @see https://docs.scriptable.app/filemanager/#-remove
     */
  remove(filePath: string): void {
    return this.invokeMethod<void>('remove', Array.from(arguments));
  }
  /**
     * _Moves a file._
     *
     * Moves the file from the source path to the destination path. Caution: This operation will replace any existing file at the the destination.
     * @param sourceFilePath - Path of the file to move.
     * @param destinationFilePath - Path to move the file to.
     * @see https://docs.scriptable.app/filemanager/#-move
     */
  move(sourceFilePath: string, destinationFilePath: string): void {
    return this.invokeMethod<void>('move', Array.from(arguments));
  }
  /**
     * _Copies a file._
     *
     * Copies the file from the source path to the destination path. If a file already exists at the destination file path, the operation will fail and the file will not be copied.
     * @param sourceFilePath - Path of the file to copy.
     * @param destinationFilePath - Path to copy the file to.
     * @see https://docs.scriptable.app/filemanager/#-copy
     */
  copy(sourceFilePath: string, destinationFilePath: string): void {
    return this.invokeMethod<void>('copy', Array.from(arguments));
  }
  /**
     * _Checks if the file exists._
     *
     * Checks if the file exists at the specified file path. Checking this before moving or copying to a destination can be a good idea as those operations will replace any existing file
     * at the destination file path.
     * @param filePath - File path to examine.
     * @see https://docs.scriptable.app/filemanager/#-fileexists
     */
  fileExists(filePath: string): boolean {
    return this.invokeMethod<boolean>('fileExists', Array.from(arguments));
  }
  /**
     * _Checks if a path points to a directory._
     * @param path - Path to examine.
     * @see https://docs.scriptable.app/filemanager/#-isdirectory
     */
  isDirectory(path: string): boolean {
    return this.invokeMethod<boolean>('isDirectory', Array.from(arguments));
  }
  /**
     * _Creates a directory at the specified path._
     *
     * You can optionally create all intermediate directories.
     * @param path - Path of directory to create.
     * @param intermediateDirectories - Whether to create all intermediate directories. Defaults to false.
     * @see https://docs.scriptable.app/filemanager/#-createdirectory
     */
  createDirectory(path: string, intermediateDirectories?: boolean): void {
    return this.invokeMethod<void>('createDirectory', Array.from(arguments));
  }
  /**
     * _Path of temporary directory._
     *
     * Used to retrieve the path of a temporary directory on disk. Data persisted in a temporary directory will generally live shorter than data persisted in the cache directory.
     *
     * The operating system may at any time delete files stored in this directory and therefore you should not rely on it for long time storage. If you need long time storage, see
     * documentsDirectory() or libraryDirectory(). This directory is not shared between the app, the action extension and Siri.
     * @see https://docs.scriptable.app/filemanager/#-temporarydirectory
     */
  temporaryDirectory(): string {
    return this.invokeMethod<string>('temporaryDirectory', Array.from(arguments));
  }
  /**
     * _Path of cache directory._
     *
     * Used to retrieve the path of a cache directory on disk. The operating system may at any time delete files stored in this directory and therefore you should not rely on it for long
     * time storage.
     *
     * Data persisted in the cache directory will generally live longer than data persisted in a temporary directory.
     *
     * If you need long time storage, see documentsDirectory() or libraryDirectory(). This directory is not shared between the app, the action extension and Siri.
     * @see https://docs.scriptable.app/filemanager/#-cachedirectory
     */
  cacheDirectory(): string {
    return this.invokeMethod<string>('cacheDirectory', Array.from(arguments));
  }
  /**
     * _Path of documents directory._
     *
     * Used to retrieve the path to the documents directory. Your scripts are stored in this directory. If you have iCloud enabled, your scripts will be stored in the documents directory
     * in iCloud otherwise they will be stored in the local documents directory. The directory can be used for long time storage. Documents stored in this directory can be accessed using
     * the Files app. Files stored in the local documents directory will not appear in the Files app.
     * @see https://docs.scriptable.app/filemanager/#-documentsdirectory
     */
  documentsDirectory(): string {
    return this.invokeMethod<string>('documentsDirectory', Array.from(arguments));
  }
  /**
     * _Path of library directory._
     *
     * Used to retrieve the path to the library directory. The directory can be used for long time storage. Documents stored in this directory cannot be accessed using the Files app.
     * @see https://docs.scriptable.app/filemanager/#-librarydirectory
     */
  libraryDirectory(): string {
    return this.invokeMethod<string>('libraryDirectory', Array.from(arguments));
  }
  /**
     * _Joins two path components._
     *
     * Joins two paths to created one path. For example to join the path to a directory with the name of a file. This is the suggested approach for creating new file paths passed to the
     * read and write functions of a FileManager.
     * @param lhsPath - Left-hand side part of the new path.
     * @param rhsPath - Right-hand side part of the new path.
     * @see https://docs.scriptable.app/filemanager/#-joinpath
     */
  joinPath(lhsPath: string, rhsPath: string): string {
    return this.invokeMethod<string>('joinPath', Array.from(arguments));
  }
  /**
     * _Reads all tags from a file._
     *
     * The tags are read from the file at the specified path. Tags can either be read, added and removed using the Files app by using the APIs provided by a FileManager.
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of file to read tags from.
     * @see https://docs.scriptable.app/filemanager/#-alltags
     */
  allTags(filePath: string): string[] {
    return this.invokeMethod<string[]>('allTags', Array.from(arguments));
  }
  /**
     * _Adds a tag to a file._
     *
     * A tag can only be added to a file once. It is not possible to specify a color for the tag. You can create the tags using the Files app to specify the color and then add them to
     * files afterwards using the FileManager API.
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of file to add the tag to.
     * @param tag - Tag to add. This can be an existing tag or a new tag.
     * @see https://docs.scriptable.app/filemanager/#-addtag
     */
  addTag(filePath: string, tag: string): void {
    return this.invokeMethod<void>('addTag', Array.from(arguments));
  }
  /**
     * _Removes a tag from a file._
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of file to remove the tag from.
     * @param tag - Tag to remove.
     * @see https://docs.scriptable.app/filemanager/#-removetag
     */
  removeTag(filePath: string, tag: string): void {
    return this.invokeMethod<void>('removeTag', Array.from(arguments));
  }
  /**
     * _Reads an extended attribute from a file._
     *
     * Extended attributes are metadata that can be stored on a file. Note that extended attributes are not synced with iCloud.
     *
     * The function will return `null` if the attribute does not exist.
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of file to read extended attribute from.
     * @param name - Name of the extended attribute to read.
     * @see https://docs.scriptable.app/filemanager/#-readextendedattribute
     */
  readExtendedAttribute(filePath: string, name: string): string {
    return this.invokeMethod<string>('readExtendedAttribute', Array.from(arguments));
  }
  /**
     * _Writes an extended attribute to a file._
     *
     * Extended attributes are metadata that can be stored on a file. Note that extended attributes are not synced with iCloud.
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of file to add an extended attribute to.
     * @param value - Value of the extended attribute.
     * @param name - Name of the extended attribute. This is used to retrieve the value at a later point.
     * @see https://docs.scriptable.app/filemanager/#-writeextendedattribute
     */
  writeExtendedAttribute(filePath: string, value: string, name: string): void {
    return this.invokeMethod<void>('writeExtendedAttribute', Array.from(arguments));
  }
  /**
     * _Removes an extended attribute from a file._
     *
     * Extended attributes are metadata that can be stored on a file. Note that extended attributes are not synced with iCloud.
     *
     * The function will error if the file does not exist or if it exists in iCloud but has not been download. Use `fileExists(filePath)` to check if a file exists and
     * `downloadFileFromiCloud(filePath)` to download the file. Note that it is always safe to call `downloadFileFromiCloud(filePath)`, even if the file is stored locally on the device.
     * @param filePath - Path of file to add an extended attribute to.
     * @param name - Name of the extended attribute to remove.
     * @see https://docs.scriptable.app/filemanager/#-removeextendedattribute
     */
  removeExtendedAttribute(filePath: string, name: string): void {
    return this.invokeMethod<void>('removeExtendedAttribute', Array.from(arguments));
  }
  /**
     * _Reads all extended attributes on a file._
     *
     * Extended attributes are metadata that can be stored on a file. Note that extended attributes are not synced with iCloud.
     * @param filePath - Path of file to read extended attributes from.
     * @see https://docs.scriptable.app/filemanager/#-allextendedattributes
     */
  allExtendedAttributes(filePath: string): string[] {
    return this.invokeMethod<string[]>('allExtendedAttributes', Array.from(arguments));
  }
  /**
     * _Gets the UTI of the specified file._
     *
     * The Uniform Type Identifier is a string that identifies the type of file.
     * @param filePath - Path of file to get UTI of.
     * @see https://docs.scriptable.app/filemanager/#-getuti
     */
  getUTI(filePath: string): string {
    return this.invokeMethod<string>('getUTI', Array.from(arguments));
  }
  /**
     * _Lists content of directory._
     *
     * Lists all the contents in the specified directory. The returned array contains filenames of all files and directories in the specified directory.
     * @param directoryPath - Path to directory.
     * @see https://docs.scriptable.app/filemanager/#-listcontents
     */
  listContents(directoryPath: string): string[] {
    return this.invokeMethod<string[]>('listContents', Array.from(arguments));
  }
  /**
     * _Get name of a file._
     *
     * Takes a file path and returns the name of the file. Also supports getting the name of a directory. The returned file name optionally includes the extension of the file.
     * @param filePath - path of file to get name of.
     * @param includeFileExtension - Whether or not the file extension should be included. Defaults to false.
     * @see https://docs.scriptable.app/filemanager/#-filename
     */
  fileName(filePath: string, includeFileExtension?: boolean): string {
    return this.invokeMethod<string>('fileName', Array.from(arguments));
  }
  /**
     * _Get extension of a file._
     *
     * Takes a file path and returns the extension of the file, e.g. ".jpg" or ".js". Returns en empty string for directories.
     * @param filePath - Path of file to get extension from.
     * @see https://docs.scriptable.app/filemanager/#-fileextension
     */
  fileExtension(filePath: string): string {
    return this.invokeMethod<string>('fileExtension', Array.from(arguments));
  }
  /**
     * _Get path to a bookmarked file or folder._
     *
     * Gets the path to a bookmarked file or filder. Use file bookmarks to access files and folders outside Scriptables documents directory.
     *
     * You can edit your file bookmarks from Scriptables settings.
     *
     * The function will throw an error if the bookmark doesn't exist.
     *
     * Please beware that bookmarks created from Scriptables settings only can be used when running a script in the app and not from the Share Sheet, Siri and Shortcuts. If you wish to
     * use a bookmark from Siri or the Shortcuts app, the bookmark must be created using Scriptables "Create File Bookmark" shortcut action using the Shortcuts app.
     * @param name - Name of bookmark to create path for.
     * @see https://docs.scriptable.app/filemanager/#-bookmarkedpath
     */
  bookmarkedPath(name: string): string {
    return this.invokeMethod<string>('bookmarkedPath', Array.from(arguments));
  }
  /**
     * _Check if a bookmark exists._
     *
     * Checks if a file bookmark exists with the specified name.
     *
     * You can edit your file bookmarks from Scriptables settings.
     *
     * Please beware that bookmarks created from Scriptables settings only can be used when running a script in the app and not from the Share Sheet, Siri and Shortcuts. If you wish to
     * use a bookmark from Siri or the Shortcuts app, the bookmark must be created using Scriptables "Create File Bookmark" shortcut action using the Shortcuts app.
     * @param name - Name of bookmark.
     * @see https://docs.scriptable.app/filemanager/#-bookmarkexists
     */
  bookmarkExists(name: string): boolean {
    return this.invokeMethod<boolean>('bookmarkExists', Array.from(arguments));
  }
  /**
     * _Download file from iCloud if necessary._
     *
     * Downloads the file from iCloud if it has not already been downloaded. If you pass in a path to a file that is not stored in iCloud, the returned promise will be resolved
     * immediately making it safe to pass in any file path.
     * @param filePath - Path of file to download from iCloud.
     * @see https://docs.scriptable.app/filemanager/#-downloadfilefromicloud
     */
  downloadFileFromiCloud(filePath: string): Promise<void> {
    return this.invokeMethod<Promise<void>>('downloadFileFromiCloud', Array.from(arguments));
  }
  /**
     * _Checks if a file is stored in iCloud._
     *
     * Checks if a file is stored in iCloud or locally on the device. The function returns false if the file does not exist. Check if a file exists using `fileExists(filePath)`
     * @param filePath - Path of file.
     * @see https://docs.scriptable.app/filemanager/#-isfilestoredinicloud
     */
  isFileStoredIniCloud(filePath: string): boolean {
    return this.invokeMethod<boolean>('isFileStoredIniCloud', Array.from(arguments));
  }
  /**
     * _Checks if a file has been downloaded._
     *
     * If a file is stored in iCloud and it has not been downloaded, this function returns false. In that case, the file can be downloaded using `downloadFileFromiCloud(filePath`. If the
     * file is not stored in iCloud but rather locally on the device, this function returns true.
     *
     * The function returns false if the file does not exist. Check if a file exists using `fileExists(filePath)`
     * @param filePath - Path of file.
     * @see https://docs.scriptable.app/filemanager/#-isfiledownloaded
     */
  isFileDownloaded(filePath: string): boolean {
    return this.invokeMethod<boolean>('isFileDownloaded', Array.from(arguments));
  }
  /**
     * _Reads the creation date of a file._
     *
     * The returned value will be null if the creation date cannot be read.
     * @param filePath - Path of file.
     * @see https://docs.scriptable.app/filemanager/#-creationdate
     */
  creationDate(filePath: string): Date {
    return this.invokeMethod<Date>('creationDate', Array.from(arguments));
  }
  /**
     * _Reads the modification date of a file._
     *
     * The returned value will be null if the modification date cannot be read.
     * @param filePath - Path of file.
     * @see https://docs.scriptable.app/filemanager/#-modificationdate
     */
  modificationDate(filePath: string): Date {
    return this.invokeMethod<Date>('modificationDate', Array.from(arguments));
  }
  /**
     * _Size of the file in kilobytes._
     *
     * The returned value will be null if the file size cannot be read.
     * @param filePath - Path of file.
     * @see https://docs.scriptable.app/filemanager/#-filesize
     */
  fileSize(filePath: string): number {
    return this.invokeMethod<number>('fileSize', Array.from(arguments));
  }
  /**
     * _Reads all file bookmarks created in settings._
     *
     * File bookmarks are used to bookmark a file or a folder to read from it or write to it later. File bookmarks are created from Scriptables settings.
     *
     * This function returns all file bookmarks as an array of objects that take the following form.
     *
     *     {
     *       "name": "My Bookmark",
     *       "source": "host"
     *     }
     *
     * The source can either be `host` for file bookmarks that can be used in the app or `siri_shortcuts` for file bookmarks that can be used in Siri and Shortcuts.
     * @see https://docs.scriptable.app/filemanager/#-allfilebookmarks
     */
  allFileBookmarks(): FileManager.AllFileBookmarks[] {
    return this.invokeMethod<FileManager.AllFileBookmarks[]>('allFileBookmarks', Array.from(arguments));
  }
}

export interface FontState {

}

export class AbsFont<S extends FontState = FontState> extends ScriptableClass<S> implements Omit<Font, 'font'> {
  static readonly identifier = 'Font';

  get asFont(): Font {
    return this.asOriginal<Font>();
  }

  /**
     * _Preferred font for large titles._
     * @see https://docs.scriptable.app/font/#largetitle
     */
  static largeTitle(): Font {
    return this.invokeStaticMethod<Font>('largeTitle', Array.from(arguments));
  }
  /**
     * _Preferred font for first level hierarchical headings._
     * @see https://docs.scriptable.app/font/#title1
     */
  static title1(): Font {
    return this.invokeStaticMethod<Font>('title1', Array.from(arguments));
  }
  /**
     * _Preferred font for second level hierarchical headings._
     * @see https://docs.scriptable.app/font/#title2
     */
  static title2(): Font {
    return this.invokeStaticMethod<Font>('title2', Array.from(arguments));
  }
  /**
     * _Preferred font for third level hierarchical headings._
     * @see https://docs.scriptable.app/font/#title3
     */
  static title3(): Font {
    return this.invokeStaticMethod<Font>('title3', Array.from(arguments));
  }
  /**
     * _Preferred font for headings._
     * @see https://docs.scriptable.app/font/#headline
     */
  static headline(): Font {
    return this.invokeStaticMethod<Font>('headline', Array.from(arguments));
  }
  /**
     * _Preferred font for subheadings._
     * @see https://docs.scriptable.app/font/#subheadline
     */
  static subheadline(): Font {
    return this.invokeStaticMethod<Font>('subheadline', Array.from(arguments));
  }
  /**
     * _Preferred font for body texts._
     * @see https://docs.scriptable.app/font/#body
     */
  static body(): Font {
    return this.invokeStaticMethod<Font>('body', Array.from(arguments));
  }
  /**
     * _Preferred font for callouts._
     * @see https://docs.scriptable.app/font/#callout
     */
  static callout(): Font {
    return this.invokeStaticMethod<Font>('callout', Array.from(arguments));
  }
  /**
     * _Preferred font for footnotes._
     * @see https://docs.scriptable.app/font/#footnote
     */
  static footnote(): Font {
    return this.invokeStaticMethod<Font>('footnote', Array.from(arguments));
  }
  /**
     * _Preferred font for standard captions._
     * @see https://docs.scriptable.app/font/#caption1
     */
  static caption1(): Font {
    return this.invokeStaticMethod<Font>('caption1', Array.from(arguments));
  }
  /**
     * _Preferred font for alternate captions._
     * @see https://docs.scriptable.app/font/#caption2
     */
  static caption2(): Font {
    return this.invokeStaticMethod<Font>('caption2', Array.from(arguments));
  }
  /**
     * _Creates a system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#systemfont
     */
  static systemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('systemFont', Array.from(arguments));
  }
  /**
     * _Creates an ultra light system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#ultralightsystemfont
     */
  static ultraLightSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('ultraLightSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a thin system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#thinsystemfont
     */
  static thinSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('thinSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a light system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#lightsystemfont
     */
  static lightSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('lightSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a regular system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#regularsystemfont
     */
  static regularSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('regularSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a medium system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#mediumsystemfont
     */
  static mediumSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('mediumSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a semibold system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#semiboldsystemfont
     */
  static semiboldSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('semiboldSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a bold system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#boldsystemfont
     */
  static boldSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('boldSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a heavy system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#heavysystemfont
     */
  static heavySystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('heavySystemFont', Array.from(arguments));
  }
  /**
     * _Creates a font with the system appearance with the black weight._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#blacksystemfont
     */
  static blackSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('blackSystemFont', Array.from(arguments));
  }
  /**
     * _Creates an italic system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#italicsystemfont
     */
  static italicSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('italicSystemFont', Array.from(arguments));
  }
  /**
     * _Creates an ultra light monospaced system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#ultralightmonospacedsystemfont
     */
  static ultraLightMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('ultraLightMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a thin monospaced system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#thinmonospacedsystemfont
     */
  static thinMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('thinMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a light monospaced system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#lightmonospacedsystemfont
     */
  static lightMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('lightMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a regular monospaced system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#regularmonospacedsystemfont
     */
  static regularMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('regularMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a medium monospaced system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#mediummonospacedsystemfont
     */
  static mediumMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('mediumMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a semibold monospaced system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#semiboldmonospacedsystemfont
     */
  static semiboldMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('semiboldMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a bold monospaced system font.._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#boldmonospacedsystemfont
     */
  static boldMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('boldMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a heavy monospaced system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#heavymonospacedsystemfont
     */
  static heavyMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('heavyMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a monospaced system font with the black weight._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#blackmonospacedsystemfont
     */
  static blackMonospacedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('blackMonospacedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates an ultra light and rounded system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#ultralightroundedsystemfont
     */
  static ultraLightRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('ultraLightRoundedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a thin and rounded system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#thinroundedsystemfont
     */
  static thinRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('thinRoundedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a light and rounded system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#lightroundedsystemfont
     */
  static lightRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('lightRoundedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a regular and rounded system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#regularroundedsystemfont
     */
  static regularRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('regularRoundedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a medium and rounded system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#mediumroundedsystemfont
     */
  static mediumRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('mediumRoundedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a semibold and rounded system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#semiboldroundedsystemfont
     */
  static semiboldRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('semiboldRoundedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a bold and rounded system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#boldroundedsystemfont
     */
  static boldRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('boldRoundedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a heavy and rounded system font._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#heavyroundedsystemfont
     */
  static heavyRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('heavyRoundedSystemFont', Array.from(arguments));
  }
  /**
     * _Creates a rounded system font with the black weight._
     * @param size - Size of the text.
     * @see https://docs.scriptable.app/font/#blackroundedsystemfont
     */
  static blackRoundedSystemFont(size: number): Font {
    return this.invokeStaticMethod<Font>('blackRoundedSystemFont', Array.from(arguments));
  }


}

export interface ImageState {
  size: Size;
}

export class AbsImage<S extends ImageState = ImageState> extends ScriptableClass<S> implements Image {
  static readonly identifier = 'Image';

  get asImage(): Image {
    return this.asOriginal<Image>();
  }

  /**
     * _Creates an image from file._
     *
     * Loads an image from the specified file path. If the image could not be read, the function will return null.
     * @param filePath - File path to read image from.
     * @see https://docs.scriptable.app/image/#fromfile
     */
  static fromFile(filePath: string): Image {
    return this.invokeStaticMethod<Image>('fromFile', Array.from(arguments));
  }
  /**
     * _Creates an image from raw data._
     *
     * Loads an image from the raw data. If the image could not be read, the function will return null.
     * @param data - Data to read image from.
     * @see https://docs.scriptable.app/image/#fromdata
     */
  static fromData(data: Data): Image {
    return this.invokeStaticMethod<Image>('fromData', Array.from(arguments));
  }

  get size() {
    if (!('size' in this.state)) {
      throw new Error('Required property "size" of type "Size" is not set');
    }
    return this.state.size as Size;
  }

  set size(value: Size) {
    this.setState({ size: value });
  }

}

export interface LinearGradientState {
  colors: Color[];
  locations: number[];
  startPoint: Point;
  endPoint: Point;
}

export class AbsLinearGradient<S extends LinearGradientState = LinearGradientState> extends ScriptableClass<S> implements LinearGradient {
  static readonly identifier = 'LinearGradient';

  get asLinearGradient(): LinearGradient {
    return this.asOriginal<LinearGradient>();
  }


  get colors() {
    if (!('colors' in this.state)) {
      return [];
    }
    return this.state.colors as Color[];
  }

  set colors(value: Color[]) {
    this.setState({ colors: value });
  }
  get locations() {
    if (!('locations' in this.state)) {
      return [];
    }
    return this.state.locations as number[];
  }

  set locations(value: number[]) {
    this.setState({ locations: value });
  }
  get startPoint() {
    if (!('startPoint' in this.state)) {
      throw new Error('Required property "startPoint" of type "Point" is not set');
    }
    return this.state.startPoint as Point;
  }

  set startPoint(value: Point) {
    this.setState({ startPoint: value });
  }
  get endPoint() {
    if (!('endPoint' in this.state)) {
      throw new Error('Required property "endPoint" of type "Point" is not set');
    }
    return this.state.endPoint as Point;
  }

  set endPoint(value: Point) {
    this.setState({ endPoint: value });
  }

}

export interface ListWidgetState {
  backgroundColor: Color;
  backgroundImage: Image;
  backgroundGradient: LinearGradient;
  addAccessoryWidgetBackground: boolean;
  spacing: number;
  url: string;
  refreshAfterDate: Date;
}

export class AbsListWidget<S extends ListWidgetState = ListWidgetState> extends ScriptableClass<S> implements ListWidget {
  static readonly identifier = 'ListWidget';

  get asListWidget(): ListWidget {
    return this.asOriginal<ListWidget>();
  }


  get backgroundColor() {
    if (!('backgroundColor' in this.state)) {
      throw new Error('Required property "backgroundColor" of type "Color" is not set');
    }
    return this.state.backgroundColor as Color;
  }

  set backgroundColor(value: Color) {
    this.setState({ backgroundColor: value });
  }
  get backgroundImage() {
    if (!('backgroundImage' in this.state)) {
      throw new Error('Required property "backgroundImage" of type "Image" is not set');
    }
    return this.state.backgroundImage as Image;
  }

  set backgroundImage(value: Image) {
    this.setState({ backgroundImage: value });
  }
  get backgroundGradient() {
    if (!('backgroundGradient' in this.state)) {
      throw new Error('Required property "backgroundGradient" of type "LinearGradient" is not set');
    }
    return this.state.backgroundGradient as LinearGradient;
  }

  set backgroundGradient(value: LinearGradient) {
    this.setState({ backgroundGradient: value });
  }
  get addAccessoryWidgetBackground() {
    if (!('addAccessoryWidgetBackground' in this.state)) {
      return false;
    }
    return this.state.addAccessoryWidgetBackground as boolean;
  }

  set addAccessoryWidgetBackground(value: boolean) {
    this.setState({ addAccessoryWidgetBackground: value });
  }
  get spacing() {
    if (!('spacing' in this.state)) {
      return 0;
    }
    return this.state.spacing as number;
  }

  set spacing(value: number) {
    this.setState({ spacing: value });
  }
  get url() {
    if (!('url' in this.state)) {
      return "";
    }
    return this.state.url as string;
  }

  set url(value: string) {
    this.setState({ url: value });
  }
  get refreshAfterDate() {
    if (!('refreshAfterDate' in this.state)) {
      throw new Error('Required property "refreshAfterDate" of type "Date" is not set');
    }
    return this.state.refreshAfterDate as Date;
  }

  set refreshAfterDate(value: Date) {
    this.setState({ refreshAfterDate: value });
  }

  /**
     * _Add text to the widget._
     *
     * Adds a text element to the widget. Use the properties on the returned element to style the text.
     * @see https://docs.scriptable.app/listwidget/#-addtext
     */
  addText(text: string): WidgetText {
    return this.invokeMethod<WidgetText>('addText', Array.from(arguments));
  }
  /**
     * _Add date to the widget._
     *
     * Adds a date element to the widget. Use the properties on the returned element to style the date.
     * @see https://docs.scriptable.app/listwidget/#-adddate
     */
  addDate(date: Date): WidgetDate {
    return this.invokeMethod<WidgetDate>('addDate', Array.from(arguments));
  }
  /**
     * _Add image to the widget._
     *
     * Adds an image element to the widget. Use the properties on the returned element to style the image.
     * @see https://docs.scriptable.app/listwidget/#-addimage
     */
  addImage(image: Image): WidgetImage {
    return this.invokeMethod<WidgetImage>('addImage', Array.from(arguments));
  }
  /**
     * _Add spacer._
     *
     * Adds a spacer to the widget. This can be used to offset the content vertically in the widget.
     * @param length - Length of the spacer. Pass null to create a spacer with a flexible length.
     * @see https://docs.scriptable.app/listwidget/#-addspacer
     */
  addSpacer(length?: number): WidgetSpacer {
    return this.invokeMethod<WidgetSpacer>('addSpacer', Array.from(arguments));
  }
  /**
     * _Add stack._
     *
     * Adds a stack to the widget. Stacks layout elements horizontally by default.
     * @see https://docs.scriptable.app/listwidget/#-addstack
     */
  addStack(): WidgetStack {
    return this.invokeMethod<WidgetStack>('addStack', Array.from(arguments));
  }
  /**
     * _Set padding._
     *
     * Sets the padding on each side of the widget.
     * @param top - Padding on the top edge.
     * @param leading - Padding on the leading edge.
     * @param bottom - Padding on the bottom edge.
     * @param trailing - Padding on the trailing edge.
     * @see https://docs.scriptable.app/listwidget/#-setpadding
     */
  setPadding(top: number, leading: number, bottom: number, trailing: number): void {
    return this.invokeMethod<void>('setPadding', Array.from(arguments));
  }
  /**
     * _Use the default padding._
     *
     * Configure the widget to use the default padding. Any padding previously defined with `setPadding()` will be discarded.
     * @see https://docs.scriptable.app/listwidget/#-usedefaultpadding
     */
  useDefaultPadding(): void {
    return this.invokeMethod<void>('useDefaultPadding', Array.from(arguments));
  }
  /**
     * _Presents a preview of the widget._
     *
     * The widget is presented in its small size.
     *
     * Widgets on the Home Screen are updated periodically so while working on your widget you may want to preview it in the app.
     * @see https://docs.scriptable.app/listwidget/#-presentsmall
     */
  presentSmall(): Promise<void> {
    return this.invokeMethod<Promise<void>>('presentSmall', Array.from(arguments));
  }
  /**
     * _Presents a preview of the widget._
     *
     * The widget is presented in its medium size.
     *
     * Widgets on the Home Screen are updated periodically so while working on your widget you may want to preview it in the app.
     * @see https://docs.scriptable.app/listwidget/#-presentmedium
     */
  presentMedium(): Promise<void> {
    return this.invokeMethod<Promise<void>>('presentMedium', Array.from(arguments));
  }
  /**
     * _Presents a preview of the widget._
     *
     * The widget is presented in its large size.
     *
     * Widgets on the Home Screen are updated periodically so while working on your widget you may want to preview it in the app.
     * @see https://docs.scriptable.app/listwidget/#-presentlarge
     */
  presentLarge(): Promise<void> {
    return this.invokeMethod<Promise<void>>('presentLarge', Array.from(arguments));
  }
  /**
     * _Presents a preview of the widget._
     *
     * The widget is presented in its extra large size.
     *
     * Widgets on the Home Screen are updated periodically so while working on your widget you may want to preview it in the app.
     *
     * The extra large widget is only available on iPads running iOS 15 and newer.
     * @see https://docs.scriptable.app/listwidget/#-presentextralarge
     */
  presentExtraLarge(): Promise<void> {
    return this.invokeMethod<Promise<void>>('presentExtraLarge', Array.from(arguments));
  }
  /**
     * _Presents a preview of the widget._
     *
     * The widget is presented in its accessory inline size that is suitable to be displayed above the clock on the Lock Screen.
     *
     * The preview is an estimated representation of the widget and is not accurate. Widgets placed on the Lock Screen change appearance based on the wallpaper and tint color of the Lock
     * Screen, and as such, they cannot be previewed accurately in Scriptable.
     *
     * Widgets on the Lock Screen are updated periodically so while working on your widget you may want to preview it in the app.
     *
     * The accessory inline widget is available on iPhones running iOS 16 and newer and can display a single image and a single text. Any additional elements will be filtered away during
     * presentation.
     * @see https://docs.scriptable.app/listwidget/#-presentaccessoryinline
     */
  presentAccessoryInline(): Promise<void> {
    return this.invokeMethod<Promise<void>>('presentAccessoryInline', Array.from(arguments));
  }
  /**
     * _Presents a preview of the widget._
     *
     * The widget is presented in its circular accessory size that is suitable to be displayed below the clock on the Lock Screen.
     *
     * The preview is an estimated representation of the widget and is not accurate. Widgets placed on the Lock Screen change appearance based on the wallpaper and tint color of the Lock
     * Screen, and as such, they cannot be previewed accurately in Scriptable.
     *
     * Widgets on the Lock Screen are updated periodically so while working on your widget you may want to preview it in the app.
     *
     * The circular accessory widget is available on iPhones running iOS 16 and newer.
     * @see https://docs.scriptable.app/listwidget/#-presentaccessorycircular
     */
  presentAccessoryCircular(): Promise<void> {
    return this.invokeMethod<Promise<void>>('presentAccessoryCircular', Array.from(arguments));
  }
  /**
     * _Presents a preview of the widget._
     *
     * The widget is presented in its rectangular accessory size that is suitable to be displayed below the clock on the Lock Screen.
     *
     * The preview is an estimated representation of the widget and is not accurate. Widgets placed on the Lock Screen change appearance based on the wallpaper and tint color of the Lock
     * Screen, and as such, they cannot be previewed accurately in Scriptable.
     *
     * Widgets on the Lock Screen are updated periodically so while working on your widget you may want to preview it in the app.
     *
     * The rectangular accessory widget is available on iPhones running iOS 16 and newer.
     * @see https://docs.scriptable.app/listwidget/#-presentaccessoryrectangular
     */
  presentAccessoryRectangular(): Promise<void> {
    return this.invokeMethod<Promise<void>>('presentAccessoryRectangular', Array.from(arguments));
  }
}

export interface MailState {
  toRecipients: string[];
  ccRecipients: string[];
  bccRecipients: string[];
  subject: string;
  body: string;
  isBodyHTML: boolean;
  preferredSendingEmailAddress: string;
}

export class AbsMail<S extends MailState = MailState> extends ScriptableClass<S> implements Mail {
  static readonly identifier = 'Mail';

  get asMail(): Mail {
    return this.asOriginal<Mail>();
  }


  get toRecipients() {
    if (!('toRecipients' in this.state)) {
      return [];
    }
    return this.state.toRecipients as string[];
  }

  set toRecipients(value: string[]) {
    this.setState({ toRecipients: value });
  }
  get ccRecipients() {
    if (!('ccRecipients' in this.state)) {
      return [];
    }
    return this.state.ccRecipients as string[];
  }

  set ccRecipients(value: string[]) {
    this.setState({ ccRecipients: value });
  }
  get bccRecipients() {
    if (!('bccRecipients' in this.state)) {
      return [];
    }
    return this.state.bccRecipients as string[];
  }

  set bccRecipients(value: string[]) {
    this.setState({ bccRecipients: value });
  }
  get subject() {
    if (!('subject' in this.state)) {
      return "";
    }
    return this.state.subject as string;
  }

  set subject(value: string) {
    this.setState({ subject: value });
  }
  get body() {
    if (!('body' in this.state)) {
      return "";
    }
    return this.state.body as string;
  }

  set body(value: string) {
    this.setState({ body: value });
  }
  get isBodyHTML() {
    if (!('isBodyHTML' in this.state)) {
      return false;
    }
    return this.state.isBodyHTML as boolean;
  }

  set isBodyHTML(value: boolean) {
    this.setState({ isBodyHTML: value });
  }
  get preferredSendingEmailAddress() {
    if (!('preferredSendingEmailAddress' in this.state)) {
      return "";
    }
    return this.state.preferredSendingEmailAddress as string;
  }

  set preferredSendingEmailAddress(value: string) {
    this.setState({ preferredSendingEmailAddress: value });
  }

  /**
     * _Send the mail._
     *
     * Presents a screen from which the mail can be sent. The mail will not be sent until you have confirmed it from the presented screen.
     * @see https://docs.scriptable.app/mail/#-send
     */
  send(): Promise<void> {
    return this.invokeMethod<Promise<void>>('send', Array.from(arguments));
  }
  /**
     * _Adds an image attachment to the mail._
     * @param image - Image to add to the mail.
     * @see https://docs.scriptable.app/mail/#-addimageattachment
     */
  addImageAttachment(image: Image): void {
    return this.invokeMethod<void>('addImageAttachment', Array.from(arguments));
  }
  /**
     * _Adds a file attachment to the mail._
     * @param filePath - Path of file to add to the mail.
     * @see https://docs.scriptable.app/mail/#-addfileattachment
     */
  addFileAttachment(filePath: string): void {
    return this.invokeMethod<void>('addFileAttachment', Array.from(arguments));
  }
  /**
     * _Adds a data attachment to the mail._
     *
     * When adding a data attachment to the mail, you are responsible for providing a valid MIME type and filename. It is advised to use `addImageAttachment` and `addFileAttachment`
     * whenever possible.
     * @param data - Data representation of file to add to the mail.
     * @param mimeType - MIME type of file represented by the data.
     * @param filename - Name of the file represented by the data.
     * @see https://docs.scriptable.app/mail/#-adddataattachment
     */
  addDataAttachment(data: Data, mimeType: string, filename: string): void {
    return this.invokeMethod<void>('addDataAttachment', Array.from(arguments));
  }
}

export interface MessageState {
  recipients: string[];
  body: string;
}

export class AbsMessage<S extends MessageState = MessageState> extends ScriptableClass<S> implements Message {
  static readonly identifier = 'Message';

  get asMessage(): Message {
    return this.asOriginal<Message>();
  }


  get recipients() {
    if (!('recipients' in this.state)) {
      return [];
    }
    return this.state.recipients as string[];
  }

  set recipients(value: string[]) {
    this.setState({ recipients: value });
  }
  get body() {
    if (!('body' in this.state)) {
      return "";
    }
    return this.state.body as string;
  }

  set body(value: string) {
    this.setState({ body: value });
  }

  /**
     * _Send the message._
     *
     * Presents a screen from which the message can be sent. The message will not be sent until you have confirmed it from the presented screen.
     * @see https://docs.scriptable.app/message/#-send
     */
  send(): Promise<void> {
    return this.invokeMethod<Promise<void>>('send', Array.from(arguments));
  }
  /**
     * _Adds an image attachment to the message._
     * @param image - Image to add to the message.
     * @see https://docs.scriptable.app/message/#-addimageattachment
     */
  addImageAttachment(image: Image): void {
    return this.invokeMethod<void>('addImageAttachment', Array.from(arguments));
  }
  /**
     * _Adds a file attachment to the message._
     * @param filePath - Path of file to add to the message.
     * @see https://docs.scriptable.app/message/#-addfileattachment
     */
  addFileAttachment(filePath: string): void {
    return this.invokeMethod<void>('addFileAttachment', Array.from(arguments));
  }
  /**
     * _Adds a data attachment to the message._
     *
     * When adding a data attachment to the message, you are responsible for providing a valid Uniform Type Identifier and filename. It is advised to use `addImageAttachment` and
     * `addFileAttachment` whenever possible.
     * @param data - Data representation of file to add to the message.
     * @param uti - UTI of file represented by the data.
     * @param filename - Name of the file represented by the data.
     * @see https://docs.scriptable.app/message/#-adddataattachment
     */
  addDataAttachment(data: Data, uti: string, filename: string): void {
    return this.invokeMethod<void>('addDataAttachment', Array.from(arguments));
  }
}

export interface NotificationState {
  identifier: string;
  title: string;
  subtitle: string;
  body: string;
  preferredContentHeight: number;
  badge: number;
  threadIdentifier: string;
  userInfo: { [key: string]: any; };
  sound: "default" | "accept" | "alert" | "complete" | "event" | "failure" | "piano_error" | "piano_success" | "popup";
  openURL: string;
  deliveryDate: Date;
  nextTriggerDate: Date;
  scriptName: string;
  actions: Notification.Actions;
}

export class AbsNotification<S extends NotificationState = NotificationState> extends ScriptableClass<S> implements Notification {
  static readonly identifier = 'Notification';

  get asNotification(): Notification {
    return this.asOriginal<Notification>();
  }

  /**
     * _All pending notifications._
     *
     * Fetches all notifications that have been scheduled from Scriptable and are waiting to be delivered.
     * @see https://docs.scriptable.app/notification/#allpending
     */
  static allPending(): Promise<Notification[]> {
    return this.invokeStaticMethod<Promise<Notification[]>>('allPending', Array.from(arguments));
  }
  /**
     * _Delivered notifications displayed in the Notification Center._
     *
     * Fetches all notifications that have been scheduled from Scriptable and that are still displayed in the Notification Center of iOS.
     * @see https://docs.scriptable.app/notification/#alldelivered
     */
  static allDelivered(): Promise<Notification[]> {
    return this.invokeStaticMethod<Promise<Notification[]>>('allDelivered', Array.from(arguments));
  }
  /**
     * _Removes all pending notifications._
     *
     * Removes all notifications that have been scheduled from Scriptable and are waiting to be delivered.
     *
     * Use with caution. This removes all notifications scheduled across all of your scripts and the action cannot be undone.
     * @see https://docs.scriptable.app/notification/#removeallpending
     */
  static removeAllPending(): Promise<void> {
    return this.invokeStaticMethod<Promise<void>>('removeAllPending', Array.from(arguments));
  }
  /**
     * _Removes all delivered notifications._
     *
     * Removes all notifications that have been scheduled from Scriptable and that are still displayed in the Notification Center of iOS.
     * @see https://docs.scriptable.app/notification/#removealldelivered
     */
  static removeAllDelivered(): Promise<void> {
    return this.invokeStaticMethod<Promise<void>>('removeAllDelivered', Array.from(arguments));
  }
  /**
     * _Removes pending notifications._
     *
     * Removes notifications with the specified identifiers. The notifications are only removed if they are pending, that is they have been scheduled and are waiting to be delivered. To
     * remove delivered notifications, see `Notification.removeDelivered()`.
     * @see https://docs.scriptable.app/notification/#removepending
     */
  static removePending(identifiers: readonly string[]): Promise<void> {
    return this.invokeStaticMethod<Promise<void>>('removePending', Array.from(arguments));
  }
  /**
     * _Removes delivered notifications._
     *
     * Removes notifications with the specified identifiers. The notifications are only removed if they have been delivered. To remove pending notifications, see
     * `Notification.removePending()`.
     * @see https://docs.scriptable.app/notification/#removedelivered
     */
  static removeDelivered(identifiers: readonly string[]): Promise<void> {
    return this.invokeStaticMethod<Promise<void>>('removeDelivered', Array.from(arguments));
  }
  /**
     * _Resets the current notification._
     *
     * Effectively sets `args.notification` to null.
     *
     * When a notification scheduled from Scriptable has been tapped to open the app or while the app was open, `args.notification` will have a value until Scriptable is quit. You can
     * manually reset the value using `Notification.resetCurrent`.
     * @see https://docs.scriptable.app/notification/#resetcurrent
     */
  static resetCurrent(): void {
    return this.invokeStaticMethod<void>('resetCurrent', Array.from(arguments));
  }

  get identifier() {
    if (!('identifier' in this.state)) {
      return "";
    }
    return this.state.identifier as string;
  }

  set identifier(value: string) {
    this.setState({ identifier: value });
  }
  get title() {
    if (!('title' in this.state)) {
      return "";
    }
    return this.state.title as string;
  }

  set title(value: string) {
    this.setState({ title: value });
  }
  get subtitle() {
    if (!('subtitle' in this.state)) {
      return "";
    }
    return this.state.subtitle as string;
  }

  set subtitle(value: string) {
    this.setState({ subtitle: value });
  }
  get body() {
    if (!('body' in this.state)) {
      return "";
    }
    return this.state.body as string;
  }

  set body(value: string) {
    this.setState({ body: value });
  }
  get preferredContentHeight() {
    if (!('preferredContentHeight' in this.state)) {
      return 0;
    }
    return this.state.preferredContentHeight as number;
  }

  set preferredContentHeight(value: number) {
    this.setState({ preferredContentHeight: value });
  }
  get badge() {
    if (!('badge' in this.state)) {
      return 0;
    }
    return this.state.badge as number;
  }

  set badge(value: number) {
    this.setState({ badge: value });
  }
  get threadIdentifier() {
    if (!('threadIdentifier' in this.state)) {
      return "";
    }
    return this.state.threadIdentifier as string;
  }

  set threadIdentifier(value: string) {
    this.setState({ threadIdentifier: value });
  }
  get userInfo() {
    if (!('userInfo' in this.state)) {
      throw new Error('Required property "userInfo" of type "{ [key: string]: any; }" is not set');
    }
    return this.state.userInfo as { [key: string]: any; };
  }

  set userInfo(value: { [key: string]: any; }) {
    this.setState({ userInfo: value });
  }
  get sound() {
    if (!('sound' in this.state)) {
      throw new Error('Required property "sound" of type ""default" | "accept" | "alert" | "complete" | "event" | "failure" | "piano_error" | "piano_success" | "popup"" is not set');
    }
    return this.state.sound as "default" | "accept" | "alert" | "complete" | "event" | "failure" | "piano_error" | "piano_success" | "popup";
  }

  set sound(value: "default" | "accept" | "alert" | "complete" | "event" | "failure" | "piano_error" | "piano_success" | "popup") {
    this.setState({ sound: value });
  }
  get openURL() {
    if (!('openURL' in this.state)) {
      return "";
    }
    return this.state.openURL as string;
  }

  set openURL(value: string) {
    this.setState({ openURL: value });
  }
  get deliveryDate() {
    if (!('deliveryDate' in this.state)) {
      throw new Error('Required property "deliveryDate" of type "Date" is not set');
    }
    return this.state.deliveryDate as Date;
  }

  set deliveryDate(value: Date) {
    this.setState({ deliveryDate: value });
  }
  get nextTriggerDate() {
    if (!('nextTriggerDate' in this.state)) {
      throw new Error('Required property "nextTriggerDate" of type "Date" is not set');
    }
    return this.state.nextTriggerDate as Date;
  }

  set nextTriggerDate(value: Date) {
    this.setState({ nextTriggerDate: value });
  }
  get scriptName() {
    if (!('scriptName' in this.state)) {
      return "";
    }
    return this.state.scriptName as string;
  }

  set scriptName(value: string) {
    this.setState({ scriptName: value });
  }
  get actions() {
    if (!('actions' in this.state)) {
      throw new Error('Required property "actions" of type "Notification.Actions" is not set');
    }
    return this.state.actions as Notification.Actions;
  }

  set actions(value: Notification.Actions) {
    this.setState({ actions: value });
  }

  /**
     * _Schedules the notification._
     *
     * When a new notification is constructed, it must be scheduled, otherwise it will not be delivered. If an existing notification is modified, it must also be scheduled again for the
     * changes to take effect.
     * @see https://docs.scriptable.app/notification/#-schedule
     */
  schedule(): Promise<void> {
    return this.invokeMethod<Promise<void>>('schedule', Array.from(arguments));
  }
  /**
     * _Removes the notification._
     *
     * Removes all future triggers of the notification.
     * @see https://docs.scriptable.app/notification/#-remove
     */
  remove(): Promise<void> {
    return this.invokeMethod<Promise<void>>('remove', Array.from(arguments));
  }
  /**
     * _Sets the notification to be triggered on a date and time._
     * @param date - Date and time to trigger the notification on.
     * @see https://docs.scriptable.app/notification/#-settriggerdate
     */
  setTriggerDate(date: Date): void {
    return this.invokeMethod<void>('setTriggerDate', Array.from(arguments));
  }
  /**
     * _Sets the notification to be triggered daily._
     *
     * Sets the notification to be triggered on a specific time of the day. When the notification repeats, it will be sent at the same time on all future days. If the notification is not
     * repating it will be sent on the next occurrence of the specified time.
     * @param hour - Hour of the day to trigger the notification.
     * @param minute - Minute of the day to trigger the notification.
     * @param repeats - If true the notification will be sent daily on the specified time, otherwise it will only be sent once. Defaults to false.
     * @see https://docs.scriptable.app/notification/#-setdailytrigger
     */
  setDailyTrigger(hour: number, minute: number, repeats?: boolean): void {
    return this.invokeMethod<void>('setDailyTrigger', Array.from(arguments));
  }
  /**
     * _Sets the notification to be triggered weekly._
     *
     * Sets the notification to be triggered on a specific day of the week and a specific time of that day. When the notification repeats, it will be sent at the same time on all future
     * days. If the notification is not repating it will be sent on the next occurrence of the specified time.
     * @param weekday - Day of the week to trigger the notification.
     * @param hour - Hour of the day to trigger the notification.
     * @param minute - Minute of the day to trigger the notification.
     * @param repeats - If true the notification will be sent daily on the specified time, otherwise it will only be sent once. Defaults to false.
     * @see https://docs.scriptable.app/notification/#-setweeklytrigger
     */
  setWeeklyTrigger(weekday: number, hour: number, minute: number, repeats?: boolean): void {
    return this.invokeMethod<void>('setWeeklyTrigger', Array.from(arguments));
  }
  /**
     * _Adds an action button._
     *
     * Actions are shown as buttons in the notification. When screen space is unlimited, the system shows up to 10 actions. When the space is limited the system shows at most two actions.
     * @param title - Title of the action.
     * @param url - URL to open when choosing the action.
     * @param destructive - Optional. If set to true, the button is displayed with special highlighting to indicate that it performs a destructive task. Defaults to false.
     * @see https://docs.scriptable.app/notification/#-addaction
     */
  addAction(title: string, url: string, destructive?: boolean): void {
    return this.invokeMethod<void>('addAction', Array.from(arguments));
  }
}

export interface PathState {

}

export class AbsPath<S extends PathState = PathState> extends ScriptableClass<S> implements Path {
  static readonly identifier = 'Path';

  get asPath(): Path {
    return this.asOriginal<Path>();
  }



  /**
     * _Moves to a point._
     *
     * Moves to a point without drawing a line between the current point and the new point.
     * @param point - Point to move to.
     * @see https://docs.scriptable.app/path/#-move
     */
  move(point: Point): void {
    return this.invokeMethod<void>('move', Array.from(arguments));
  }
  /**
     * _Adds a line to a point._
     *
     * Add a line from the current point, e.g. set using the move method, and to the new point.
     * @param point - Point to add line to.
     * @see https://docs.scriptable.app/path/#-addline
     */
  addLine(point: Point): void {
    return this.invokeMethod<void>('addLine', Array.from(arguments));
  }
  /**
     * _Adds a rectangle._
     *
     * This is a convenience function for adding a rectangle to the path starting from the lower left corner and drawing the lines counter-clockwise until the rectangle is closed.
     * @param rect - Rectangle to add.
     * @see https://docs.scriptable.app/path/#-addrect
     */
  addRect(rect: Rect): void {
    return this.invokeMethod<void>('addRect', Array.from(arguments));
  }
  /**
     * _Adds an ellipse._
     *
     * Adds an ellipse incapsulated by the provided rectangle to the path.
     * @param rect - Rectangle incapsulating the ellipse.
     * @see https://docs.scriptable.app/path/#-addellipse
     */
  addEllipse(rect: Rect): void {
    return this.invokeMethod<void>('addEllipse', Array.from(arguments));
  }
  /**
     * _Adds a rounded rectangle._
     *
     * Adds a rounded rectangle to the path. The corner width specifies the horizontal size of the corner and the corner height specifies the the vertical size of the corner.
     * @param rect - Rectangle to add.
     * @param cornerWidth - Horizontal size of the rounded corner.
     * @param cornerHeight - Vertical size of the rounded corner.
     * @see https://docs.scriptable.app/path/#-addroundedrect
     */
  addRoundedRect(rect: Rect, cornerWidth: number, cornerHeight: number): void {
    return this.invokeMethod<void>('addRoundedRect', Array.from(arguments));
  }
  /**
     * _Adds a cubic curve to a point._
     *
     * Adds a cubic Bézier curve to the path with the specified end point and control points.
     * @param point - End point of the curve.
     * @param control1 - First control point of the curve.
     * @param control2 - Second control point of the curve.
     * @see https://docs.scriptable.app/path/#-addcurve
     */
  addCurve(point: Point, control1: Point, control2: Point): void {
    return this.invokeMethod<void>('addCurve', Array.from(arguments));
  }
  /**
     * _Adds a quadratic curve to a point._
     *
     * Adds a quadratic Bézier curve to the specified end point with the specified control point.
     * @param point - End point of the curve.
     * @param control - Control point of the curve.
     * @see https://docs.scriptable.app/path/#-addquadcurve
     */
  addQuadCurve(point: Point, control: Point): void {
    return this.invokeMethod<void>('addQuadCurve', Array.from(arguments));
  }
  /**
     * _Adds a set of lines._
     *
     * Adds straight lines between an array of points. Calling this method is equivalent to calling the move function with the first point in the array of points and then calling addLine
     * on the subsequent points in the array.
     * @param points - Points to add lines between.
     * @see https://docs.scriptable.app/path/#-addlines
     */
  addLines(points: readonly Point[]): void {
    return this.invokeMethod<void>('addLines', Array.from(arguments));
  }
  /**
     * _Adds a set of rectangles._
     *
     * Calling this is equivalent to repeatedly calling addRect.
     * @param rects - Rectangles to add.
     * @see https://docs.scriptable.app/path/#-addrects
     */
  addRects(rects: readonly Rect[]): void {
    return this.invokeMethod<void>('addRects', Array.from(arguments));
  }
  /**
     * _Closes a sub path._
     *
     * Adds a straight line from the current point to the start of the current subpath.
     * @see https://docs.scriptable.app/path/#-closesubpath
     */
  closeSubpath(): void {
    return this.invokeMethod<void>('closeSubpath', Array.from(arguments));
  }
}

export interface PointState {
  x: number;
  y: number;
}

export class AbsPoint<S extends PointState = PointState> extends ScriptableClass<S> implements Point {
  static readonly identifier = 'Point';

  get asPoint(): Point {
    return this.asOriginal<Point>();
  }


  get x() {
    if (!('x' in this.state)) {
      return 0;
    }
    return this.state.x as number;
  }

  set x(value: number) {
    this.setState({ x: value });
  }
  get y() {
    if (!('y' in this.state)) {
      return 0;
    }
    return this.state.y as number;
  }

  set y(value: number) {
    this.setState({ y: value });
  }

}

export interface RectState {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  x: number;
  y: number;
  width: number;
  height: number;
  origin: Point;
  size: Size;
}

export class AbsRect<S extends RectState = RectState> extends ScriptableClass<S> implements Rect {
  static readonly identifier = 'Rect';

  get asRect(): Rect {
    return this.asOriginal<Rect>();
  }


  get minX() {
    if (!('minX' in this.state)) {
      return 0;
    }
    return this.state.minX as number;
  }

  set minX(value: number) {
    this.setState({ minX: value });
  }
  get minY() {
    if (!('minY' in this.state)) {
      return 0;
    }
    return this.state.minY as number;
  }

  set minY(value: number) {
    this.setState({ minY: value });
  }
  get maxX() {
    if (!('maxX' in this.state)) {
      return 0;
    }
    return this.state.maxX as number;
  }

  set maxX(value: number) {
    this.setState({ maxX: value });
  }
  get maxY() {
    if (!('maxY' in this.state)) {
      return 0;
    }
    return this.state.maxY as number;
  }

  set maxY(value: number) {
    this.setState({ maxY: value });
  }
  get x() {
    if (!('x' in this.state)) {
      return 0;
    }
    return this.state.x as number;
  }

  set x(value: number) {
    this.setState({ x: value });
  }
  get y() {
    if (!('y' in this.state)) {
      return 0;
    }
    return this.state.y as number;
  }

  set y(value: number) {
    this.setState({ y: value });
  }
  get width() {
    if (!('width' in this.state)) {
      return 0;
    }
    return this.state.width as number;
  }

  set width(value: number) {
    this.setState({ width: value });
  }
  get height() {
    if (!('height' in this.state)) {
      return 0;
    }
    return this.state.height as number;
  }

  set height(value: number) {
    this.setState({ height: value });
  }
  get origin() {
    if (!('origin' in this.state)) {
      throw new Error('Required property "origin" of type "Point" is not set');
    }
    return this.state.origin as Point;
  }

  set origin(value: Point) {
    this.setState({ origin: value });
  }
  get size() {
    if (!('size' in this.state)) {
      throw new Error('Required property "size" of type "Size" is not set');
    }
    return this.state.size as Size;
  }

  set size(value: Size) {
    this.setState({ size: value });
  }

}

export interface RecurrenceRuleState {

}

export class AbsRecurrenceRule<S extends RecurrenceRuleState = RecurrenceRuleState> extends ScriptableClass<S> implements Omit<RecurrenceRule, 'recurrenceRule'> {
  static readonly identifier = 'RecurrenceRule';

  get asRecurrenceRule(): RecurrenceRule {
    return this.asOriginal<RecurrenceRule>();
  }

  /**
     * _Constructs a daily recurrence rule._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every day and a
     * value of 3 specifies that the rule should repeat every third day.
     * @param interval - Interval at which to repeat the rule.
     * @see https://docs.scriptable.app/recurrencerule/#daily
     */
  static daily(interval: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('daily', Array.from(arguments));
  }
  /**
     * _Constructs a daily recurrence rule with an end date._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every day and a
     * value of 3 specifies that the rule should repeat every third day.
     * @param interval - Interval at which to repeat the rule.
     * @param endDate - Date at which the recurrence rule should end.
     * @see https://docs.scriptable.app/recurrencerule/#dailyenddate
     */
  static dailyEndDate(interval: number, endDate: Date): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('dailyEndDate', Array.from(arguments));
  }
  /**
     * _Constructs a daily recurrence rule with an occurrence count._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every day and a
     * value of 3 specifies that the rule should repeat every third day.
     * @param interval - Interval at which to repeat the rule.
     * @param occurrenceCount - Number of times the rule should repeat before it ends.
     * @see https://docs.scriptable.app/recurrencerule/#dailyoccurrencecount
     */
  static dailyOccurrenceCount(interval: number, occurrenceCount: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('dailyOccurrenceCount', Array.from(arguments));
  }
  /**
     * _Constructs a weekly recurrence rule._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every week and a
     * value of 3 specifies that the rule should repeat every third week.
     * @param interval - Interval at which to repeat the rule.
     * @see https://docs.scriptable.app/recurrencerule/#weekly
     */
  static weekly(interval: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('weekly', Array.from(arguments));
  }
  /**
     * _Constructs a weekly recurrence rule with an end date._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every week and a
     * value of 3 specifies that the rule should repeat every third week.
     * @param interval - Interval at which to repeat the rule.
     * @param endDate - Date at which the recurrence rule should end.
     * @see https://docs.scriptable.app/recurrencerule/#weeklyenddate
     */
  static weeklyEndDate(interval: number, endDate: Date): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('weeklyEndDate', Array.from(arguments));
  }
  /**
     * _Constructs a weekly recurrence rule with an occurrence count._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every week and a
     * value of 3 specifies that the rule should repeat every third week.
     * @param interval - Interval at which to repeat the rule.
     * @param occurrenceCount - Number of times the rule should repeat before it ends.
     * @see https://docs.scriptable.app/recurrencerule/#weeklyoccurrencecount
     */
  static weeklyOccurrenceCount(interval: number, occurrenceCount: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('weeklyOccurrenceCount', Array.from(arguments));
  }
  /**
     * _Constructs a monthly recurrence rule._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every month and a
     * value of 3 specifies that the rule should repeat every third month.
     * @param interval - Interval at which to repeat the rule.
     * @see https://docs.scriptable.app/recurrencerule/#monthly
     */
  static monthly(interval: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('monthly', Array.from(arguments));
  }
  /**
     * _Constructs a monthly recurrence rule with an end date._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every month and a
     * value of 3 specifies that the rule should repeat every third month.
     * @param interval - Interval at which to repeat the rule.
     * @param endDate - Date at which the recurrence rule should end.
     * @see https://docs.scriptable.app/recurrencerule/#monthlyenddate
     */
  static monthlyEndDate(interval: number, endDate: Date): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('monthlyEndDate', Array.from(arguments));
  }
  /**
     * _Constructs a monthly recurrence rule with an occurrence count._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every month and a
     * value of 3 specifies that the rule should repeat every third month.
     * @param interval - Interval at which to repeat the rule.
     * @param occurrenceCount - Number of times the rule should repeat before it ends.
     * @see https://docs.scriptable.app/recurrencerule/#monthlyoccurrencecount
     */
  static monthlyOccurrenceCount(interval: number, occurrenceCount: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('monthlyOccurrenceCount', Array.from(arguments));
  }
  /**
     * _Constructs a yearly recurrence rule._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every year and a
     * value of 3 specifies that the rule should repeat every third year.
     * @param interval - Interval at which to repeat the rule.
     * @see https://docs.scriptable.app/recurrencerule/#yearly
     */
  static yearly(interval: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('yearly', Array.from(arguments));
  }
  /**
     * _Constructs a yearly recurrence rule with an end date._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every year and a
     * value of 3 specifies that the rule should repeat every third year.
     * @param interval - Interval at which to repeat the rule.
     * @param endDate - Date at which the recurrence rule should end.
     * @see https://docs.scriptable.app/recurrencerule/#yearlyenddate
     */
  static yearlyEndDate(interval: number, endDate: Date): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('yearlyEndDate', Array.from(arguments));
  }
  /**
     * _Constructs a yearly recurrence rule with an occurrence count._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every year and a
     * value of 3 specifies that the rule should repeat every third year.
     * @param interval - Interval at which to repeat the rule.
     * @param occurrenceCount - Number of times the rule should repeat before it ends.
     * @see https://docs.scriptable.app/recurrencerule/#yearlyoccurrencecount
     */
  static yearlyOccurrenceCount(interval: number, occurrenceCount: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('yearlyOccurrenceCount', Array.from(arguments));
  }
  /**
     * _Constructs a complex weekly recurrence rule._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every week and a
     * value of 3 specifies that the rule should repeat every third week.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @see https://docs.scriptable.app/recurrencerule/#complexweekly
     */
  static complexWeekly(interval: number, daysOfTheWeek: readonly number[], setPositions: readonly number[]): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexWeekly', Array.from(arguments));
  }
  /**
     * _Constructs a complex weekly recurrence rule with an end date._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every week and a
     * value of 3 specifies that the rule should repeat every third week.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @param endDate - Date at which the recurrence rule should end.
     * @see https://docs.scriptable.app/recurrencerule/#complexweeklyenddate
     */
  static complexWeeklyEndDate(interval: number, daysOfTheWeek: readonly number[], setPositions: readonly number[], endDate: Date): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexWeeklyEndDate', Array.from(arguments));
  }
  /**
     * _Constructs a complex weekly recurrence rule with an occurrence count._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every week and a
     * value of 3 specifies that the rule should repeat every third week.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @param occurrenceCount - Number of times the rule should repeat before it ends.
     * @see https://docs.scriptable.app/recurrencerule/#complexweeklyoccurrencecount
     */
  static complexWeeklyOccurrenceCount(interval: number, daysOfTheWeek: readonly number[], setPositions: readonly number[], occurrenceCount: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexWeeklyOccurrenceCount', Array.from(arguments));
  }
  /**
     * _Constructs a complex monthly recurrence rule._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every month and a
     * value of 3 specifies that the rule should repeat every third month.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param daysOfTheMonth - Days of the month to repeat the rule. Values range from 1 to 31 and from -1 to -31.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @see https://docs.scriptable.app/recurrencerule/#complexmonthly
     */
  static complexMonthly(interval: number, daysOfTheWeek: readonly number[], daysOfTheMonth: readonly number[], setPositions: readonly number[]): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexMonthly', Array.from(arguments));
  }
  /**
     * _Constructs a complex monthly recurrence rule with an end date._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every month and a
     * value of 3 specifies that the rule should repeat every third month.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param daysOfTheMonth - Days of the month to repeat the rule. Values range from 1 to 31 and from -1 to -31.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @param endDate - Date at which the recurrence rule should end.
     * @see https://docs.scriptable.app/recurrencerule/#complexmonthlyenddate
     */
  static complexMonthlyEndDate(interval: number, daysOfTheWeek: readonly number[], daysOfTheMonth: readonly number[], setPositions: readonly number[], endDate: Date): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexMonthlyEndDate', Array.from(arguments));
  }
  /**
     * _Constructs a complex monthly recurrence rule with an occurrence count._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every month and a
     * value of 3 specifies that the rule should repeat every third month.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param daysOfTheMonth - Days of the month to repeat the rule. Values range from 1 to 31 and from -1 to -31.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @param occurrenceCount - Number of times the rule should repeat before it ends.
     * @see https://docs.scriptable.app/recurrencerule/#complexmonthlyoccurrencecount
     */
  static complexMonthlyOccurrenceCount(interval: number, daysOfTheWeek: readonly number[], daysOfTheMonth: readonly number[], setPositions: readonly number[], occurrenceCount: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexMonthlyOccurrenceCount', Array.from(arguments));
  }
  /**
     * _Constructs a complex yearly recurrence rule._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every year and a
     * value of 3 specifies that the rule should repeat every third year.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param monthsOfTheYear - The months of the year to repeat the rule. Values range from 1 to 12.
     * @param weeksOfTheYear - The weeks of the year to repeat the rule. Values range from 1 to 53 and -1 to -53.
     * @param daysOfTheYear - The days of the year to repeat the rule. Values range from 1 to 366 and -1 to -366.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @see https://docs.scriptable.app/recurrencerule/#complexyearly
     */
  static complexYearly(interval: number, daysOfTheWeek: readonly number[], monthsOfTheYear: readonly number[], weeksOfTheYear: readonly number[], daysOfTheYear: readonly number[], setPositions: readonly number[]): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexYearly', Array.from(arguments));
  }
  /**
     * _Constructs a complex yearly recurrence rule with an end date._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every year and a
     * value of 3 specifies that the rule should repeat every third week.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param monthsOfTheYear - The months of the year to repeat the rule. Values range from 1 to 12.
     * @param weeksOfTheYear - The weeks of the year to repeat the rule. Values range from 1 to 53 and -1 to -53.
     * @param daysOfTheYear - The days of the year to repeat the rule. Values range from 1 to 366 and -1 to -366.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @param endDate - Date at which the recurrence rule should end.
     * @see https://docs.scriptable.app/recurrencerule/#complexyearlyenddate
     */
  static complexYearlyEndDate(interval: number, daysOfTheWeek: readonly number[], monthsOfTheYear: readonly number[], weeksOfTheYear: readonly number[], daysOfTheYear: readonly number[], setPositions: readonly number[], endDate: Date): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexYearlyEndDate', Array.from(arguments));
  }
  /**
     * _Constructs a complex yearly recurrence rule with an occurrence count._
     *
     * The interval should have a value greater than 0 and specifies how often the pattern repeats. For example, an interval of 1 specifies that the rule should repeat every year and a
     * value of 3 specifies that the rule should repeat every third year.
     *
     * The setPositions filters which recurrences to include in the rule's frequency. For example, a yearly recurrence rule that has a daysOfTheWeek value that specifies Monday through
     * Friday and setPositions contain 2 and -1, occurs only on the second weekday and last weekday of every year.
     * @param interval - Interval at which to repeat the rule.
     * @param daysOfTheWeek - Days of the week to repeat the rule. Values range from 1 to 7, with Sunday being 1.
     * @param monthsOfTheYear - The months of the year to repeat the rule. Values range from 1 to 12.
     * @param weeksOfTheYear - The weeks of the year to repeat the rule. Values range from 1 to 53 and -1 to -53.
     * @param daysOfTheYear - The days of the year to repeat the rule. Values range from 1 to 366 and -1 to -366.
     * @param setPositions - Filters which recurrences to include in the rule's frequency.
     * @param occurrenceCount - Number of times the rule should repeat before it ends.
     * @see https://docs.scriptable.app/recurrencerule/#complexyearlyoccurrencecount
     */
  static complexYearlyOccurrenceCount(interval: number, daysOfTheWeek: readonly number[], monthsOfTheYear: readonly number[], weeksOfTheYear: readonly number[], daysOfTheYear: readonly number[], setPositions: readonly number[], occurrenceCount: number): RecurrenceRule {
    return this.invokeStaticMethod<RecurrenceRule>('complexYearlyOccurrenceCount', Array.from(arguments));
  }


}

export interface RelativeDateTimeFormatterState {
  locale: string;
}

export class AbsRelativeDateTimeFormatter<S extends RelativeDateTimeFormatterState = RelativeDateTimeFormatterState> extends ScriptableClass<S> implements RelativeDateTimeFormatter {
  static readonly identifier = 'RelativeDateTimeFormatter';

  get asRelativeDateTimeFormatter(): RelativeDateTimeFormatter {
    return this.asOriginal<RelativeDateTimeFormatter>();
  }


  get locale() {
    if (!('locale' in this.state)) {
      return "";
    }
    return this.state.locale as string;
  }

  set locale(value: string) {
    this.setState({ locale: value });
  }

  /**
     * _Creates a localized string communicating the amount of time between two dates._
     *
     * Creates a localized textual representation of the amount of time between to dates. If the two dates are the same, the function will return "now". If the reference date is
     * yesterday, the function will return "yesterday". Other examples include "in 10 seconds", "2 hours ago", "last week" and "next year".
     * @param date - The date to create a relative date and time for.
     * @param referenceDate - The reference date that `date` is relative to.
     * @see https://docs.scriptable.app/relativedatetimeformatter/#-string
     */
  string(date: Date, referenceDate: Date): string {
    return this.invokeMethod<string>('string', Array.from(arguments));
  }
  /**
     * _Prefers named dates and times._
     *
     * When using the named style, the formatter tries to find a suitable textual representation over a numeric value for the relative time, e.g. "now" instead of "in 0 seconds" and
     * "yesterday" instead of "1 day ago".
     *
     * When no named representation is found the formatter will fallback to using the numeric style.
     * @see https://docs.scriptable.app/relativedatetimeformatter/#-usenameddatetimestyle
     */
  useNamedDateTimeStyle(): void {
    return this.invokeMethod<void>('useNamedDateTimeStyle', Array.from(arguments));
  }
  /**
     * _Prefers numeric dates and times._
     *
     * When using the numeric style, the formatter will always prefer numeric representations over named representations. E.g. it will return "in 0 seconds" instead of "now" and "1 day
     * ago" instead of "yesteday".
     * @see https://docs.scriptable.app/relativedatetimeformatter/#-usenumericdatetimestyle
     */
  useNumericDateTimeStyle(): void {
    return this.invokeMethod<void>('useNumericDateTimeStyle', Array.from(arguments));
  }
}

export interface ReminderState {
  identifier: string;
  title: string;
  notes: string;
  isCompleted: boolean;
  isOverdue: boolean;
  priority: number;
  dueDate: Date;
  dueDateIncludesTime: boolean;
  completionDate: Date;
  creationDate: Date;
  calendar: Calendar;
}

export class AbsReminder<S extends ReminderState = ReminderState> extends ScriptableClass<S> implements Reminder {
  static readonly identifier = 'Reminder';

  get asReminder(): Reminder {
    return this.asOriginal<Reminder>();
  }

  /**
     * _Fetches the schedule of reminders._
     *
     * The fetched result contains reminders that are due today and reminders that are overdue. This is similar to the reminders shown in the Reminders apps "Scheduled" list. For
     * performance reasons iOS limits fetched results to events within a four year timespan.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#scheduled
     */
  static scheduled(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('scheduled', Array.from(arguments));
  }
  /**
     * _Fetches all reminders._
     *
     * For performance reasons iOS limits fetched results to events within a four year timespan.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#all
     */
  static all(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('all', Array.from(arguments));
  }
  /**
     * _Fetches all completed reminders._
     *
     * For performance reasons iOS limits fetched results to events within a four year timespan.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#allcompleted
     */
  static allCompleted(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allCompleted', Array.from(arguments));
  }
  /**
     * _Fetches all incomplete reminders._
     *
     * For performance reasons iOS limits fetched results to events within a four year timespan.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#allincomplete
     */
  static allIncomplete(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allIncomplete', Array.from(arguments));
  }
  /**
     * _Fetches all reminders due today._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#allduetoday
     */
  static allDueToday(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allDueToday', Array.from(arguments));
  }
  /**
     * _Fetches completed reminders due today._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedduetoday
     */
  static completedDueToday(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedDueToday', Array.from(arguments));
  }
  /**
     * _Fetches incomplete reminders due today._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#incompleteduetoday
     */
  static incompleteDueToday(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('incompleteDueToday', Array.from(arguments));
  }
  /**
     * _Fetches all reminders due tomorrow._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#allduetomorrow
     */
  static allDueTomorrow(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allDueTomorrow', Array.from(arguments));
  }
  /**
     * _Fetches completed reminders due tomorrow._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedduetomorrow
     */
  static completedDueTomorrow(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedDueTomorrow', Array.from(arguments));
  }
  /**
     * _Fetches incomplete reminders due tomorrow._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#incompleteduetomorrow
     */
  static incompleteDueTomorrow(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('incompleteDueTomorrow', Array.from(arguments));
  }
  /**
     * _Fetches all reminders due yesterday._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#alldueyesterday
     */
  static allDueYesterday(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allDueYesterday', Array.from(arguments));
  }
  /**
     * _Fetches completed reminders due yesterday._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completeddueyesterday
     */
  static completedDueYesterday(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedDueYesterday', Array.from(arguments));
  }
  /**
     * _Fetches incomplete reminders due yesterday._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#incompletedueyesterday
     */
  static incompleteDueYesterday(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('incompleteDueYesterday', Array.from(arguments));
  }
  /**
     * _Fetches all reminders due this week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#allduethisweek
     */
  static allDueThisWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allDueThisWeek', Array.from(arguments));
  }
  /**
     * _Fetches completed reminders due this week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedduethisweek
     */
  static completedDueThisWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedDueThisWeek', Array.from(arguments));
  }
  /**
     * _Fetches incomplete reminders due this week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#incompleteduethisweek
     */
  static incompleteDueThisWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('incompleteDueThisWeek', Array.from(arguments));
  }
  /**
     * _Fetches all reminders due next week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#allduenextweek
     */
  static allDueNextWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allDueNextWeek', Array.from(arguments));
  }
  /**
     * _Fetches completed reminders due next week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedduenextweek
     */
  static completedDueNextWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedDueNextWeek', Array.from(arguments));
  }
  /**
     * _Fetches incomplete reminders due next week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#incompleteduenextweek
     */
  static incompleteDueNextWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('incompleteDueNextWeek', Array.from(arguments));
  }
  /**
     * _Fetches all reminders due last week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#allduelastweek
     */
  static allDueLastWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allDueLastWeek', Array.from(arguments));
  }
  /**
     * _Fetches completed reminders due last week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedduelastweek
     */
  static completedDueLastWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedDueLastWeek', Array.from(arguments));
  }
  /**
     * _Fetches incomplete reminders due last week._
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#incompleteduelastweek
     */
  static incompleteDueLastWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('incompleteDueLastWeek', Array.from(arguments));
  }
  /**
     * _Fetches reminders completed today._
     *
     * Note that this does not take the due date into account. This will return all reminders that you have completed today.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedtoday
     */
  static completedToday(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedToday', Array.from(arguments));
  }
  /**
     * _Fetches reminders completed this week._
     *
     * Note that this does not take the due date into account. This will return all reminders that you have completed this week.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedthisweek
     */
  static completedThisWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedThisWeek', Array.from(arguments));
  }
  /**
     * _Fetches reminders completed last week._
     *
     * Note that this does not take the due date into account. This will return all reminders that you have completed last week.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedlastweek
     */
  static completedLastWeek(calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedLastWeek', Array.from(arguments));
  }
  /**
     * _Fetches due reminders._
     *
     * Fetches reminders that are due within the time interval constituted by the start and end dates.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#allduebetween
     */
  static allDueBetween(startDate: Date, endDate: Date, calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('allDueBetween', Array.from(arguments));
  }
  /**
     * _Fetches completed reminders._
     *
     * Fetches reminders that are completed and that were due within the time interval constituted by the start and end dates.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedduebetween
     */
  static completedDueBetween(startDate: Date, endDate: Date, calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedDueBetween', Array.from(arguments));
  }
  /**
     * _Fetches incomplete reminders._
     *
     * Fetches reminders that are incomplete and that were due within the time interval constituted by the start and end dates.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#incompleteduebetween
     */
  static incompleteDueBetween(startDate: Date, endDate: Date, calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('incompleteDueBetween', Array.from(arguments));
  }
  /**
     * _Fetches completed reminders._
     *
     * Fetches reminders that were completed within the time interval constituted by the start and end dates.
     * @param calendars - Calendars to fetch reminders for. Defaults to all calendars.
     * @see https://docs.scriptable.app/reminder/#completedbetween
     */
  static completedBetween(startDate: Date, endDate: Date, calendars?: readonly Calendar[]): Promise<Reminder[]> {
    return this.invokeStaticMethod<Promise<Reminder[]>>('completedBetween', Array.from(arguments));
  }

  get identifier() {
    if (!('identifier' in this.state)) {
      return "";
    }
    return this.state.identifier as string;
  }

  set identifier(value: string) {
    this.setState({ identifier: value });
  }
  get title() {
    if (!('title' in this.state)) {
      return "";
    }
    return this.state.title as string;
  }

  set title(value: string) {
    this.setState({ title: value });
  }
  get notes() {
    if (!('notes' in this.state)) {
      return "";
    }
    return this.state.notes as string;
  }

  set notes(value: string) {
    this.setState({ notes: value });
  }
  get isCompleted() {
    if (!('isCompleted' in this.state)) {
      return false;
    }
    return this.state.isCompleted as boolean;
  }

  set isCompleted(value: boolean) {
    this.setState({ isCompleted: value });
  }
  get isOverdue() {
    if (!('isOverdue' in this.state)) {
      return false;
    }
    return this.state.isOverdue as boolean;
  }

  set isOverdue(value: boolean) {
    this.setState({ isOverdue: value });
  }
  get priority() {
    if (!('priority' in this.state)) {
      return 0;
    }
    return this.state.priority as number;
  }

  set priority(value: number) {
    this.setState({ priority: value });
  }
  get dueDate() {
    if (!('dueDate' in this.state)) {
      throw new Error('Required property "dueDate" of type "Date" is not set');
    }
    return this.state.dueDate as Date;
  }

  set dueDate(value: Date) {
    this.setState({ dueDate: value });
  }
  get dueDateIncludesTime() {
    if (!('dueDateIncludesTime' in this.state)) {
      return false;
    }
    return this.state.dueDateIncludesTime as boolean;
  }

  set dueDateIncludesTime(value: boolean) {
    this.setState({ dueDateIncludesTime: value });
  }
  get completionDate() {
    if (!('completionDate' in this.state)) {
      throw new Error('Required property "completionDate" of type "Date" is not set');
    }
    return this.state.completionDate as Date;
  }

  set completionDate(value: Date) {
    this.setState({ completionDate: value });
  }
  get creationDate() {
    if (!('creationDate' in this.state)) {
      throw new Error('Required property "creationDate" of type "Date" is not set');
    }
    return this.state.creationDate as Date;
  }

  set creationDate(value: Date) {
    this.setState({ creationDate: value });
  }
  get calendar() {
    if (!('calendar' in this.state)) {
      throw new Error('Required property "calendar" of type "Calendar" is not set');
    }
    return this.state.calendar as Calendar;
  }

  set calendar(value: Calendar) {
    this.setState({ calendar: value });
  }

  /**
     * _Adds a recurrence rule._
     *
     * Recurrence rules specify when the reminder should be repeated. See the documentation of RecurrenceRule for more information on creating rules.
     * @param recurrenceRule - Recurrence rule to add to the reminder.
     * @see https://docs.scriptable.app/reminder/#-addrecurrencerule
     */
  addRecurrenceRule(recurrenceRule: RecurrenceRule): void {
    return this.invokeMethod<void>('addRecurrenceRule', Array.from(arguments));
  }
  /**
     * _Removes all recurrence rules._
     * @see https://docs.scriptable.app/reminder/#-removeallrecurrencerules
     */
  removeAllRecurrenceRules(): void {
    return this.invokeMethod<void>('removeAllRecurrenceRules', Array.from(arguments));
  }
  /**
     * _Saves reminder._
     *
     * Saves changes to a reminder, inserting it into the calendar if it is newly created.
     * @see https://docs.scriptable.app/reminder/#-save
     */
  save(): void {
    return this.invokeMethod<void>('save', Array.from(arguments));
  }
  /**
     * _Removes reminder from calendar._
     * @see https://docs.scriptable.app/reminder/#-remove
     */
  remove(): void {
    return this.invokeMethod<void>('remove', Array.from(arguments));
  }
}

export interface RequestState {
  url: string;
  method: string;
  headers: { [key: string]: string; };
  body: any;
  timeoutInterval: number;
  response: { [key: string]: any; };
  allowInsecureRequest: boolean;
  onRedirect: (arg0: Request) => Request;
}

export class AbsRequest<S extends RequestState = RequestState> extends ScriptableClass<S> implements Request {
  static readonly identifier = 'Request';

  get asRequest(): Request {
    return this.asOriginal<Request>();
  }


  get url() {
    if (!('url' in this.state)) {
      return "";
    }
    return this.state.url as string;
  }

  set url(value: string) {
    this.setState({ url: value });
  }
  get method() {
    if (!('method' in this.state)) {
      return "";
    }
    return this.state.method as string;
  }

  set method(value: string) {
    this.setState({ method: value });
  }
  get headers() {
    if (!('headers' in this.state)) {
      throw new Error('Required property "headers" of type "{ [key: string]: string; }" is not set');
    }
    return this.state.headers as { [key: string]: string; };
  }

  set headers(value: { [key: string]: string; }) {
    this.setState({ headers: value });
  }
  get body() {
    if (!('body' in this.state)) {
      throw new Error('Required property "body" of type "any" is not set');
    }
    return this.state.body as any;
  }

  set body(value: any) {
    this.setState({ body: value });
  }
  get timeoutInterval() {
    if (!('timeoutInterval' in this.state)) {
      return 0;
    }
    return this.state.timeoutInterval as number;
  }

  set timeoutInterval(value: number) {
    this.setState({ timeoutInterval: value });
  }
  get response() {
    if (!('response' in this.state)) {
      throw new Error('Required property "response" of type "{ [key: string]: any; }" is not set');
    }
    return this.state.response as { [key: string]: any; };
  }

  set response(value: { [key: string]: any; }) {
    this.setState({ response: value });
  }
  get allowInsecureRequest() {
    if (!('allowInsecureRequest' in this.state)) {
      return false;
    }
    return this.state.allowInsecureRequest as boolean;
  }

  set allowInsecureRequest(value: boolean) {
    this.setState({ allowInsecureRequest: value });
  }
  get onRedirect() {
    if (!('onRedirect' in this.state)) {
      throw new Error('Required property "onRedirect" of type "(arg0: Request) => Request" is not set');
    }
    return this.state.onRedirect as (arg0: Request) => Request;
  }

  set onRedirect(value: (arg0: Request) => Request) {
    this.setState({ onRedirect: value });
  }

  /**
     * _Sends request._
     *
     * Call to send the configured request to the specified URL. The raw response is provided when the returned promise is fulfilled.
     * @see https://docs.scriptable.app/request/#-load
     */
  load(): Promise<Data> {
    return this.invokeMethod<Promise<Data>>('load', Array.from(arguments));
  }
  /**
     * _Sends request and parses response as a string._
     *
     * Call to send the configured request to the specified URL. The response is parsed to a string and provided when the returned promise is fulfilled.
     * @see https://docs.scriptable.app/request/#-loadstring
     */
  loadString(): Promise<string> {
    return this.invokeMethod<Promise<string>>('loadString', Array.from(arguments));
  }
  /**
     * _Sends request and parses response as JSON._
     *
     * Call to send the configured request to the specified URL. The response is expected to be a valid JSON string and is parsed into an object.
     * @see https://docs.scriptable.app/request/#-loadjson
     */
  loadJSON(): Promise<any> {
    return this.invokeMethod<Promise<any>>('loadJSON', Array.from(arguments));
  }
  /**
     * _Sends request and parses response as an image._
     *
     * Call to send the configured request to the specified URL. The response is expected to be an image.
     * @see https://docs.scriptable.app/request/#-loadimage
     */
  loadImage(): Promise<Image> {
    return this.invokeMethod<Promise<Image>>('loadImage', Array.from(arguments));
  }
  /**
     * _Adds a parameter to a multipart request._
     *
     * Converts the request to a multipart request and adds a parameter with the specified name and value. Be aware that the `body` property on the request is ignored for multipart
     * requests as parameters and files added to the request constitutes the body.
     *
     * Calling this function will make the request a multipart request. When the request is send, the content type will automatically be set to "multipart/form-data".
     * @param name - Name of the parameter.
     * @param value - Value of the parameter.
     * @see https://docs.scriptable.app/request/#-addparametertomultipart
     */
  addParameterToMultipart(name: string, value: string): void {
    return this.invokeMethod<void>('addParameterToMultipart', Array.from(arguments));
  }
  /**
     * _Adds a file to a multipart request._
     *
     * Converts the request to a multipart request and adds the file to the request. Be aware that the `body` property on the request is ignored for multipart requests as parameters and
     * files added to the request constitutes the body.
     *
     * Calling this function will make the request a multipart request. When the request is send, the content type will automatically be set to "multipart/form-data".
     * @param data - File data to add.
     * @param mimeType - MIME type of the file to add.
     * @param name - Name of the parameter which holds the file.
     * @param filename - Name of the file.
     * @see https://docs.scriptable.app/request/#-addfiledatatomultipart
     */
  addFileDataToMultipart(data: Data, mimeType: string, name: string, filename: string): void {
    return this.invokeMethod<void>('addFileDataToMultipart', Array.from(arguments));
  }
  /**
     * _Adds a file to a multipart request._
     *
     * Converts the request to a multipart request and adds the file to the request. The function will automatically determine the MIME type of the file as well as the filename. Be aware
     * that the `body` property on the request is ignored for multipart requests as parameters and files added to the request constitutes the body.
     *
     * Calling this function will make the request a multipart request. When the request is send, the content type will automatically be set to "multipart/form-data".
     * @param filePath - Path of the file to add.
     * @param name - Name of the parameter which holds the file.
     * @param filename - Optional name of the uploaded file.
     * @see https://docs.scriptable.app/request/#-addfiletomultipart
     */
  addFileToMultipart(filePath: string, name: string, filename?: string): void {
    return this.invokeMethod<void>('addFileToMultipart', Array.from(arguments));
  }
  /**
     * _Adds an image to a multipart request._
     *
     * Converts the request to a multipart request and adds the image to the request. The function will automatically determine the MIME type of the file Be aware that the `body` property
     * on the request is ignored for multipart requests as parameters and files added to the request constitutes the body.
     *
     * Calling this function will make the request a multipart request. When the request is send, the content type will automatically be set to "multipart/form-data".
     * @param image - Image to add.
     * @param name - Name of the parameter which holds the file.
     * @param filename - Optional name of the uploaded file.
     * @see https://docs.scriptable.app/request/#-addimagetomultipart
     */
  addImageToMultipart(image: Image, name: string, filename?: string): void {
    return this.invokeMethod<void>('addImageToMultipart', Array.from(arguments));
  }
}

export interface SFSymbolState {
  image: Image;
}

export class AbsSFSymbol<S extends SFSymbolState = SFSymbolState> extends ScriptableClass<S> implements SFSymbol {
  static readonly identifier = 'SFSymbol';

  get asSFSymbol(): SFSymbol {
    return this.asOriginal<SFSymbol>();
  }

  /**
     * _Constructs an SF symbol._
     *
     * SF symbols are Apples configurable icons that are designed to look great with the San Francisco font.
     *
     * Symbols are referenced by their name. You can find the symbol names in [Apples SF Symbols app for macOS](https://developer.apple.com/sf-symbols/). You can also browse symbol names
     * in the [SF Symbols Browser](https://apps.apple.com/us/app/sf-symbols-browser/id1491161336) and [San Fransymbols](https://apps.apple.com/us/app/san-fransymbols/id1504761986) apps
     * for iOS.
     * @param symbolName - Name of the symbol.
     * @see https://docs.scriptable.app/sfsymbol/#named
     */
  static named(symbolName: string): SFSymbol {
    return this.invokeStaticMethod<SFSymbol>('named', Array.from(arguments));
  }

  get image() {
    if (!('image' in this.state)) {
      throw new Error('Required property "image" of type "Image" is not set');
    }
    return this.state.image as Image;
  }

  set image(value: Image) {
    this.setState({ image: value });
  }

  /**
     * _Configures the symbol with the specified font information._
     * @see https://docs.scriptable.app/sfsymbol/#-applyfont
     */
  applyFont(font: Font): void {
    return this.invokeMethod<void>('applyFont', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use an ultra light weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applyultralightweight
     */
  applyUltraLightWeight(): void {
    return this.invokeMethod<void>('applyUltraLightWeight', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use a thin weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applythinweight
     */
  applyThinWeight(): void {
    return this.invokeMethod<void>('applyThinWeight', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use a light weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applylightweight
     */
  applyLightWeight(): void {
    return this.invokeMethod<void>('applyLightWeight', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use a regular weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applyregularweight
     */
  applyRegularWeight(): void {
    return this.invokeMethod<void>('applyRegularWeight', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use a medium weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applymediumweight
     */
  applyMediumWeight(): void {
    return this.invokeMethod<void>('applyMediumWeight', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use a semibold weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applysemiboldweight
     */
  applySemiboldWeight(): void {
    return this.invokeMethod<void>('applySemiboldWeight', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use a bold weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applyboldweight
     */
  applyBoldWeight(): void {
    return this.invokeMethod<void>('applyBoldWeight', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use a heavy weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applyheavyweight
     */
  applyHeavyWeight(): void {
    return this.invokeMethod<void>('applyHeavyWeight', Array.from(arguments));
  }
  /**
     * _Configures the symbol to use a black weight._
     * @see https://docs.scriptable.app/sfsymbol/#-applyblackweight
     */
  applyBlackWeight(): void {
    return this.invokeMethod<void>('applyBlackWeight', Array.from(arguments));
  }
}

export interface SizeState {
  width: number;
  height: number;
}

export class AbsSize<S extends SizeState = SizeState> extends ScriptableClass<S> implements Size {
  static readonly identifier = 'Size';

  get asSize(): Size {
    return this.asOriginal<Size>();
  }


  get width() {
    if (!('width' in this.state)) {
      return 0;
    }
    return this.state.width as number;
  }

  set width(value: number) {
    this.setState({ width: value });
  }
  get height() {
    if (!('height' in this.state)) {
      return 0;
    }
    return this.state.height as number;
  }

  set height(value: number) {
    this.setState({ height: value });
  }

}

export interface TextFieldState {
  text: string;
  placeholder: string;
  isSecure: boolean;
  textColor: Color;
  font: Font;
}

export class AbsTextField<S extends TextFieldState = TextFieldState> extends ScriptableClass<S> implements TextField {
  static readonly identifier = 'TextField';

  get asTextField(): TextField {
    return this.asOriginal<TextField>();
  }


  get text() {
    if (!('text' in this.state)) {
      return "";
    }
    return this.state.text as string;
  }

  set text(value: string) {
    this.setState({ text: value });
  }
  get placeholder() {
    if (!('placeholder' in this.state)) {
      return "";
    }
    return this.state.placeholder as string;
  }

  set placeholder(value: string) {
    this.setState({ placeholder: value });
  }
  get isSecure() {
    if (!('isSecure' in this.state)) {
      return false;
    }
    return this.state.isSecure as boolean;
  }

  set isSecure(value: boolean) {
    this.setState({ isSecure: value });
  }
  get textColor() {
    if (!('textColor' in this.state)) {
      throw new Error('Required property "textColor" of type "Color" is not set');
    }
    return this.state.textColor as Color;
  }

  set textColor(value: Color) {
    this.setState({ textColor: value });
  }
  get font() {
    if (!('font' in this.state)) {
      throw new Error('Required property "font" of type "Font" is not set');
    }
    return this.state.font as Font;
  }

  set font(value: Font) {
    this.setState({ font: value });
  }

  /**
     * _Use the default keyboard for entering text._
     * @see https://docs.scriptable.app/textfield/#-setdefaultkeyboard
     */
  setDefaultKeyboard(): void {
    return this.invokeMethod<void>('setDefaultKeyboard', Array.from(arguments));
  }
  /**
     * _Use a keyboard that prominently features the numbers 0 through 9._
     * @see https://docs.scriptable.app/textfield/#-setnumberpadkeyboard
     */
  setNumberPadKeyboard(): void {
    return this.invokeMethod<void>('setNumberPadKeyboard', Array.from(arguments));
  }
  /**
     * _Use a numeric keyboard with a decimal point for entering text._
     * @see https://docs.scriptable.app/textfield/#-setdecimalpadkeyboard
     */
  setDecimalPadKeyboard(): void {
    return this.invokeMethod<void>('setDecimalPadKeyboard', Array.from(arguments));
  }
  /**
     * _Use a numeric keyboard with punctuation for entering text._
     * @see https://docs.scriptable.app/textfield/#-setnumbersandpunctuationkeyboard
     */
  setNumbersAndPunctuationKeyboard(): void {
    return this.invokeMethod<void>('setNumbersAndPunctuationKeyboard', Array.from(arguments));
  }
  /**
     * _Use a keyboard that prominently features the numbers 0 through 9 and the * and # characters._
     * @see https://docs.scriptable.app/textfield/#-setphonepadkeyboard
     */
  setPhonePadKeyboard(): void {
    return this.invokeMethod<void>('setPhonePadKeyboard', Array.from(arguments));
  }
  /**
     * _Use a keyboard that prominently features the space and period characters._
     * @see https://docs.scriptable.app/textfield/#-setwebsearchkeyboard
     */
  setWebSearchKeyboard(): void {
    return this.invokeMethod<void>('setWebSearchKeyboard', Array.from(arguments));
  }
  /**
     * _Use a keyboard that prominently features the @, period and space characters._
     * @see https://docs.scriptable.app/textfield/#-setemailaddresskeyboard
     */
  setEmailAddressKeyboard(): void {
    return this.invokeMethod<void>('setEmailAddressKeyboard', Array.from(arguments));
  }
  /**
     * _Use a keyboard that prominently features the period and slash characters and the ".com" string._
     * @see https://docs.scriptable.app/textfield/#-seturlkeyboard
     */
  setURLKeyboard(): void {
    return this.invokeMethod<void>('setURLKeyboard', Array.from(arguments));
  }
  /**
     * _Use a keyboard that prominently features the @ and # characters._
     * @see https://docs.scriptable.app/textfield/#-settwitterkeyboard
     */
  setTwitterKeyboard(): void {
    return this.invokeMethod<void>('setTwitterKeyboard', Array.from(arguments));
  }
  /**
     * _Left aligns the text._
     *
     * This is the default text alignment.
     * @see https://docs.scriptable.app/textfield/#-leftaligntext
     */
  leftAlignText(): void {
    return this.invokeMethod<void>('leftAlignText', Array.from(arguments));
  }
  /**
     * _Center aligns the text._
     * @see https://docs.scriptable.app/textfield/#-centeraligntext
     */
  centerAlignText(): void {
    return this.invokeMethod<void>('centerAlignText', Array.from(arguments));
  }
  /**
     * _Right aligns the text._
     * @see https://docs.scriptable.app/textfield/#-rightaligntext
     */
  rightAlignText(): void {
    return this.invokeMethod<void>('rightAlignText', Array.from(arguments));
  }
}

export interface TimerState {
  timeInterval: number;
  repeats: boolean;
}

export class AbsTimer<S extends TimerState = TimerState> extends ScriptableClass<S> implements Timer {
  static readonly identifier = 'Timer';

  get asTimer(): Timer {
    return this.asOriginal<Timer>();
  }

  /**
     * _Schedules a timer._
     *
     * This is a convenience function for creating a new timer. The created timer is instantly scheduled and will fire after the specified time interval.
     * @param timeInterval - The time interval to fire the timer at.
     * @param repeats - Whether the timer should repeat or not.
     * @param callback - The callback to be called when the timer fires.
     * @see https://docs.scriptable.app/timer/#schedule
     */
  static schedule(timeInterval: number, repeats: boolean, callback: () => void): Timer {
    return this.invokeStaticMethod<Timer>('schedule', Array.from(arguments));
  }

  get timeInterval() {
    if (!('timeInterval' in this.state)) {
      return 0;
    }
    return this.state.timeInterval as number;
  }

  set timeInterval(value: number) {
    this.setState({ timeInterval: value });
  }
  get repeats() {
    if (!('repeats' in this.state)) {
      return false;
    }
    return this.state.repeats as boolean;
  }

  set repeats(value: boolean) {
    this.setState({ repeats: value });
  }

  /**
     * _Schedules the timer._
     *
     * Schedules the timer using its configuration. The supplied function is called when the timer fires. To stop the timer from firing, call the `invalidate()` function.
     * @param callback - The callback to be called when the timer fires.
     * @see https://docs.scriptable.app/timer/#-schedule
     */
  schedule(callback: () => void): void {
    return this.invokeMethod<void>('schedule', Array.from(arguments));
  }
  /**
     * _Stops the timer from firing._
     *
     * Stops the timer from firing ever again. Non-repeating timers are automatically invalidated after they have fired once. Repeating timers must be manually invalidated.
     * @see https://docs.scriptable.app/timer/#-invalidate
     */
  invalidate(): void {
    return this.invokeMethod<void>('invalidate', Array.from(arguments));
  }
}

export interface UITableState {
  showSeparators: boolean;
}

export class AbsUITable<S extends UITableState = UITableState> extends ScriptableClass<S> implements UITable {
  static readonly identifier = 'UITable';

  get asUITable(): UITable {
    return this.asOriginal<UITable>();
  }


  get showSeparators() {
    if (!('showSeparators' in this.state)) {
      return false;
    }
    return this.state.showSeparators as boolean;
  }

  set showSeparators(value: boolean) {
    this.setState({ showSeparators: value });
  }

  /**
     * _Adds a row._
     *
     * Adds a row to the table. Rows are shown vertically in the table view, i.e. from the top and down. Rows are rendered in the order they are added.
     * @param row - Row to add.
     * @see https://docs.scriptable.app/uitable/#-addrow
     */
  addRow(row: UITableRow): void {
    return this.invokeMethod<void>('addRow', Array.from(arguments));
  }
  /**
     * _Removes a row._
     *
     * Removes a row from the table.
     * @param row - Row to remove.
     * @see https://docs.scriptable.app/uitable/#-removerow
     */
  removeRow(row: UITableRow): void {
    return this.invokeMethod<void>('removeRow', Array.from(arguments));
  }
  /**
     * _Removes all rows._
     *
     * Removes all rows from the table. If the table is presented, you must call the `reload` function in order for the changes to be reflected visually.
     * @see https://docs.scriptable.app/uitable/#-removeallrows
     */
  removeAllRows(): void {
    return this.invokeMethod<void>('removeAllRows', Array.from(arguments));
  }
  /**
     * _Reloads the table._
     *
     * If you add or remove rows while a table view is presented, you must reload the table in order for the changes to take effect.
     * @see https://docs.scriptable.app/uitable/#-reload
     */
  reload(): void {
    return this.invokeMethod<void>('reload', Array.from(arguments));
  }
  /**
     * _Presents the table._
     * @param fullscreen - Optional. Set to true to present the web view in fullscreen. This only has an effect when used within the app. Defaults to false.
     * @see https://docs.scriptable.app/uitable/#-present
     */
  present(fullscreen?: boolean): Promise<void> {
    return this.invokeMethod<Promise<void>>('present', Array.from(arguments));
  }
}

export interface UITableCellState {
  widthWeight: number;
  dismissOnTap: boolean;
  titleColor: Color;
  subtitleColor: Color;
  titleFont: Font;
  subtitleFont: Font;
  onTap: () => void;
}

export class AbsUITableCell<S extends UITableCellState = UITableCellState> extends ScriptableClass<S> implements UITableCell {
  static readonly identifier = 'UITableCell';

  get asUITableCell(): UITableCell {
    return this.asOriginal<UITableCell>();
  }

  /**
     * _Constructs a text cell._
     *
     * Constructs a new cell containing text.
     * @param title - Optional title to show in the cell.
     * @param subtitle - Optional subtitle shown below the title.
     * @see https://docs.scriptable.app/uitablecell/#text
     */
  static text(title?: string, subtitle?: string): UITableCell {
    return this.invokeStaticMethod<UITableCell>('text', Array.from(arguments));
  }
  /**
     * _Constructs an image cell._
     *
     * Constructs a new cell containing an image.
     * @param image - Image to show in the cell.
     * @see https://docs.scriptable.app/uitablecell/#image
     */
  static image(image: Image): UITableCell {
    return this.invokeStaticMethod<UITableCell>('image', Array.from(arguments));
  }
  /**
     * _Constructs an image cell._
     *
     * Constructs a new cell that loads the image at the specified URL.
     * @param url - URL to image.
     * @see https://docs.scriptable.app/uitablecell/#imageaturl
     */
  static imageAtURL(url: string): UITableCell {
    return this.invokeStaticMethod<UITableCell>('imageAtURL', Array.from(arguments));
  }
  /**
     * _Constructs a button cell._
     *
     * Constructs a new cell that contains a button. Set the `onTap` property to specify an action to performed when the button is tapped.
     * @param title - Title of the button.
     * @see https://docs.scriptable.app/uitablecell/#button
     */
  static button(title: string): UITableCell {
    return this.invokeStaticMethod<UITableCell>('button', Array.from(arguments));
  }

  get widthWeight() {
    if (!('widthWeight' in this.state)) {
      return 0;
    }
    return this.state.widthWeight as number;
  }

  set widthWeight(value: number) {
    this.setState({ widthWeight: value });
  }
  get dismissOnTap() {
    if (!('dismissOnTap' in this.state)) {
      return false;
    }
    return this.state.dismissOnTap as boolean;
  }

  set dismissOnTap(value: boolean) {
    this.setState({ dismissOnTap: value });
  }
  get titleColor() {
    if (!('titleColor' in this.state)) {
      throw new Error('Required property "titleColor" of type "Color" is not set');
    }
    return this.state.titleColor as Color;
  }

  set titleColor(value: Color) {
    this.setState({ titleColor: value });
  }
  get subtitleColor() {
    if (!('subtitleColor' in this.state)) {
      throw new Error('Required property "subtitleColor" of type "Color" is not set');
    }
    return this.state.subtitleColor as Color;
  }

  set subtitleColor(value: Color) {
    this.setState({ subtitleColor: value });
  }
  get titleFont() {
    if (!('titleFont' in this.state)) {
      throw new Error('Required property "titleFont" of type "Font" is not set');
    }
    return this.state.titleFont as Font;
  }

  set titleFont(value: Font) {
    this.setState({ titleFont: value });
  }
  get subtitleFont() {
    if (!('subtitleFont' in this.state)) {
      throw new Error('Required property "subtitleFont" of type "Font" is not set');
    }
    return this.state.subtitleFont as Font;
  }

  set subtitleFont(value: Font) {
    this.setState({ subtitleFont: value });
  }
  get onTap() {
    if (!('onTap' in this.state)) {
      throw new Error('Required property "onTap" of type "() => void" is not set');
    }
    return this.state.onTap as () => void;
  }

  set onTap(value: () => void) {
    this.setState({ onTap: value });
  }

  /**
     * _Left aligns content._
     *
     * Specifies that the content in the cell should be left aligned.
     * @see https://docs.scriptable.app/uitablecell/#-leftaligned
     */
  leftAligned(): void {
    return this.invokeMethod<void>('leftAligned', Array.from(arguments));
  }
  /**
     * _Center aligns content._
     *
     * Specifies that the content in the cell should be center aligned.
     * @see https://docs.scriptable.app/uitablecell/#-centeraligned
     */
  centerAligned(): void {
    return this.invokeMethod<void>('centerAligned', Array.from(arguments));
  }
  /**
     * _Right aligns content._
     *
     * Specifies that the content in the cell should be right aligned.
     * @see https://docs.scriptable.app/uitablecell/#-rightaligned
     */
  rightAligned(): void {
    return this.invokeMethod<void>('rightAligned', Array.from(arguments));
  }
}

export interface UITableRowState {
  cellSpacing: number;
  height: number;
  isHeader: boolean;
  dismissOnSelect: boolean;
  backgroundColor: Color;
  onSelect: () => void;
}

export class AbsUITableRow<S extends UITableRowState = UITableRowState> extends ScriptableClass<S> implements UITableRow {
  static readonly identifier = 'UITableRow';

  get asUITableRow(): UITableRow {
    return this.asOriginal<UITableRow>();
  }


  get cellSpacing() {
    if (!('cellSpacing' in this.state)) {
      return 0;
    }
    return this.state.cellSpacing as number;
  }

  set cellSpacing(value: number) {
    this.setState({ cellSpacing: value });
  }
  get height() {
    if (!('height' in this.state)) {
      return 0;
    }
    return this.state.height as number;
  }

  set height(value: number) {
    this.setState({ height: value });
  }
  get isHeader() {
    if (!('isHeader' in this.state)) {
      return false;
    }
    return this.state.isHeader as boolean;
  }

  set isHeader(value: boolean) {
    this.setState({ isHeader: value });
  }
  get dismissOnSelect() {
    if (!('dismissOnSelect' in this.state)) {
      return false;
    }
    return this.state.dismissOnSelect as boolean;
  }

  set dismissOnSelect(value: boolean) {
    this.setState({ dismissOnSelect: value });
  }
  get backgroundColor() {
    if (!('backgroundColor' in this.state)) {
      throw new Error('Required property "backgroundColor" of type "Color" is not set');
    }
    return this.state.backgroundColor as Color;
  }

  set backgroundColor(value: Color) {
    this.setState({ backgroundColor: value });
  }
  get onSelect() {
    if (!('onSelect' in this.state)) {
      throw new Error('Required property "onSelect" of type "() => void" is not set');
    }
    return this.state.onSelect as () => void;
  }

  set onSelect(value: () => void) {
    this.setState({ onSelect: value });
  }

  /**
     * _Adds a cell._
     *
     * Adds a cell to the row. Note that cells are shown in the order they are added to the row.
     * @param cell - Cell to add to the row.
     * @see https://docs.scriptable.app/uitablerow/#-addcell
     */
  addCell(cell: UITableCell): void {
    return this.invokeMethod<void>('addCell', Array.from(arguments));
  }
  /**
     * _Adds a text cell._
     *
     * Constructs a new cell containing the specified string and adds it to the row.
     * @param title - Optional title to show in the cell.
     * @param subtitle - Optional subtitle shown below the title in the cell.
     * @see https://docs.scriptable.app/uitablerow/#-addtext
     */
  addText(title?: string, subtitle?: string): UITableCell {
    return this.invokeMethod<UITableCell>('addText', Array.from(arguments));
  }
  /**
     * _Adds an image cell._
     *
     * Constructs a new cell containing the specified image and adds it to the row.
     * @param image - Image to show in the cell.
     * @see https://docs.scriptable.app/uitablerow/#-addimage
     */
  addImage(image: Image): UITableCell {
    return this.invokeMethod<UITableCell>('addImage', Array.from(arguments));
  }
  /**
     * _Adds an image cell._
     *
     * Constructs a new cell that loads the image at the specified url and adds the cell to the row.
     * @param url - URL to image.
     * @see https://docs.scriptable.app/uitablerow/#-addimageaturl
     */
  addImageAtURL(url: string): UITableCell {
    return this.invokeMethod<UITableCell>('addImageAtURL', Array.from(arguments));
  }
  /**
     * _Adds a button cell._
     *
     * Constructs a new cell that contains a button. Set the `onTap` property to specify an action to performed when the button is tapped.
     * @param title - Title of the button.
     * @see https://docs.scriptable.app/uitablerow/#-addbutton
     */
  addButton(title: string): UITableCell {
    return this.invokeMethod<UITableCell>('addButton', Array.from(arguments));
  }
}

export interface WebViewState {
  shouldAllowRequest: (arg0: Request) => boolean;
}

export class AbsWebView<S extends WebViewState = WebViewState> extends ScriptableClass<S> implements WebView {
  static readonly identifier = 'WebView';

  get asWebView(): WebView {
    return this.asOriginal<WebView>();
  }

  /**
     * _Loads HTML and renders it._
     * @param html - HTML to load and render.
     * @param baseURL - Optional. Base URL used to resolve relative URLs in the HTML.
     * @param preferredSize - Optional. Preferred size of the view. This size is not guaranteed to be respected and is only used when the script is run with Siri or in the Shortcuts app.
     * @param fullscreen - Optional. Set to true to present the web view in fullscreen. This only has an effect when used within the app. Defaults to false.
     * @see https://docs.scriptable.app/webview/#loadhtml
     */
  static loadHTML(html: string, baseURL?: string, preferredSize?: Size, fullscreen?: boolean): Promise<void> {
    return this.invokeStaticMethod<Promise<void>>('loadHTML', Array.from(arguments));
  }
  /**
     * _Loads a file and renders it._
     *
     * Files can be of various types, including HTML files and images.
     *
     * The supplied HTML file can reference files and nested directories in the same directory as the HTML file resides.
     *
     * The optional `preferredSize` parameter is ignored unless the script is run in a Siri Shortcut.
     *
     * If you are displaying large images in a memory constrained envrionment, for example in a Siri Shortcut, you should use the WebView bridge instead of the QuickLook bridge. The
     * technical reason for this is that a Siri Shortcut and other app extension processes have very limited memory and loading a very large image will cause the app extension to be
     * terminated. However, the web view will run in a different process meaning that it is not affected by the same memory constraints.
     * @param fileURL - URL of the file to load and render.
     * @param preferredSize - Optional. Preferred size of the view. This size is not guaranteed to be respected and is only used when the script is run with Siri or in the Shortcuts app.
     * @param fullscreen - Optional. Set to true to present the web view in fullscreen. This only has an effect when used within the app. Defaults to false.
     * @see https://docs.scriptable.app/webview/#loadfile
     */
  static loadFile(fileURL: string, preferredSize?: Size, fullscreen?: boolean): Promise<void> {
    return this.invokeStaticMethod<Promise<void>>('loadFile', Array.from(arguments));
  }
  /**
     * _Loads URL in web view and presents the web view._
     *
     * The optional `preferredSize` parameter is ignored unless the script is run in a Siri Shortcut.
     * @param url - URL to load into the web view.
     * @param preferredSize - Optional. Preferred size of the view. This size is not guaranteed to be respected and is only used when the script is run with Siri or in the Shortcuts app.
     * @param fullscreen - Optional. Set to true to present the web view in fullscreen. This only has an effect when used within the app. Defaults to false.
     * @see https://docs.scriptable.app/webview/#loadurl
     */
  static loadURL(url: string, preferredSize?: Size, fullscreen?: boolean): Promise<void> {
    return this.invokeStaticMethod<Promise<void>>('loadURL', Array.from(arguments));
  }

  get shouldAllowRequest() {
    if (!('shouldAllowRequest' in this.state)) {
      throw new Error('Required property "shouldAllowRequest" of type "(arg0: Request) => boolean" is not set');
    }
    return this.state.shouldAllowRequest as (arg0: Request) => boolean;
  }

  set shouldAllowRequest(value: (arg0: Request) => boolean) {
    this.setState({ shouldAllowRequest: value });
  }

  /**
     * _Loads URL in web view._
     *
     * Loads the URL in the web view. The returned promise will complete once the web view has finished loading.
     * @param url - URL to load into the web view.
     * @see https://docs.scriptable.app/webview/#-loadurl
     */
  loadURL(url: string): Promise<void> {
    return this.invokeMethod<Promise<void>>('loadURL', Array.from(arguments));
  }
  /**
     * _Loads request in web view._
     *
     * When loading a request into the web view, the HTTP method, body and headers of the request will be respected. The onRedirect function on the request will not be invoked.
     * @param request - Request to load into the web view.
     * @see https://docs.scriptable.app/webview/#-loadrequest
     */
  loadRequest(request: Request): Promise<void> {
    return this.invokeMethod<Promise<void>>('loadRequest', Array.from(arguments));
  }
  /**
     * _Loads HTML in web view._
     *
     * Loads the HTML into the web view. The returned promise will complete once the web view has finished loading.
     * @param html - HTML to load into the web view.
     * @param baseURL - Optional. Base URL used to resolve relative URLs in the HTML.
     * @see https://docs.scriptable.app/webview/#-loadhtml
     */
  loadHTML(html: string, baseURL?: string): Promise<void> {
    return this.invokeMethod<Promise<void>>('loadHTML', Array.from(arguments));
  }
  /**
     * _Loads file in the web view._
     *
     * Files can be of various types, including HTML files and images.
     *
     * The supplied HTML file can reference files and nested directories in the same directory as the HTML file resides.
     * @param fileURL - URL of the file to load and render.
     * @see https://docs.scriptable.app/webview/#-loadfile
     */
  loadFile(fileURL: string): Promise<void> {
    return this.invokeMethod<Promise<void>>('loadFile', Array.from(arguments));
  }
  /**
     * _Evaluates JavaScript in the web view._
     *
     * Evaluates JavaScript in the current context of the web view. The returned promise carries the result of evaluating the JavaScript.
     *
     * When passing `false` to the `useCallback` parameter, which is the default value, evaluation will terminate after evaluating the last line of the JavaScript. The value on the last
     * line of the script will be carried by the promise returned by `evaluateJavaScript`.
     *
     * When passing `true` to the `useCallback` parameter, evaluation will only complete after the globally available `completion` function is called. Any value passed to the function,
     * will be carried by the promise returned by `evaluateJavaScript`.
     *
     * The log is available from the evaluated JavaScript, i.e. messages passed to the globally available `log` and `logError` functions will be shown in the log.
     * @param javaScript - JavaScript to evaluate in the web view.
     * @param useCallback - Optional. If true the web view waits for the globally available completion function of the web view to be called before terminating. Defaults to false.
     * @see https://docs.scriptable.app/webview/#-evaluatejavascript
     */
  evaluateJavaScript(javaScript: string, useCallback?: boolean): Promise<any> {
    return this.invokeMethod<Promise<any>>('evaluateJavaScript', Array.from(arguments));
  }
  /**
     * _Reads and returns HTML from the loaded website._
     * @see https://docs.scriptable.app/webview/#-gethtml
     */
  getHTML(): Promise<any> {
    return this.invokeMethod<Promise<any>>('getHTML', Array.from(arguments));
  }
  /**
     * _Presents the web view._
     *
     * The web view is presented with the content that has been loaded into it.
     * @param fullscreen - Set to true to present the web view in fullscreen. Defaults to false.
     * @see https://docs.scriptable.app/webview/#-present
     */
  present(fullscreen?: boolean): Promise<void> {
    return this.invokeMethod<Promise<void>>('present', Array.from(arguments));
  }
  /**
     * _Waits for the web view to load._
     *
     * The returned promise will be fulfilled when the web view finishes loading. If the load fails, the promise will be fulfilled with an error. Use this with caution. If the web view is
     * not loading a new page or is not about to load a new page, the returned promise will never be fulfilled. This limitation exists because Scriptable cannot determine if a web view is
     * about to load a page in cases where evaluating JavaScript in the web view causes a new page to load.
     *
     * Generally this should only be used when loading causing a new page to load from `evaluateJavaScript`. In other cases, e.g. when loading a URL using `loadURL`, the returned promise
     * will be fulfilled when the page has been loaded.
     * @see https://docs.scriptable.app/webview/#-waitforload
     */
  waitForLoad(): Promise<any> {
    return this.invokeMethod<Promise<any>>('waitForLoad', Array.from(arguments));
  }
}

export interface WidgetDateState {
  date: Date;
  textColor: Color;
  font: Font;
  textOpacity: number;
  lineLimit: number;
  minimumScaleFactor: number;
  shadowColor: Color;
  shadowRadius: number;
  shadowOffset: Point;
  url: string;
}

export class AbsWidgetDate<S extends WidgetDateState = WidgetDateState> extends ScriptableClass<S> implements WidgetDate {
  static readonly identifier = 'WidgetDate';

  get asWidgetDate(): WidgetDate {
    return this.asOriginal<WidgetDate>();
  }


  get date() {
    if (!('date' in this.state)) {
      throw new Error('Required property "date" of type "Date" is not set');
    }
    return this.state.date as Date;
  }

  set date(value: Date) {
    this.setState({ date: value });
  }
  get textColor() {
    if (!('textColor' in this.state)) {
      throw new Error('Required property "textColor" of type "Color" is not set');
    }
    return this.state.textColor as Color;
  }

  set textColor(value: Color) {
    this.setState({ textColor: value });
  }
  get font() {
    if (!('font' in this.state)) {
      throw new Error('Required property "font" of type "Font" is not set');
    }
    return this.state.font as Font;
  }

  set font(value: Font) {
    this.setState({ font: value });
  }
  get textOpacity() {
    if (!('textOpacity' in this.state)) {
      return 0;
    }
    return this.state.textOpacity as number;
  }

  set textOpacity(value: number) {
    this.setState({ textOpacity: value });
  }
  get lineLimit() {
    if (!('lineLimit' in this.state)) {
      return 0;
    }
    return this.state.lineLimit as number;
  }

  set lineLimit(value: number) {
    this.setState({ lineLimit: value });
  }
  get minimumScaleFactor() {
    if (!('minimumScaleFactor' in this.state)) {
      return 0;
    }
    return this.state.minimumScaleFactor as number;
  }

  set minimumScaleFactor(value: number) {
    this.setState({ minimumScaleFactor: value });
  }
  get shadowColor() {
    if (!('shadowColor' in this.state)) {
      throw new Error('Required property "shadowColor" of type "Color" is not set');
    }
    return this.state.shadowColor as Color;
  }

  set shadowColor(value: Color) {
    this.setState({ shadowColor: value });
  }
  get shadowRadius() {
    if (!('shadowRadius' in this.state)) {
      return 0;
    }
    return this.state.shadowRadius as number;
  }

  set shadowRadius(value: number) {
    this.setState({ shadowRadius: value });
  }
  get shadowOffset() {
    if (!('shadowOffset' in this.state)) {
      throw new Error('Required property "shadowOffset" of type "Point" is not set');
    }
    return this.state.shadowOffset as Point;
  }

  set shadowOffset(value: Point) {
    this.setState({ shadowOffset: value });
  }
  get url() {
    if (!('url' in this.state)) {
      return "";
    }
    return this.state.url as string;
  }

  set url(value: string) {
    this.setState({ url: value });
  }

  /**
     * _Left aligns the text._
     *
     * Specifies that text should be left aligned. This is the default.
     *
     * This does not affect texts placed in stacks. Use spacers instead when aligning text in stacks. To align the text to left in a horizontal stack, you should place a spacer after the
     * text.
     * @see https://docs.scriptable.app/widgetdate/#-leftaligntext
     */
  leftAlignText(): void {
    return this.invokeMethod<void>('leftAlignText', Array.from(arguments));
  }
  /**
     * _Center aligns the text._
     *
     * Specifies that text should be center aligned.
     *
     * This does not affect texts placed in stacks. Use spacers instead when aligning text in stacks. To align the text in the center of a horizontal stack, you should place a spacer both
     * before and after the text.
     * @see https://docs.scriptable.app/widgetdate/#-centeraligntext
     */
  centerAlignText(): void {
    return this.invokeMethod<void>('centerAlignText', Array.from(arguments));
  }
  /**
     * _Right aligns the text._
     *
     * Specifies that text should be right aligned.
     *
     * This does not affect texts placed in stacks. Use spacers instead when aligning text in stacks. To align the text to the right in a horizontal stack, you should place a spacer
     * before the text.
     * @see https://docs.scriptable.app/widgetdate/#-rightaligntext
     */
  rightAlignText(): void {
    return this.invokeMethod<void>('rightAlignText', Array.from(arguments));
  }
  /**
     * _Display time component of the date._
     *
     * Example output: 11:23PM
     * @see https://docs.scriptable.app/widgetdate/#-applytimestyle
     */
  applyTimeStyle(): void {
    return this.invokeMethod<void>('applyTimeStyle', Array.from(arguments));
  }
  /**
     * _Display entire date._
     *
     * Example output: June 3, 2019
     *
     * This is the default.
     * @see https://docs.scriptable.app/widgetdate/#-applydatestyle
     */
  applyDateStyle(): void {
    return this.invokeMethod<void>('applyDateStyle', Array.from(arguments));
  }
  /**
     * _Display date as relative to now._
     *
     * Example output: 2 hours, 23 minutes 1 year, 1 month
     * @see https://docs.scriptable.app/widgetdate/#-applyrelativestyle
     */
  applyRelativeStyle(): void {
    return this.invokeMethod<void>('applyRelativeStyle', Array.from(arguments));
  }
  /**
     * _Display date as offset from now._
     *
     * Example output: +2 hours -3 months
     * @see https://docs.scriptable.app/widgetdate/#-applyoffsetstyle
     */
  applyOffsetStyle(): void {
    return this.invokeMethod<void>('applyOffsetStyle', Array.from(arguments));
  }
  /**
     * _Display date as timer counting from now._
     *
     * Example output: 2:32 36:59:01
     * @see https://docs.scriptable.app/widgetdate/#-applytimerstyle
     */
  applyTimerStyle(): void {
    return this.invokeMethod<void>('applyTimerStyle', Array.from(arguments));
  }
}

export interface WidgetImageState {
  image: Image;
  resizable: boolean;
  imageSize: Size;
  imageOpacity: number;
  cornerRadius: number;
  borderWidth: number;
  borderColor: Color;
  containerRelativeShape: boolean;
  tintColor: Color;
  url: string;
}

export class AbsWidgetImage<S extends WidgetImageState = WidgetImageState> extends ScriptableClass<S> implements WidgetImage {
  static readonly identifier = 'WidgetImage';

  get asWidgetImage(): WidgetImage {
    return this.asOriginal<WidgetImage>();
  }


  get image() {
    if (!('image' in this.state)) {
      throw new Error('Required property "image" of type "Image" is not set');
    }
    return this.state.image as Image;
  }

  set image(value: Image) {
    this.setState({ image: value });
  }
  get resizable() {
    if (!('resizable' in this.state)) {
      return false;
    }
    return this.state.resizable as boolean;
  }

  set resizable(value: boolean) {
    this.setState({ resizable: value });
  }
  get imageSize() {
    if (!('imageSize' in this.state)) {
      throw new Error('Required property "imageSize" of type "Size" is not set');
    }
    return this.state.imageSize as Size;
  }

  set imageSize(value: Size) {
    this.setState({ imageSize: value });
  }
  get imageOpacity() {
    if (!('imageOpacity' in this.state)) {
      return 0;
    }
    return this.state.imageOpacity as number;
  }

  set imageOpacity(value: number) {
    this.setState({ imageOpacity: value });
  }
  get cornerRadius() {
    if (!('cornerRadius' in this.state)) {
      return 0;
    }
    return this.state.cornerRadius as number;
  }

  set cornerRadius(value: number) {
    this.setState({ cornerRadius: value });
  }
  get borderWidth() {
    if (!('borderWidth' in this.state)) {
      return 0;
    }
    return this.state.borderWidth as number;
  }

  set borderWidth(value: number) {
    this.setState({ borderWidth: value });
  }
  get borderColor() {
    if (!('borderColor' in this.state)) {
      throw new Error('Required property "borderColor" of type "Color" is not set');
    }
    return this.state.borderColor as Color;
  }

  set borderColor(value: Color) {
    this.setState({ borderColor: value });
  }
  get containerRelativeShape() {
    if (!('containerRelativeShape' in this.state)) {
      return false;
    }
    return this.state.containerRelativeShape as boolean;
  }

  set containerRelativeShape(value: boolean) {
    this.setState({ containerRelativeShape: value });
  }
  get tintColor() {
    if (!('tintColor' in this.state)) {
      throw new Error('Required property "tintColor" of type "Color" is not set');
    }
    return this.state.tintColor as Color;
  }

  set tintColor(value: Color) {
    this.setState({ tintColor: value });
  }
  get url() {
    if (!('url' in this.state)) {
      return "";
    }
    return this.state.url as string;
  }

  set url(value: string) {
    this.setState({ url: value });
  }

  /**
     * _Left aligns the image._
     *
     * Specifies that image should be left aligned. This is the default.
     * @see https://docs.scriptable.app/widgetimage/#-leftalignimage
     */
  leftAlignImage(): void {
    return this.invokeMethod<void>('leftAlignImage', Array.from(arguments));
  }
  /**
     * _Center aligns the image._
     *
     * Specifies that image should be center aligned.
     * @see https://docs.scriptable.app/widgetimage/#-centeralignimage
     */
  centerAlignImage(): void {
    return this.invokeMethod<void>('centerAlignImage', Array.from(arguments));
  }
  /**
     * _Right aligns the image._
     *
     * Specifies that image should be right aligned.
     * @see https://docs.scriptable.app/widgetimage/#-rightalignimage
     */
  rightAlignImage(): void {
    return this.invokeMethod<void>('rightAlignImage', Array.from(arguments));
  }
  /**
     * _Uses fitting content mode._
     *
     * The image will fit the available space. This content mode is the default.
     * @see https://docs.scriptable.app/widgetimage/#-applyfittingcontentmode
     */
  applyFittingContentMode(): void {
    return this.invokeMethod<void>('applyFittingContentMode', Array.from(arguments));
  }
  /**
     * _Uses filling content mode._
     *
     * The image will fill the available space.
     * @see https://docs.scriptable.app/widgetimage/#-applyfillingcontentmode
     */
  applyFillingContentMode(): void {
    return this.invokeMethod<void>('applyFillingContentMode', Array.from(arguments));
  }
}

export interface WidgetSpacerState {
  length: number;
}

export class AbsWidgetSpacer<S extends WidgetSpacerState = WidgetSpacerState> extends ScriptableClass<S> implements WidgetSpacer {
  static readonly identifier = 'WidgetSpacer';

  get asWidgetSpacer(): WidgetSpacer {
    return this.asOriginal<WidgetSpacer>();
  }


  get length() {
    if (!('length' in this.state)) {
      return 0;
    }
    return this.state.length as number;
  }

  set length(value: number) {
    this.setState({ length: value });
  }

}

export interface WidgetStackState {
  backgroundColor: Color;
  backgroundImage: Image;
  backgroundGradient: LinearGradient;
  spacing: number;
  size: Size;
  cornerRadius: number;
  borderWidth: number;
  borderColor: Color;
  url: string;
}

export class AbsWidgetStack<S extends WidgetStackState = WidgetStackState> extends ScriptableClass<S> implements WidgetStack {
  static readonly identifier = 'WidgetStack';

  get asWidgetStack(): WidgetStack {
    return this.asOriginal<WidgetStack>();
  }


  get backgroundColor() {
    if (!('backgroundColor' in this.state)) {
      throw new Error('Required property "backgroundColor" of type "Color" is not set');
    }
    return this.state.backgroundColor as Color;
  }

  set backgroundColor(value: Color) {
    this.setState({ backgroundColor: value });
  }
  get backgroundImage() {
    if (!('backgroundImage' in this.state)) {
      throw new Error('Required property "backgroundImage" of type "Image" is not set');
    }
    return this.state.backgroundImage as Image;
  }

  set backgroundImage(value: Image) {
    this.setState({ backgroundImage: value });
  }
  get backgroundGradient() {
    if (!('backgroundGradient' in this.state)) {
      throw new Error('Required property "backgroundGradient" of type "LinearGradient" is not set');
    }
    return this.state.backgroundGradient as LinearGradient;
  }

  set backgroundGradient(value: LinearGradient) {
    this.setState({ backgroundGradient: value });
  }
  get spacing() {
    if (!('spacing' in this.state)) {
      return 0;
    }
    return this.state.spacing as number;
  }

  set spacing(value: number) {
    this.setState({ spacing: value });
  }
  get size() {
    if (!('size' in this.state)) {
      throw new Error('Required property "size" of type "Size" is not set');
    }
    return this.state.size as Size;
  }

  set size(value: Size) {
    this.setState({ size: value });
  }
  get cornerRadius() {
    if (!('cornerRadius' in this.state)) {
      return 0;
    }
    return this.state.cornerRadius as number;
  }

  set cornerRadius(value: number) {
    this.setState({ cornerRadius: value });
  }
  get borderWidth() {
    if (!('borderWidth' in this.state)) {
      return 0;
    }
    return this.state.borderWidth as number;
  }

  set borderWidth(value: number) {
    this.setState({ borderWidth: value });
  }
  get borderColor() {
    if (!('borderColor' in this.state)) {
      throw new Error('Required property "borderColor" of type "Color" is not set');
    }
    return this.state.borderColor as Color;
  }

  set borderColor(value: Color) {
    this.setState({ borderColor: value });
  }
  get url() {
    if (!('url' in this.state)) {
      return "";
    }
    return this.state.url as string;
  }

  set url(value: string) {
    this.setState({ url: value });
  }

  /**
     * _Add text to the stack._
     *
     * Adds a text element to the stack. Use the properties on the returned element to style the text.
     * @see https://docs.scriptable.app/widgetstack/#-addtext
     */
  addText(text: string): WidgetText {
    return this.invokeMethod<WidgetText>('addText', Array.from(arguments));
  }
  /**
     * _Add date to the widget._
     *
     * Adds a date element to the widget. Use the properties on the returned element to style the date.
     * @see https://docs.scriptable.app/widgetstack/#-adddate
     */
  addDate(date: Date): WidgetDate {
    return this.invokeMethod<WidgetDate>('addDate', Array.from(arguments));
  }
  /**
     * _Add image to the stack._
     *
     * Adds an image element to the stack. Use the properties on the returned element to style the image.
     * @see https://docs.scriptable.app/widgetstack/#-addimage
     */
  addImage(image: Image): WidgetImage {
    return this.invokeMethod<WidgetImage>('addImage', Array.from(arguments));
  }
  /**
     * _Add spacer._
     *
     * Adds a spacer to the stack. This can be used to offset the content horizontally in the stack.
     * @param length - Length of the spacer. Pass null to create a spacer with a flexible length.
     * @see https://docs.scriptable.app/widgetstack/#-addspacer
     */
  addSpacer(length?: number): WidgetSpacer {
    return this.invokeMethod<WidgetSpacer>('addSpacer', Array.from(arguments));
  }
  /**
     * _Add stack._
     *
     * Adds a stack to the widget. Stacks layout elements horizontally by default.
     * @see https://docs.scriptable.app/widgetstack/#-addstack
     */
  addStack(): WidgetStack {
    return this.invokeMethod<WidgetStack>('addStack', Array.from(arguments));
  }
  /**
     * _Set padding._
     *
     * Sets the padding on each side of the stack.
     * @param top - Padding on the top edge.
     * @param leading - Padding on the leading edge.
     * @param bottom - Padding on the bottom edge.
     * @param trailing - Padding on the trailing edge.
     * @see https://docs.scriptable.app/widgetstack/#-setpadding
     */
  setPadding(top: number, leading: number, bottom: number, trailing: number): void {
    return this.invokeMethod<void>('setPadding', Array.from(arguments));
  }
  /**
     * _Use the default padding._
     *
     * Configure the stack to use the default padding. Any padding previously defined with `setPadding()` will be discarded.
     * @see https://docs.scriptable.app/widgetstack/#-usedefaultpadding
     */
  useDefaultPadding(): void {
    return this.invokeMethod<void>('useDefaultPadding', Array.from(arguments));
  }
  /**
     * _Top aligns the content._
     *
     * Specifies that the content should be top aligned. This is the default.
     * @see https://docs.scriptable.app/widgetstack/#-topaligncontent
     */
  topAlignContent(): void {
    return this.invokeMethod<void>('topAlignContent', Array.from(arguments));
  }
  /**
     * _Center aligns the content._
     *
     * Specifies that the content should be center aligned.
     * @see https://docs.scriptable.app/widgetstack/#-centeraligncontent
     */
  centerAlignContent(): void {
    return this.invokeMethod<void>('centerAlignContent', Array.from(arguments));
  }
  /**
     * _Bottom aligns the content._
     *
     * Specifies that the content should be bottom aligned.
     * @see https://docs.scriptable.app/widgetstack/#-bottomaligncontent
     */
  bottomAlignContent(): void {
    return this.invokeMethod<void>('bottomAlignContent', Array.from(arguments));
  }
  /**
     * _Layout elements horizontally._
     *
     * Specifies that the stack should layout elements horizontally. This is the default.
     * @see https://docs.scriptable.app/widgetstack/#-layouthorizontally
     */
  layoutHorizontally(): void {
    return this.invokeMethod<void>('layoutHorizontally', Array.from(arguments));
  }
  /**
     * _Layout elements vertically._
     *
     * Specifies that the stack should layout elements vertically.
     * @see https://docs.scriptable.app/widgetstack/#-layoutvertically
     */
  layoutVertically(): void {
    return this.invokeMethod<void>('layoutVertically', Array.from(arguments));
  }
}

export interface WidgetTextState {
  text: string;
  textColor: Color;
  font: Font;
  textOpacity: number;
  lineLimit: number;
  minimumScaleFactor: number;
  shadowColor: Color;
  shadowRadius: number;
  shadowOffset: Point;
  url: string;
}

export class AbsWidgetText<S extends WidgetTextState = WidgetTextState> extends ScriptableClass<S> implements WidgetText {
  static readonly identifier = 'WidgetText';

  get asWidgetText(): WidgetText {
    return this.asOriginal<WidgetText>();
  }


  get text() {
    if (!('text' in this.state)) {
      return "";
    }
    return this.state.text as string;
  }

  set text(value: string) {
    this.setState({ text: value });
  }
  get textColor() {
    if (!('textColor' in this.state)) {
      throw new Error('Required property "textColor" of type "Color" is not set');
    }
    return this.state.textColor as Color;
  }

  set textColor(value: Color) {
    this.setState({ textColor: value });
  }
  get font() {
    if (!('font' in this.state)) {
      throw new Error('Required property "font" of type "Font" is not set');
    }
    return this.state.font as Font;
  }

  set font(value: Font) {
    this.setState({ font: value });
  }
  get textOpacity() {
    if (!('textOpacity' in this.state)) {
      return 0;
    }
    return this.state.textOpacity as number;
  }

  set textOpacity(value: number) {
    this.setState({ textOpacity: value });
  }
  get lineLimit() {
    if (!('lineLimit' in this.state)) {
      return 0;
    }
    return this.state.lineLimit as number;
  }

  set lineLimit(value: number) {
    this.setState({ lineLimit: value });
  }
  get minimumScaleFactor() {
    if (!('minimumScaleFactor' in this.state)) {
      return 0;
    }
    return this.state.minimumScaleFactor as number;
  }

  set minimumScaleFactor(value: number) {
    this.setState({ minimumScaleFactor: value });
  }
  get shadowColor() {
    if (!('shadowColor' in this.state)) {
      throw new Error('Required property "shadowColor" of type "Color" is not set');
    }
    return this.state.shadowColor as Color;
  }

  set shadowColor(value: Color) {
    this.setState({ shadowColor: value });
  }
  get shadowRadius() {
    if (!('shadowRadius' in this.state)) {
      return 0;
    }
    return this.state.shadowRadius as number;
  }

  set shadowRadius(value: number) {
    this.setState({ shadowRadius: value });
  }
  get shadowOffset() {
    if (!('shadowOffset' in this.state)) {
      throw new Error('Required property "shadowOffset" of type "Point" is not set');
    }
    return this.state.shadowOffset as Point;
  }

  set shadowOffset(value: Point) {
    this.setState({ shadowOffset: value });
  }
  get url() {
    if (!('url' in this.state)) {
      return "";
    }
    return this.state.url as string;
  }

  set url(value: string) {
    this.setState({ url: value });
  }

  /**
     * _Left aligns the text._
     *
     * Specifies that text should be left aligned. This is the default.
     *
     * This does not affect texts placed in stacks. Use spacers instead when aligning text in stacks. To align the text to left in a horizontal stack, you should place a spacer after the
     * text.
     * @see https://docs.scriptable.app/widgettext/#-leftaligntext
     */
  leftAlignText(): void {
    return this.invokeMethod<void>('leftAlignText', Array.from(arguments));
  }
  /**
     * _Center aligns the text._
     *
     * Specifies that text should be center aligned.
     *
     * This does not affect texts placed in stacks. Use spacers instead when aligning text in stacks. To align the text in the center of a horizontal stack, you should place a spacer both
     * before and after the text.
     * @see https://docs.scriptable.app/widgettext/#-centeraligntext
     */
  centerAlignText(): void {
    return this.invokeMethod<void>('centerAlignText', Array.from(arguments));
  }
  /**
     * _Right aligns the text._
     *
     * Specifies that text should be right aligned.
     *
     * This does not affect texts placed in stacks. Use spacers instead when aligning text in stacks. To align the text to the right in a horizontal stack, you should place a spacer
     * before the text.
     * @see https://docs.scriptable.app/widgettext/#-rightaligntext
     */
  rightAlignText(): void {
    return this.invokeMethod<void>('rightAlignText', Array.from(arguments));
  }
}

export interface XMLParserState {
  string: string;
  didStartDocument: () => void;
  didEndDocument: () => void;
  didStartElement: (arg0: string, arg1: { [key: string]: string; }) => void;
  didEndElement: (name: string) => void;
  foundCharacters: (str: string) => void;
  parseErrorOccurred: (error: string) => void;
}

export class AbsXMLParser<S extends XMLParserState = XMLParserState> extends ScriptableClass<S> implements XMLParser {
  static readonly identifier = 'XMLParser';

  get asXMLParser(): XMLParser {
    return this.asOriginal<XMLParser>();
  }


  get string() {
    if (!('string' in this.state)) {
      return "";
    }
    return this.state.string as string;
  }

  set string(value: string) {
    this.setState({ string: value });
  }
  get didStartDocument() {
    if (!('didStartDocument' in this.state)) {
      throw new Error('Required property "didStartDocument" of type "() => void" is not set');
    }
    return this.state.didStartDocument as () => void;
  }

  set didStartDocument(value: () => void) {
    this.setState({ didStartDocument: value });
  }
  get didEndDocument() {
    if (!('didEndDocument' in this.state)) {
      throw new Error('Required property "didEndDocument" of type "() => void" is not set');
    }
    return this.state.didEndDocument as () => void;
  }

  set didEndDocument(value: () => void) {
    this.setState({ didEndDocument: value });
  }
  get didStartElement() {
    if (!('didStartElement' in this.state)) {
      throw new Error('Required property "didStartElement" of type "(arg0: string, arg1: { [key: string]: string; }) => void" is not set');
    }
    return this.state.didStartElement as (arg0: string, arg1: { [key: string]: string; }) => void;
  }

  set didStartElement(value: (arg0: string, arg1: { [key: string]: string; }) => void) {
    this.setState({ didStartElement: value });
  }
  get didEndElement() {
    if (!('didEndElement' in this.state)) {
      throw new Error('Required property "didEndElement" of type "(name: string) => void" is not set');
    }
    return this.state.didEndElement as (name: string) => void;
  }

  set didEndElement(value: (name: string) => void) {
    this.setState({ didEndElement: value });
  }
  get foundCharacters() {
    if (!('foundCharacters' in this.state)) {
      throw new Error('Required property "foundCharacters" of type "(str: string) => void" is not set');
    }
    return this.state.foundCharacters as (str: string) => void;
  }

  set foundCharacters(value: (str: string) => void) {
    this.setState({ foundCharacters: value });
  }
  get parseErrorOccurred() {
    if (!('parseErrorOccurred' in this.state)) {
      throw new Error('Required property "parseErrorOccurred" of type "(error: string) => void" is not set');
    }
    return this.state.parseErrorOccurred as (error: string) => void;
  }

  set parseErrorOccurred(value: (error: string) => void) {
    this.setState({ parseErrorOccurred: value });
  }

  /**
     * _Starts parsing._
     *
     * Before calling this function you should ensure that the parser is correctly configured, i.e. the necessary callback functions should be set.
     * @see https://docs.scriptable.app/xmlparser/#-parse
     */
  parse(): boolean {
    return this.invokeMethod<boolean>('parse', Array.from(arguments));
  }
}
// Variable variable type definitions
// These types are automatically generated from @types/scriptable-ios

/** Properties interface for global variable `args` */
export interface ArgsState {
  plainTexts: string[];
  urls: string[];
  fileURLs: string[];
  images: Image[];
  queryParameters: { [key: string]: string; };
  shortcutParameter: any;
  widgetParameter: any;
  notification: Notification;
}

/** Type definition for global variable `args` */
export type Args = typeof args;

/** Abstract class for global variable `args` */
export class AbsArgs<S extends ArgsState = ArgsState> extends ScriptableVariable<S> implements Args {
  static readonly identifier = 'args';

  static get instance(): AbsArgs<ArgsState> {
    return super.instance as AbsArgs<ArgsState>;
  }

  get asArgs(): Args {
    return this.asOriginal<Args>();
  }

  get plainTexts() {
    if (!('plainTexts' in this.state)) {
      return [];
    }
    return this.state.plainTexts as string[];
  }

  set plainTexts(value: string[]) {
    this.setState({ plainTexts: value });
  }

  get urls() {
    if (!('urls' in this.state)) {
      return [];
    }
    return this.state.urls as string[];
  }

  set urls(value: string[]) {
    this.setState({ urls: value });
  }

  get fileURLs() {
    if (!('fileURLs' in this.state)) {
      return [];
    }
    return this.state.fileURLs as string[];
  }

  set fileURLs(value: string[]) {
    this.setState({ fileURLs: value });
  }

  get images() {
    if (!('images' in this.state)) {
      return [];
    }
    return this.state.images as Image[];
  }

  set images(value: Image[]) {
    this.setState({ images: value });
  }

  get queryParameters() {
    if (!('queryParameters' in this.state)) {
      throw new Error('Required property "queryParameters" of type "{ [key: string]: string; }" is not set');
    }
    return this.state.queryParameters as { [key: string]: string; };
  }

  set queryParameters(value: { [key: string]: string; }) {
    this.setState({ queryParameters: value });
  }

  get shortcutParameter() {
    if (!('shortcutParameter' in this.state)) {
      throw new Error('Required property "shortcutParameter" of type "any" is not set');
    }
    return this.state.shortcutParameter as any;
  }

  set shortcutParameter(value: any) {
    this.setState({ shortcutParameter: value });
  }

  get widgetParameter() {
    if (!('widgetParameter' in this.state)) {
      throw new Error('Required property "widgetParameter" of type "any" is not set');
    }
    return this.state.widgetParameter as any;
  }

  set widgetParameter(value: any) {
    this.setState({ widgetParameter: value });
  }

  get notification() {
    if (!('notification' in this.state)) {
      throw new Error('Required property "notification" of type "Notification" is not set');
    }
    return this.state.notification as Notification;
  }

  set notification(value: Notification) {
    this.setState({ notification: value });
  }
}

/** Properties interface for global variable `config` */
export interface ConfigState {
  runsInApp: boolean;
  runsInActionExtension: boolean;
  runsWithSiri: boolean;
  runsInWidget: boolean;
  runsInAccessoryWidget: boolean;
  runsInNotification: boolean;
  runsFromHomeScreen: boolean;
  widgetFamily: "small" | "medium" | "large" | "extraLarge" | "accessoryRectangular" | "accessoryInline" | "accessoryCircular";
}

/** Type definition for global variable `config` */
export type Config = typeof config;

/** Abstract class for global variable `config` */
export class AbsConfig<S extends ConfigState = ConfigState> extends ScriptableVariable<S> implements Config {
  static readonly identifier = 'config';

  static get instance(): AbsConfig<ConfigState> {
    return super.instance as AbsConfig<ConfigState>;
  }

  get asConfig(): Config {
    return this.asOriginal<Config>();
  }

  get runsInApp() {
    if (!('runsInApp' in this.state)) {
      return false;
    }
    return this.state.runsInApp as boolean;
  }

  set runsInApp(value: boolean) {
    this.setState({ runsInApp: value });
  }

  get runsInActionExtension() {
    if (!('runsInActionExtension' in this.state)) {
      return false;
    }
    return this.state.runsInActionExtension as boolean;
  }

  set runsInActionExtension(value: boolean) {
    this.setState({ runsInActionExtension: value });
  }

  get runsWithSiri() {
    if (!('runsWithSiri' in this.state)) {
      return false;
    }
    return this.state.runsWithSiri as boolean;
  }

  set runsWithSiri(value: boolean) {
    this.setState({ runsWithSiri: value });
  }

  get runsInWidget() {
    if (!('runsInWidget' in this.state)) {
      return false;
    }
    return this.state.runsInWidget as boolean;
  }

  set runsInWidget(value: boolean) {
    this.setState({ runsInWidget: value });
  }

  get runsInAccessoryWidget() {
    if (!('runsInAccessoryWidget' in this.state)) {
      return false;
    }
    return this.state.runsInAccessoryWidget as boolean;
  }

  set runsInAccessoryWidget(value: boolean) {
    this.setState({ runsInAccessoryWidget: value });
  }

  get runsInNotification() {
    if (!('runsInNotification' in this.state)) {
      return false;
    }
    return this.state.runsInNotification as boolean;
  }

  set runsInNotification(value: boolean) {
    this.setState({ runsInNotification: value });
  }

  get runsFromHomeScreen() {
    if (!('runsFromHomeScreen' in this.state)) {
      return false;
    }
    return this.state.runsFromHomeScreen as boolean;
  }

  set runsFromHomeScreen(value: boolean) {
    this.setState({ runsFromHomeScreen: value });
  }

  get widgetFamily() {
    if (!('widgetFamily' in this.state)) {
      throw new Error('Required property "widgetFamily" of type ""small" | "medium" | "large" | "extraLarge" | "accessoryRectangular" | "accessoryInline" | "accessoryCircular"" is not set');
    }
    return this.state.widgetFamily as "small" | "medium" | "large" | "extraLarge" | "accessoryRectangular" | "accessoryInline" | "accessoryCircular";
  }

  set widgetFamily(value: "small" | "medium" | "large" | "extraLarge" | "accessoryRectangular" | "accessoryInline" | "accessoryCircular") {
    this.setState({ widgetFamily: value });
  }
}

/** Properties interface for global variable `console` */
export interface ConsoleState {

}

/** Type definition for global variable `console` */
export type Console = typeof console;

/** Abstract class for global variable `console` */
export class AbsConsole<S extends ConsoleState = ConsoleState> extends ScriptableVariable<S> implements Console {
  static readonly identifier = 'console';

  static get instance(): AbsConsole<ConsoleState> {
    return super.instance as AbsConsole<ConsoleState>;
  }

  get asConsole(): Console {
    return this.asOriginal<Console>();
  }

  log(message: any): void {
    return this.invokeMethod<void>('log', [message]);
  }

  warn(message: any): void {
    return this.invokeMethod<void>('warn', [message]);
  }

  error(message: any): void {
    return this.invokeMethod<void>('error', [message]);
  }
}

/** Properties interface for global variable `Device` */
export interface DeviceState {

}

/** Type definition for global variable `Device` */
export type Device = typeof Device;

/** Abstract class for global variable `Device` */
export class AbsDevice<S extends DeviceState = DeviceState> extends ScriptableVariable<S> implements Device {
  static readonly identifier = 'Device';

  static get instance(): AbsDevice<DeviceState> {
    return super.instance as AbsDevice<DeviceState>;
  }

  get asDevice(): Device {
    return this.asOriginal<Device>();
  }

  name(): string {
    return this.invokeMethod<string>('name', []);
  }

  systemName(): string {
    return this.invokeMethod<string>('systemName', []);
  }

  systemVersion(): string {
    return this.invokeMethod<string>('systemVersion', []);
  }

  model(): string {
    return this.invokeMethod<string>('model', []);
  }

  isPhone(): boolean {
    return this.invokeMethod<boolean>('isPhone', []);
  }

  isPad(): boolean {
    return this.invokeMethod<boolean>('isPad', []);
  }

  screenSize(): Size {
    return this.invokeMethod<Size>('screenSize', []);
  }

  screenResolution(): Size {
    return this.invokeMethod<Size>('screenResolution', []);
  }

  screenScale(): number {
    return this.invokeMethod<number>('screenScale', []);
  }

  screenBrightness(): number {
    return this.invokeMethod<number>('screenBrightness', []);
  }

  isInPortrait(): boolean {
    return this.invokeMethod<boolean>('isInPortrait', []);
  }

  isInPortraitUpsideDown(): boolean {
    return this.invokeMethod<boolean>('isInPortraitUpsideDown', []);
  }

  isInLandscapeLeft(): boolean {
    return this.invokeMethod<boolean>('isInLandscapeLeft', []);
  }

  isInLandscapeRight(): boolean {
    return this.invokeMethod<boolean>('isInLandscapeRight', []);
  }

  isFaceUp(): boolean {
    return this.invokeMethod<boolean>('isFaceUp', []);
  }

  isFaceDown(): boolean {
    return this.invokeMethod<boolean>('isFaceDown', []);
  }

  batteryLevel(): number {
    return this.invokeMethod<number>('batteryLevel', []);
  }

  isDischarging(): boolean {
    return this.invokeMethod<boolean>('isDischarging', []);
  }

  isCharging(): boolean {
    return this.invokeMethod<boolean>('isCharging', []);
  }

  isFullyCharged(): boolean {
    return this.invokeMethod<boolean>('isFullyCharged', []);
  }

  preferredLanguages(): string[] {
    return this.invokeMethod<string[]>('preferredLanguages', []);
  }

  locale(): string {
    return this.invokeMethod<string>('locale', []);
  }

  language(): string {
    return this.invokeMethod<string>('language', []);
  }

  isUsingDarkAppearance(): boolean {
    return this.invokeMethod<boolean>('isUsingDarkAppearance', []);
  }

  volume(): number {
    return this.invokeMethod<number>('volume', []);
  }

  setScreenBrightness(percentage: number): void {
    return this.invokeMethod<void>('setScreenBrightness', [percentage]);
  }
}

/** Properties interface for global variable `Dictation` */
export interface DictationState {

}

/** Type definition for global variable `Dictation` */
export type Dictation = typeof Dictation;

/** Abstract class for global variable `Dictation` */
export class AbsDictation<S extends DictationState = DictationState> extends ScriptableVariable<S> implements Dictation {
  static readonly identifier = 'Dictation';

  static get instance(): AbsDictation<DictationState> {
    return super.instance as AbsDictation<DictationState>;
  }

  get asDictation(): Dictation {
    return this.asOriginal<Dictation>();
  }

  start(locale?: string): Promise<string> {
    return this.invokeMethod<Promise<string>>('start', [locale]);
  }
}

/** Properties interface for global variable `DocumentPicker` */
export interface DocumentPickerState {

}

/** Type definition for global variable `DocumentPicker` */
export type DocumentPicker = typeof DocumentPicker;

/** Abstract class for global variable `DocumentPicker` */
export class AbsDocumentPicker<S extends DocumentPickerState = DocumentPickerState> extends ScriptableVariable<S> implements DocumentPicker {
  static readonly identifier = 'DocumentPicker';

  static get instance(): AbsDocumentPicker<DocumentPickerState> {
    return super.instance as AbsDocumentPicker<DocumentPickerState>;
  }

  get asDocumentPicker(): DocumentPicker {
    return this.asOriginal<DocumentPicker>();
  }

  open(types?: readonly string[]): Promise<string[]> {
    return this.invokeMethod<Promise<string[]>>('open', [types]);
  }

  openFile(): Promise<string> {
    return this.invokeMethod<Promise<string>>('openFile', []);
  }

  openFolder(): Promise<string> {
    return this.invokeMethod<Promise<string>>('openFolder', []);
  }

  export(path: string): Promise<string[]> {
    return this.invokeMethod<Promise<string[]>>('export', [path]);
  }

  exportString(content: string, name?: string): Promise<string[]> {
    return this.invokeMethod<Promise<string[]>>('exportString', [content, name]);
  }

  exportImage(image: Image, name?: string): Promise<string[]> {
    return this.invokeMethod<Promise<string[]>>('exportImage', [image, name]);
  }

  exportData(data: Data, name?: string): Promise<string[]> {
    return this.invokeMethod<Promise<string[]>>('exportData', [data, name]);
  }
}

/** Properties interface for global variable `Keychain` */
export interface KeychainState {

}

/** Type definition for global variable `Keychain` */
export type Keychain = typeof Keychain;

/** Abstract class for global variable `Keychain` */
export class AbsKeychain<S extends KeychainState = KeychainState> extends ScriptableVariable<S> implements Keychain {
  static readonly identifier = 'Keychain';

  static get instance(): AbsKeychain<KeychainState> {
    return super.instance as AbsKeychain<KeychainState>;
  }

  get asKeychain(): Keychain {
    return this.asOriginal<Keychain>();
  }

  contains(key: string): boolean {
    return this.invokeMethod<boolean>('contains', [key]);
  }

  set(key: string, value: string): void {
    return this.invokeMethod<void>('set', [key, value]);
  }

  get(key: string): string {
    return this.invokeMethod<string>('get', [key]);
  }

  remove(key: string): void {
    return this.invokeMethod<void>('remove', [key]);
  }
}

/** Properties interface for global variable `Location` */
export interface LocationState {

}

/** Type definition for global variable `Location` */
export type Location = typeof Location;

/** Abstract class for global variable `Location` */
export class AbsLocation<S extends LocationState = LocationState> extends ScriptableVariable<S> implements Location {
  static readonly identifier = 'Location';

  static get instance(): AbsLocation<LocationState> {
    return super.instance as AbsLocation<LocationState>;
  }

  get asLocation(): Location {
    return this.asOriginal<Location>();
  }

  current(): Promise<Location.CurrentLocation> {
    return this.invokeMethod<Promise<Location.CurrentLocation>>('current', []);
  }

  setAccuracyToBest(): void {
    return this.invokeMethod<void>('setAccuracyToBest', []);
  }

  setAccuracyToTenMeters(): void {
    return this.invokeMethod<void>('setAccuracyToTenMeters', []);
  }

  setAccuracyToHundredMeters(): void {
    return this.invokeMethod<void>('setAccuracyToHundredMeters', []);
  }

  setAccuracyToKilometer(): void {
    return this.invokeMethod<void>('setAccuracyToKilometer', []);
  }

  setAccuracyToThreeKilometers(): void {
    return this.invokeMethod<void>('setAccuracyToThreeKilometers', []);
  }

  reverseGeocode(latitude: number, longitude: number, locale?: string): Promise<Location.GeocodeSummary[]> {
    return this.invokeMethod<Promise<Location.GeocodeSummary[]>>('reverseGeocode', [latitude, longitude, locale]);
  }
}

/** Properties interface for global variable `module` */
export interface ModuleState {
  filename: string;
  exports: unknown;
}

/** Type definition for global variable `module` */
export type Module = typeof module;

/** Abstract class for global variable `module` */
export class AbsModule<S extends ModuleState = ModuleState> extends ScriptableVariable<S> implements Module {
  static readonly identifier = 'module';

  static get instance(): AbsModule<ModuleState> {
    return super.instance as AbsModule<ModuleState>;
  }

  get asModule(): Module {
    return this.asOriginal<Module>();
  }

  get filename() {
    if (!('filename' in this.state)) {
      return "";
    }
    return this.state.filename as string;
  }

  set filename(value: string) {
    this.setState({ filename: value });
  }

  get exports() {
    if (!('exports' in this.state)) {
      throw new Error('Required property "exports" of type "any" is not set');
    }
    return this.state.exports as any;
  }

  set exports(value: any) {
    this.setState({ exports: value });
  }
}

/** Properties interface for global variable `Pasteboard` */
export interface PasteboardState {

}

/** Type definition for global variable `Pasteboard` */
export type Pasteboard = typeof Pasteboard;

/** Abstract class for global variable `Pasteboard` */
export class AbsPasteboard<S extends PasteboardState = PasteboardState> extends ScriptableVariable<S> implements Pasteboard {
  static readonly identifier = 'Pasteboard';

  static get instance(): AbsPasteboard<PasteboardState> {
    return super.instance as AbsPasteboard<PasteboardState>;
  }

  get asPasteboard(): Pasteboard {
    return this.asOriginal<Pasteboard>();
  }

  copy(string: string): void {
    return this.invokeMethod<void>('copy', [string]);
  }

  paste(): string {
    return this.invokeMethod<string>('paste', []);
  }

  copyString(string: string): void {
    return this.invokeMethod<void>('copyString', [string]);
  }

  pasteString(): string {
    return this.invokeMethod<string>('pasteString', []);
  }

  copyImage(image: Image): void {
    return this.invokeMethod<void>('copyImage', [image]);
  }

  pasteImage(): Image {
    return this.invokeMethod<Image>('pasteImage', []);
  }
}

/** Properties interface for global variable `Photos` */
export interface PhotosState {

}

/** Type definition for global variable `Photos` */
export type Photos = typeof Photos;

/** Abstract class for global variable `Photos` */
export class AbsPhotos<S extends PhotosState = PhotosState> extends ScriptableVariable<S> implements Photos {
  static readonly identifier = 'Photos';

  static get instance(): AbsPhotos<PhotosState> {
    return super.instance as AbsPhotos<PhotosState>;
  }

  get asPhotos(): Photos {
    return this.asOriginal<Photos>();
  }

  fromLibrary(): Promise<Image> {
    return this.invokeMethod<Promise<Image>>('fromLibrary', []);
  }

  fromCamera(): Promise<Image> {
    return this.invokeMethod<Promise<Image>>('fromCamera', []);
  }

  latestPhoto(): Promise<Image> {
    return this.invokeMethod<Promise<Image>>('latestPhoto', []);
  }

  latestPhotos(count: number): Promise<Image[]> {
    return this.invokeMethod<Promise<Image[]>>('latestPhotos', [count]);
  }

  latestScreenshot(): Promise<Image> {
    return this.invokeMethod<Promise<Image>>('latestScreenshot', []);
  }

  latestScreenshots(count: number): Promise<Image[]> {
    return this.invokeMethod<Promise<Image[]>>('latestScreenshots', [count]);
  }

  removeLatestPhoto(): void {
    return this.invokeMethod<void>('removeLatestPhoto', []);
  }

  removeLatestPhotos(count: number): void {
    return this.invokeMethod<void>('removeLatestPhotos', [count]);
  }

  removeLatestScreenshot(): void {
    return this.invokeMethod<void>('removeLatestScreenshot', []);
  }

  removeLatestScreenshots(count: number): void {
    return this.invokeMethod<void>('removeLatestScreenshots', [count]);
  }

  save(image: Image): void {
    return this.invokeMethod<void>('save', [image]);
  }
}

/** Properties interface for global variable `QuickLook` */
export interface QuickLookState {

}

/** Type definition for global variable `QuickLook` */
export type QuickLook = typeof QuickLook;

/** Abstract class for global variable `QuickLook` */
export class AbsQuickLook<S extends QuickLookState = QuickLookState> extends ScriptableVariable<S> implements QuickLook {
  static readonly identifier = 'QuickLook';

  static get instance(): AbsQuickLook<QuickLookState> {
    return super.instance as AbsQuickLook<QuickLookState>;
  }

  get asQuickLook(): QuickLook {
    return this.asOriginal<QuickLook>();
  }

  present(item: any, fullscreen?: boolean): Promise<void> {
    return this.invokeMethod<Promise<void>>('present', [item, fullscreen]);
  }
}

/** Properties interface for global variable `Safari` */
export interface SafariState {

}

/** Type definition for global variable `Safari` */
export type Safari = typeof Safari;

/** Abstract class for global variable `Safari` */
export class AbsSafari<S extends SafariState = SafariState> extends ScriptableVariable<S> implements Safari {
  static readonly identifier = 'Safari';

  static get instance(): AbsSafari<SafariState> {
    return super.instance as AbsSafari<SafariState>;
  }

  get asSafari(): Safari {
    return this.asOriginal<Safari>();
  }

  openInApp(url: string, fullscreen?: boolean): Promise<void> {
    return this.invokeMethod<Promise<void>>('openInApp', [url, fullscreen]);
  }

  open(url: string): void {
    return this.invokeMethod<void>('open', [url]);
  }
}

/** Properties interface for global variable `Script` */
export interface ScriptState {

}

/** Type definition for global variable `Script` */
export type Script = typeof Script;

/** Abstract class for global variable `Script` */
export class AbsScript<S extends ScriptState = ScriptState> extends ScriptableVariable<S> implements Script {
  static readonly identifier = 'Script';

  static get instance(): AbsScript<ScriptState> {
    return super.instance as AbsScript<ScriptState>;
  }

  get asScript(): Script {
    return this.asOriginal<Script>();
  }

  name(): string {
    return this.invokeMethod<string>('name', []);
  }

  complete(): void {
    return this.invokeMethod<void>('complete', []);
  }

  setShortcutOutput(value: any): void {
    return this.invokeMethod<void>('setShortcutOutput', [value]);
  }

  setWidget(widget: any): void {
    return this.invokeMethod<void>('setWidget', [widget]);
  }
}

/** Properties interface for global variable `ShareSheet` */
export interface ShareSheetState {

}

/** Type definition for global variable `ShareSheet` */
export type ShareSheet = typeof ShareSheet;

/** Abstract class for global variable `ShareSheet` */
export class AbsShareSheet<S extends ShareSheetState = ShareSheetState> extends ScriptableVariable<S> implements ShareSheet {
  static readonly identifier = 'ShareSheet';

  static get instance(): AbsShareSheet<ShareSheetState> {
    return super.instance as AbsShareSheet<ShareSheetState>;
  }

  get asShareSheet(): ShareSheet {
    return this.asOriginal<ShareSheet>();
  }

  present(activityItems: readonly any[]): Promise<ShareSheet.ShareSheetResult> {
    return this.invokeMethod<Promise<ShareSheet.ShareSheetResult>>('present', [activityItems]);
  }
}

/** Properties interface for global variable `Speech` */
export interface SpeechState {

}

/** Type definition for global variable `Speech` */
export type Speech = typeof Speech;

/** Abstract class for global variable `Speech` */
export class AbsSpeech<S extends SpeechState = SpeechState> extends ScriptableVariable<S> implements Speech {
  static readonly identifier = 'Speech';

  static get instance(): AbsSpeech<SpeechState> {
    return super.instance as AbsSpeech<SpeechState>;
  }

  get asSpeech(): Speech {
    return this.asOriginal<Speech>();
  }

  speak(text: string): void {
    return this.invokeMethod<void>('speak', [text]);
  }
}

/** Properties interface for global variable `URLScheme` */
export interface URLSchemeState {

}

/** Type definition for global variable `URLScheme` */
export type URLScheme = typeof URLScheme;

/** Abstract class for global variable `URLScheme` */
export class AbsURLScheme<S extends URLSchemeState = URLSchemeState> extends ScriptableVariable<S> implements URLScheme {
  static readonly identifier = 'URLScheme';

  static get instance(): AbsURLScheme<URLSchemeState> {
    return super.instance as AbsURLScheme<URLSchemeState>;
  }

  get asURLScheme(): URLScheme {
    return this.asOriginal<URLScheme>();
  }

  forOpeningScript(): string {
    return this.invokeMethod<string>('forOpeningScript', []);
  }

  forOpeningScriptSettings(): string {
    return this.invokeMethod<string>('forOpeningScriptSettings', []);
  }

  forRunningScript(): string {
    return this.invokeMethod<string>('forRunningScript', []);
  }
}

/** Properties interface for global variable `UUID` */
export interface UUIDState {

}

/** Type definition for global variable `UUID` */
export type UUID = typeof UUID;

/** Abstract class for global variable `UUID` */
export class AbsUUID<S extends UUIDState = UUIDState> extends ScriptableVariable<S> implements UUID {
  static readonly identifier = 'UUID';

  static get instance(): AbsUUID<UUIDState> {
    return super.instance as AbsUUID<UUIDState>;
  }

  get asUUID(): UUID {
    return this.asOriginal<UUID>();
  }

  string(): string {
    return this.invokeMethod<string>('string', []);
  }
}
// Auto-generated global class names
export const GLOBAL_CLASS_NAMES = [
  'Alert',
  'Calendar',
  'CalendarEvent',
  'CallbackURL',
  'Color',
  'Contact',
  'ContactsContainer',
  'ContactsGroup',
  'Data',
  'DateFormatter',
  'DatePicker',
  'DrawContext',
  'FileManager',
  'Font',
  'Image',
  'LinearGradient',
  'ListWidget',
  'Mail',
  'Message',
  'Notification',
  'Path',
  'Point',
  'Rect',
  'RecurrenceRule',
  'RelativeDateTimeFormatter',
  'Reminder',
  'Request',
  'SFSymbol',
  'Size',
  'TextField',
  'Timer',
  'UITable',
  'UITableCell',
  'UITableRow',
  'WebView',
  'WidgetDate',
  'WidgetImage',
  'WidgetSpacer',
  'WidgetStack',
  'WidgetText',
  'XMLParser'
] as const;

// Auto-generated global variable names
export const GLOBAL_VAR_NAMES = [
  'args',
  'config',
  'console',
  'Device',
  'Dictation',
  'DocumentPicker',
  'Keychain',
  'Location',
  'module',
  'Pasteboard',
  'Photos',
  'QuickLook',
  'Safari',
  'Script',
  'ShareSheet',
  'Speech',
  'URLScheme',
  'UUID'
] as const;

// Type definitions for global names
export type GlobalClassName = typeof GLOBAL_CLASS_NAMES[number];
export type GlobalVarName = typeof GLOBAL_VAR_NAMES[number];